import { useProductMeasurements } from "../hooks/useProductMeasurements";
import { generateStandardImage } from "../services/standardGenerationService";
import { assertWorkflowPreflight } from "../utils/workflowPreflight";
import { mergeGeneratedImageSettings } from "../utils/imageGenerationResult";
import { mergeSerializedGenerationSettings } from "../utils/generationSettings";
import { workflowImageGeneration } from "../utils/imageGenerationSettings";
import { type ImageGenerationSettings } from "../../functions/src/shared/imageGeneration";
import { serializeImageGenerationSettings } from "../utils/imageGenerationSettings";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { auth } from "../config/firebase";
import { getTemperatureForImageEditorMode } from "../config/modelTemperature";
import {
  useGetProject,
  useListBrandTemplates,
  useListUserWorkflows,
  useTouchWorkflowLastUsed,
} from "@dataconnect/generated/react";
import { useUserSettings } from "@/src/hooks/useUserSettings";
import { useProductAnalysis } from "@/src/hooks/useProductUpload.ts";
import {
  AppImageFile,
  BackgroundRemovalConfig,
  BrandIdentity,
  ColorSwatch,
  GeneratedContent,
  GenerationSettings,
  GenerationTask,
  ImageFile,
  LifestylePreset,
  ProjectSpace,
  RotationModel,
  RotationQuality,
  RecolorModel,
  RecolorQuality,
  Scene,
  StyleConfig,
  StudioImageType,
  StudioProduct,
  Vibe,
  Workflow,
  WorkflowNode,
  WorkflowEdge,
  WorkflowInpaintPlacement,
  WorkflowInpaintPlacementPreset,
  LIFESTYLE_PRESETS,
  AppNotification,
} from "../types.ts";
import {
  getImageSrc,
  ensureImageBase64,
  ensureContentDataUrl,
  detectTransparentPixels,
  loadFullImageBase64,
  serializeGenerationSettings,
  downloadAsset,
  convertBlobFormat,
} from "../utils/imageUtils.ts";
import { applyXmpDisclosure } from "../utils/xmpMetadata.ts";
import { fetchActiveAiBadge } from "../services/aiBadgeService.ts";
import {
  environmentIdSettings,
  isBackgroundRemovalGeneratedContent,
  restoreRegenerationSettings,
} from "../utils/regeneration.ts";
import { showsEnvironmentSection } from "../utils/environmentSection.ts";
import { hasStoragePath } from "../utils/storagePath.ts";
import {
  captureImageDeepLink,
  discardPendingImageDeepLink,
  EMPTY_IMAGE_DEEP_LINK_CAPTURE,
  reconcilePartialGeneratedContentOwners,
  resolveGeneratedContentDeepLink,
  type ProjectContentLoadState,
} from "../utils/generatedContentLookup.ts";
import {
  resolveBackgroundRemovalConfig,
  backgroundRemovalCreditCost,
} from "../utils/backgroundRemoval.ts";
import { getColorSwatchDisplayLabel } from "../utils/colorSwatches.ts";
import {
  normalizeResolution,
  parseProductDimensionCm,
  resolveDecorationDimensions,
} from "../utils/studioRouteHelpers.ts";
import { scalingDimensionsComplete } from "../utils/dimensionUtils.ts";
import * as geminiService from "../services/geminiService.ts";
import { InsufficientCreditsError } from "../services/creditService";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { AppError } from "../utils/appError";
import { resolveErrorMessage } from "../utils/errorResolver";
import {
  logWarning,
  reportError,
  sanitizeFileNameForReport,
} from "../utils/errorReporting";
import {
  resolveLoadingStallCause,
  STALL_CAUSE_I18N_KEY,
  STALL_DETAILS_AFTER_MS,
  STALL_ACTIONS_AFTER_MS,
} from "../utils/loadingStall";
import {
  generateAspectRatioConversion,
  generateCutoutImage,
  generateDetailsImage,
  generateFreePromptImage,
  generateRotationImage,
  generateTransformTechnicalDrawingImage,
  recolorImage,
} from "../services/geminiService.ts";
import {
  DEFAULT_INPAINT_STYLE_MODEL,
  INPAINT_STYLE_TEMPERATURE,
  executeBackgroundRemoval,
  executeBriaProductShotGeneration,
  executeUpscale,
  toAppImageFile,
  type UpscaleFactor,
  type Upscaler,
} from "../services/imageGenerationEngine.ts";
import { generateDimensional2DDrawing } from "../services/dimensionDrawingService.ts";
import { resolveDimensionPreset } from "../utils/dimensionPresets";
import {
  setGenerationContext,
  snapshotGenerationContext,
  callGenerateImage,
} from "../services/backendAiService.ts";
import { generateDecorationImage } from "../services/decorationGenerationService.ts";
import ImageViewerModal from "../components/ImageViewerModal.tsx";
import PanelErrorBoundary, {
  PanelErrorDialog,
  reportPanelCrash,
} from "../components/PanelErrorBoundary.tsx";
import {
  previewRetryDelayMs,
  shouldRetryPreviewResolve,
} from "../utils/lifestylePresetPreviewRetry.ts";
import { CreditDisplay } from "../components/CreditDisplay";
import QueueOverlay from "../components/QueueOverlay.tsx";
import { GENERATION_QUEUE_ENABLED } from "../utils/featureFlags.ts";
import { NotificationHub } from "../components/NotificationHub.tsx";
import { useNotificationStore } from "../stores/notificationStore.ts";
import { reconcileShopvibesPushRecord } from "../services/shopvibes/pushClient.ts";

import {
  createFileRoute,
  useNavigate,
  Link,
  Outlet,
  useLocation,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import {
  FolderIcon,
  QueueListIcon,
  SparklesIcon,
  WorkflowIcon,
  XCircleIcon,
  PlusCircleIcon,
  ExclamationTriangleIcon,
} from "../components/icons.tsx";
import { ImageEditor } from "@/src/components/ImageEditor.tsx";
import type { ImageEditorApplyPayload } from "@/src/components/ImageEditor.tsx";
import {
  EDIT_IMAGE_TYPE_BY_EDITOR_MODE,
  resolveEditDetailsDisplay,
  userAuthoredPrompt,
} from "../utils/editDetails.ts";
import {
  selectEditMarkingAssets,
  type RegenerateEditOverride,
} from "../utils/regenerateEditList.ts";
import { DEBUG_TOOLS_ENABLED } from "@/src/utils/debugFlags.ts";
import {
  downloadCustomEditDebugReport,
  isCustomEditDebugArmed,
} from "@/src/utils/customEditDebugReport.ts";
import {
  COST_BG_REMOVE,
  COST_IMAGE_1K,
  COST_IMAGE_2K,
  COST_IMAGE_4K,
  COST_RECOLOR,
} from "@/src/utils/constants.ts";
import {
  generateUniqueFilename,
  getClosestAspectRatio,
  uniquifyFilename,
} from "@/src/utils/helpers.ts";
import { deserializeBrandTemplate } from "../utils/brandTemplateHelpers";
import {
  isProjectUuid,
  resolveProjectRouteState,
} from "@/src/utils/projectRouteGuard";
import ProjectNotFound from "@/src/components/ProjectNotFound";
import ProjectContext from "@/src/context/ProjectContext.tsx";
import {
  loadProjectStudioProducts,
  persistStudioProduct,
  persistGeneratedContent,
  uploadGeneratedImage,
  updateStudioProductCurrentImage,
  loadMultiProductScenesFromDB,
  deleteGeneratedImage,
  getUrlFromPath,
  uploadFileAsProduct,
  createProductFromDataUrl,
  uploadAndResolve,
  persistGeneratedSettingsImage,
  renameGeneratedContent,
  FALLBACK_IMAGE_TITLE,
  FALLBACK_VIDEO_TITLE,
} from "@/src/services/studioPersistenceService.ts";
import type { ClientImageProvenance } from "@/src/services/clientImagePersist.ts";
import { updateBrandTemplate } from "@/src/services/brandTemplateService.ts";
import {
  getMilieuFixedNodeIdsMissingStyle,
  hasMilieuFixedNodesMissingStyle,
} from "@/src/utils/workflowUtils.ts";
import {
  createLifestylePreset,
  deleteLifestylePreset,
  getLifestylePresetPreviewUrl,
  getLifestylePresetReferenceBase64,
  listLifestylePresets,
  updateLifestylePreset,
} from "@/src/services/lifestylePresetService.ts";
import { I18N_STORAGE_KEY } from "@/src/i18n";
import {
  trackImageDownloaded,
  trackImageRenamed,
} from "@/src/services/analyticsService";
import { AppHeader } from "../components/AppHeader.tsx";
import {
  useProjectStudioState,
  type EditTarget,
} from "@/src/hooks/useProjectStudioState.ts";
import {
  imageModelDisplayName,
  resolveEffectiveRecolorModel,
} from "@/src/utils/modelLabels.ts";
import { composeSceneContext } from "@/src/utils/promptContext.ts";
import {
  generationProductLightsSnapshot,
  resolveProductLightsSettings,
} from "@/src/utils/productLights.ts";

const MILIEU_CREATIVE_LABEL = "Milieu Creative";
const RECOLOR_MODEL_LABEL = "Gemini";
const MILIEU_FIXED_LABEL = "Milieu Fixed";

// SIS-500: where StudioPanel hides the environment section (and with it the
// Custom Context textarea), a value typed while another mode was active must
// not enter that mode's live-snapshot settings. `showsEnvironmentSection` is
// the panel's own rule — sharing it keeps the snapshot from claiming a
// context the user could not have entered. Regeneration overrides carry
// deliberately restored settings (e.g. a viewer-created rotation keeps its
// stored context) and are guarded separately in the regenerate path.
const hidesCustomContext = (
  imageType: string | undefined,
  modes: { detailsMode?: string; technicalDrawingMode?: string } = {}
) => !showsEnvironmentSection(imageType, modes);

// Environment-control snapshot for the persisted generationSettings, so the
// details panel can still show these after reload (fullSettings is never
// persisted). Spread into write sites whose mode shows the Environment
// section in StudioPanel.
const environmentSnapshotSettings = (settings: GenerationSettings) => ({
  useTrueToneStudio: settings.useTrueToneStudio,
  productLightsOn: settings.productLightsOn,
  productLightsWarmth: settings.productLightsOn
    ? settings.productLightsWarmth
    : undefined,
});

const ProjectStudio: React.FC = () => {
  const { t, i18n } = useTranslation();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const updateNotification = useNotificationStore((s) => s.updateNotification);
  const { projectId } = Route.useParams();
  // Set project context for backend AI generation (GCS path construction)
  React.useEffect(() => {
    setGenerationContext(projectId);
  }, [projectId]);
  const navigate = useNavigate();
  const location = useLocation();

  const getWorkflowMilieuFixedStyleRequiredMessage = React.useCallback(
    (workflowName?: string) =>
      workflowName
        ? t("studio.workflowInpaintStyleRequiredRun", {
            workflowName,
            defaultValue:
              'Workflow "{{workflowName}}" has a Milieu Fixed node without a room style. Select or upload a style before running it.',
          })
        : t("studio.workflowInpaintStyleRequiredError", {
            defaultValue:
              "Milieu Fixed requires a room style. Select or upload a style before saving or running this workflow.",
          }),
    [t]
  );

  // Restore language from localStorage when entering project (keeps dashboard choice)
  React.useEffect(() => {
    try {
      const stored = window.localStorage.getItem(I18N_STORAGE_KEY);
      if (
        stored &&
        (stored === "en" || stored === "de") &&
        i18n.language !== stored
      ) {
        i18n.changeLanguage(stored);
      }
    } catch (_) {}
  }, []);

  // Auth State
  const user = auth.currentUser;

  // Data Connect Hooks
  const { data: projectData } = useGetProject(
    { id: projectId },
    { enabled: !!user }
  );
  const { data: brandsData, refetch: refetchBrands } = useListBrandTemplates({
    enabled: !!user,
  });

  const { data: workflowsData } = useListUserWorkflows();
  const { mutateAsync: touchWorkflowLastUsed } = useTouchWorkflowLastUsed();

  const workflows: Workflow[] = React.useMemo(() => {
    if (!workflowsData?.workflows) return [];
    return workflowsData.workflows.map((workflow) => {
      const settings = (workflow.settings || {}) as {
        nodes?: WorkflowNode[];
        edges?: WorkflowEdge[];
        createdAt?: number;
      };
      return {
        id: workflow.id,
        name: workflow.name,
        nodes: settings.nodes || [],
        edges: settings.edges || [],
        createdAt: settings.createdAt ?? 0,
        lastEditedAt: workflow.lastEditedAt ?? undefined,
        lastUsedAt: workflow.lastUsedAt ?? undefined,
      };
    });
  }, [workflowsData]);

  // Global State
  const [error, setError] = React.useState<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = React.useState(true);
  // Products load failed — not the same as a project without products. Bumping
  // the attempt counter re-runs the load effect (the retry action).
  const [productsLoadFailed, setProductsLoadFailed] = React.useState(false);
  const [productsLoadAttempt, setProductsLoadAttempt] = React.useState(0);
  const [projectContentLoadState, setProjectContentLoadState] =
    React.useState<ProjectContentLoadState>({
      projectId,
      status: "loading",
    });
  // Pending "open this image" deep link (from the global library's "Open in
  // project" or a cross-project notification), latched off the URL. Held in
  // state — not the URL — so it survives a tab switch (the tab buttons navigate
  // without search) and drives the viewer once this project's data is ready.
  const [deepLinkCapture, setDeepLinkCapture] = React.useState(
    EMPTY_IMAGE_DEEP_LINK_CAPTURE
  );
  const loadedCollectionsProjectRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    const loadProducts = async () => {
      if (!projectId) return;
      const isProjectTransition =
        loadedCollectionsProjectRef.current !== projectId;
      const productsAtLoadStart = studioProducts;
      const scenesAtLoadStart = multiProductScenes;

      setProjectContentLoadState({ projectId, status: "loading" });
      if (isProjectTransition) {
        loadedCollectionsProjectRef.current = projectId;
        setStudioProducts([]);
        setMultiProductScenes([]);
        // An open viewer still points at the previous project's content;
        // with the collections cleared its gallery would render empty and its
        // actions would target a product that no longer exists in state.
        setActiveViewer(null);
      }

      try {
        setIsLoadingProducts(true);
        setProductsLoadFailed(false);
        const productResult = await loadProjectStudioProducts(projectId);
        if (!mounted) return;

        const regularProducts = productResult.items.map((product) => ({
          ...product,
          analysisStatus:
            product.productCategory || product.productDescription
              ? ("success" as const)
              : product.analysisStatus,
        }));

        // Load multiproduct scenes separately (needs productLinks from DB)
        const sceneResult = await loadMultiProductScenesFromDB(projectId);
        if (!mounted) return;

        const isPartial =
          productResult.failedItemIds.length > 0 ||
          sceneResult.failedItemIds.length > 0;

        // Only a project transition replaces the collections wholesale. Any
        // same-project load — complete or partial — reconciles against the
        // load-start snapshot, because generations, deletions and other
        // optimistic writes that landed while the request was in flight are
        // newer than the snapshot the DB returned.
        setStudioProducts((current) =>
          isProjectTransition
            ? regularProducts
            : reconcilePartialGeneratedContentOwners({
                current,
                atLoadStart: productsAtLoadStart,
                loaded: regularProducts,
                failedItemIds: productResult.failedItemIds,
              })
        );
        setMultiProductScenes((current) =>
          isProjectTransition
            ? sceneResult.items
            : reconcilePartialGeneratedContentOwners({
                current,
                atLoadStart: scenesAtLoadStart,
                loaded: sceneResult.items,
                failedItemIds: sceneResult.failedItemIds,
              })
        );

        // A partial load is not promoted to the blocking error banner: a
        // single unparseable row would otherwise pin an unfixable full-screen
        // Retry loop over an otherwise working project. Per-item failures are
        // already reported to Sentry by the loaders, and the deep-link
        // resolver treats "partial" as terminal.
        setProjectContentLoadState({
          projectId,
          status: isPartial ? "partial" : "ready",
        });
      } catch (e) {
        console.error("Failed to load studio products", e);
        if (mounted) {
          setProjectContentLoadState({ projectId, status: "error" });
          setProductsLoadFailed(true);
        }
      } finally {
        if (mounted) setIsLoadingProducts(false);
      }
    };
    loadProducts();
    return () => {
      mounted = false;
    };
  }, [projectId, productsLoadAttempt]);

  React.useEffect(() => {
    // Clean up or reset other state if needed
  }, [projectId]);

  // Project Management State
  const currentProject: ProjectSpace | null = React.useMemo(() => {
    const p = projectData?.projects?.[0];
    if (!p) return null;
    return {
      id: p.id,
      name: p.name,
      lastModified: new Date(p.updatedAt).getTime(),
      data: {
        products: [],
        multiProductScenes: [],
        retailerProfiles: [],
        highlightsConfigs: [],
        workflows: [],
        settings: {
          defaultBackgroundColor: "#FFFFFF",
          logoPosition: "bottom-right",
          dimensionMarker: "both",
          companyLogo: null,
          useAvatar: false,
        },
      },
      brandTemplate: p.brandTemplate
        ? { id: p.brandTemplate.id, name: p.brandTemplate.name }
        : undefined,
    };
  }, [projectData]);

  // Global Brand Library State
  const globalBrands: BrandIdentity[] = React.useMemo(() => {
    if (!brandsData?.brandTemplates) return [];
    return brandsData.brandTemplates.map((template) =>
      deserializeBrandTemplate(template as any)
    );
  }, [brandsData]);

  const currentBrand = React.useMemo(() => {
    if (!currentProject?.brandTemplate) return null;
    return (
      globalBrands.find((b) => b.id === currentProject.brandTemplate!.id) ||
      null
    );
  }, [currentProject, globalBrands]);

  const {
    activeViewer,
    setActiveViewer,
    companyLogo,
    setCompanyLogo,
    savedColorSwatches,
    setSavedColorSwatches,
    defaultBackgroundColor,
    setDefaultBackgroundColor,
    retailerProfiles,
    setRetailerProfiles,
    logoPosition,
    setLogoPosition,
    logoScale,
    setLogoScale,
    dimensionMarker,
    setDimensionMarker,
    dimensionPresetId,
    setDimensionPresetId,
    dimensionShowLabel,
    setDimensionShowLabel,
    dimensionCapStyle,
    setDimensionCapStyle,
    dimensionTargetFormat,
    setDimensionTargetFormat,
    highlightsConfigs,
    setHighlightsConfigs,
    studioProducts,
    setStudioProducts,
    customLifestylePresets,
    setCustomLifestylePresets,
    customLifestylePresetPreviews,
    setCustomLifestylePresetPreviews,
    multiProductScenes,
    setMultiProductScenes,
    studioVibe,
    setStudioVibe,
    studioScene,
    setStudioScene,
    studioImageType,
    setStudioImageType,
    studioRotationAngle,
    setStudioRotationAngle,
    studioRotationMode,
    setStudioRotationMode,
    studioRotationModel,
    setStudioRotationModel,
    studioRotationQuality,
    setStudioRotationQuality,
    studioImageGeneration,
    setStudioImageGeneration,
    studioAspectRatio,
    setStudioAspectRatio,
    studioVibeContext,
    setStudioVibeContext,
    useTrueToneStudio,
    setUseTrueToneStudio,
    productLightsOn,
    setProductLightsOn,
    productLightsWarmth,
    setProductLightsWarmth,
    studioDecorationSurfaceOverride,
    setStudioDecorationSurfaceOverride,
    studioDecorationFramingPreference,
    setStudioDecorationFramingPreference,
    studioDecorationRoomPlacementPreference,
    setStudioDecorationRoomPlacementPreference,
    studioDecorationViewAngle,
    setStudioDecorationViewAngle,
    studioSelectedProfileId,
    setStudioSelectedProfileId,
    modelType,
    setModelType,
    colorSwatches,
    setColorSwatches,
    studioRecolorOptions,
    setStudioRecolorOptions,
    useAvatar,
    setUseAvatar,
    studioAvatar,
    setStudioAvatar,
    studioHighlightsConfigId,
    setStudioHighlightsConfigId,
    studioHighlightsInputs,
    setStudioHighlightsInputs,
    studioTechnicalDrawingMode,
    setStudioTechnicalDrawingMode,
    studioTechnicalDrawingMaterialImage,
    setStudioTechnicalDrawingMaterialImage,
    studioTechnicalDrawingMaterial,
    setStudioTechnicalDrawingMaterial,
    studioTechnicalDrawingShadowStyle,
    setStudioTechnicalDrawingShadowStyle,
    studioDetailsFocus,
    setStudioDetailsFocus,
    studioDetailsMode,
    setStudioDetailsMode,
    studioDetailsCameraPerspective,
    setStudioDetailsCameraPerspective,
    studioDetailsMaterial,
    setStudioDetailsMaterial,
    studioDetailsMaterialReference,
    setStudioDetailsMaterialReference,
    studioDetailsSceneDescription,
    setStudioDetailsSceneDescription,
    studioCutoutShadows,
    setStudioCutoutShadows,
    studioBackgroundRemoval,
    setStudioBackgroundRemoval,
    studioProductDetails,
    setStudioProductDetails,
    editingContent,
    setEditingContent,
    isApplyingEdit,
    setIsApplyingEdit,
    isRemovingBackgroundInViewer,
    setIsRemovingBackgroundInViewer,
    isAspectRatioGeneratingInViewer,
    setIsAspectRatioGeneratingInViewer,
    backgroundRemovalProgress,
    setBackgroundRemovalProgress,
    isUpscalingInViewer,
    setIsUpscalingInViewer,
    isRotatingInViewer,
    setIsRotatingInViewer,
    isRecoloringInViewer,
    setIsRecoloringInViewer,
    isRegeneratingInViewer,
    setIsRegeneratingInViewer,
    generationQueue,
    setGenerationQueue,
    isQueueOpen,
    setIsQueueOpen,
    isQueueProcessing,
    setIsQueueProcessing,
  } = useProjectStudioState();

  // This route stays mounted across projects, so async work started in one
  // project (an upload awaiting GCS) would otherwise write into the next
  // project's state through a setter captured before the switch. Consumers
  // get a setter bound to the project it was handed out for; updates from
  // a stale closure are dropped (SIS-566 review).
  // Assigned during render, not in a layout effect: children's layout
  // effects run before the parent's, so an effect-based update would let a
  // child compare the new setter against the previous project for a beat.
  // Both hooks live above the `!user` early return below — a sign-out
  // re-renders this component with no user before the redirect runs, and a
  // hook after that return would vanish on that render.
  const activeProjectRef = React.useRef(projectId);
  activeProjectRef.current = projectId;
  const setStudioProductsForProject = React.useCallback<
    React.Dispatch<React.SetStateAction<StudioProduct[]>>
  >(
    (update) => {
      const owner = projectId;
      setStudioProducts((current) => {
        if (activeProjectRef.current !== owner) return current;
        return typeof update === "function" ? update(current) : update;
      });
    },
    [projectId, setStudioProducts]
  );

  // --- Image deep-link handling ---------------------------------------------
  // Handled once here in the layout (not per-tab) so it survives switching
  // between the Studio/Library/Workflows tabs and opens the shared,
  // layout-level ImageViewerModal regardless of which tab is active. Both the
  // global library's "Open in project" and cross-project notifications navigate
  // here with `?image=<contentId>`.
  const deepLinkSearch = Route.useSearch();

  // Latch the requested id into state and strip it from the URL immediately, so
  // a refresh won't reopen it and a tab switch can't drop it mid-load.
  React.useEffect(() => {
    const incoming = deepLinkSearch.image;
    const nextCapture = captureImageDeepLink(
      deepLinkCapture,
      projectId,
      incoming
    );
    if (nextCapture === deepLinkCapture) return;

    setDeepLinkCapture(nextCapture);
    if (!incoming) return;
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, image: undefined }),
      replace: true,
    });
  }, [deepLinkSearch.image, deepLinkCapture, projectId, navigate]);

  // Resolve only against data loaded for this project. Both terminal load
  // states consume the link: a complete load proves a miss is a deleted image,
  // while a partial load can only report that the image is unavailable — it
  // may live in an owner that failed to load. Only "loading" and "error" keep
  // the link pending (the error banner's Retry/dismiss own that resolution).
  React.useEffect(() => {
    const pendingDeepLink = deepLinkCapture.pending;
    // Let the URL-capture effect finish first. This prevents an older pending
    // link from opening during the render that introduces a newer `?image=`.
    if (!pendingDeepLink || deepLinkSearch.image) return;

    const decision = resolveGeneratedContentDeepLink({
      pending: pendingDeepLink,
      currentProjectId: projectId,
      loadState: projectContentLoadState,
      products: studioProducts,
      scenes: multiProductScenes,
    });
    if (decision.kind === "wait") return;

    setDeepLinkCapture((current) =>
      current.pending === pendingDeepLink
        ? { ...current, pending: null }
        : current
    );
    if (decision.kind === "discard") return;
    if (decision.kind === "missing") {
      toast.error(t("library.imageNotFound"));
      return;
    }
    if (decision.kind === "unavailable") {
      toast.error(t("library.imageLoadFailed"));
      return;
    }

    setActiveViewer({
      content: decision.content,
      productId: decision.productId,
      currentContentId: pendingDeepLink.imageId,
    });
  }, [
    deepLinkCapture.pending,
    deepLinkSearch.image,
    projectContentLoadState,
    projectId,
    studioProducts,
    multiProductScenes,
    setActiveViewer,
    t,
  ]);

  // User-level settings — read-only here; edits happen on /settings/general.
  // flushUserSettings persists any pending debounced edits before leaving.
  const {
    dimensionDrawingSettings,
    softLighting,
    neverHideParts,
    alwaysCenter,
    desiredRoomStyle,
    namingTemplate,
    aiWatermarkEnabled,
    aiWatermarkStyle,
    flushSave: flushUserSettings,
  } = useUserSettings();

  const activeDimensionPreset = resolveDimensionPreset(
    dimensionDrawingSettings,
    dimensionPresetId
  );

  // Vibes and Scenes - derived from brand identity
  const vibes = React.useMemo(() => currentBrand?.vibes || [], [currentBrand]);
  const scenes = React.useMemo(
    () => currentBrand?.scenes || [],
    [currentBrand]
  );
  // Derived values for backward compatibility within this component
  const viewedContent = activeViewer?.content ?? null;
  const activeGallery = React.useMemo(
    () =>
      activeViewer
        ? {
            productId: activeViewer.productId,
            currentContentId: activeViewer.currentContentId,
          }
        : null,
    [activeViewer]
  );

  // Helper: update the viewed content in the viewer (keeps productId stable)
  const updateViewerContent = React.useCallback(
    (uploaded: GeneratedContent) => {
      setActiveViewer((prev) =>
        prev
          ? { ...prev, content: uploaded, currentContentId: uploaded.id }
          : prev
      );
    },
    []
  );

  // After persisting generated content to the database, the DB assigns a new
  // UUID that differs from the temporary client-side ID. This helper patches
  // all in-memory references so that subsequent operations (e.g. rating) use
  // the correct database ID. That includes the notification store: its
  // (localStorage-persisted) entries feed cross-project `?image=` deep links,
  // which resolve against freshly loaded DB data and therefore need the DB id.
  const syncContentDbId = React.useCallback(
    (productId: string, oldId: string, dbId: string) => {
      useNotificationStore.getState().syncContentId(oldId, dbId);
      // A Shopvibes push inside this window stamped its record against the
      // temporary id, so the column write matched no row. This is the first
      // moment the real id exists, and therefore the only chance to write
      // it: a re-push would be refused as a duplicate.
      void reconcileShopvibesPushRecord(oldId, dbId);
      setStudioProducts((prev) =>
        prev.map((p) => {
          if (p.id !== productId) return p;
          return {
            ...p,
            generatedContent: p.generatedContent.map((c) =>
              c.id === oldId ? { ...c, id: dbId } : c
            ),
          };
        })
      );
      setMultiProductScenes((prev) =>
        prev.map((s) => {
          if (s.id !== productId) return s;
          return {
            ...s,
            generatedContent: s.generatedContent.map((c) =>
              c.id === oldId ? { ...c, id: dbId } : c
            ),
          };
        })
      );
      setActiveViewer((prev) =>
        prev && prev.content.id === oldId
          ? {
              ...prev,
              content: { ...prev.content, id: dbId },
              currentContentId:
                prev.currentContentId === oldId ? dbId : prev.currentContentId,
            }
          : prev
      );
    },
    []
  );

  const createRecolorSwatch = React.useCallback(
    (
      color: string,
      referenceImage?: ImageFile,
      usageReferenceImage?: ImageFile,
      material?: string
    ): ColorSwatch => ({
      id: crypto.randomUUID(),
      value: color,
      isSelected: true,
      referenceImage,
      usageReferenceImage,
      material,
    }),
    []
  );

  const buildRecolorGenerationSettings = React.useCallback(
    (
      color: string,
      aspectRatio?: string,
      referenceImage?: ImageFile,
      usageReferenceImage?: ImageFile,
      material?: string,
      extraSettings: Record<string, unknown> = {}
    ) => {
      return {
        // Legacy fallback only — callers pass the actual model via
        // extraSettings, which must win so GPT recolors aren't persisted
        // with the generic "Gemini" label.
        model: RECOLOR_MODEL_LABEL,
        ...extraSettings,
        imageType: "Re-Coloring",
        recolorColor: color,
        recolorReferenceImage: referenceImage,
        recolorUsageReferenceImage: usageReferenceImage,
        recolorMaterial: material,
        aspectRatio,
      };
    },
    []
  );

  const getSavedRecolorSwatch = React.useCallback(
    (
      color: string,
      savedSettings: Partial<GenerationSettings>,
      generationSettings: GeneratedContent["generationSettings"]
    ): ColorSwatch | null => {
      const explicitReference = generationSettings?.recolorReferenceImage;
      const explicitUsageReference =
        generationSettings?.recolorUsageReferenceImage;
      const explicitMaterial = generationSettings?.recolorMaterial;

      if (explicitReference || explicitUsageReference || explicitMaterial) {
        return createRecolorSwatch(
          color,
          explicitReference || undefined,
          explicitUsageReference || undefined,
          explicitMaterial
        );
      }

      const matchingSavedSwatch = (savedSettings.colorSwatches || []).find(
        (swatch) => swatch.isSelected && swatch.value === color
      );

      return matchingSavedSwatch
        ? {
            ...matchingSavedSwatch,
            id: matchingSavedSwatch.id || crypto.randomUUID(),
            isSelected: true,
          }
        : null;
    },
    [createRecolorSwatch]
  );

  const isInpaintSceneContent = React.useCallback(
    (content: GeneratedContent) =>
      content.imageType === "Inpaint Scene" ||
      content.generationSettings?.imageType === "Inpaint Scene",
    []
  );

  const getSourceGalleryContent = React.useCallback(
    (product: StudioProduct): GeneratedContent => ({
      id: "source",
      title: product.originalImage.name,
      image: getImageSrc(product.originalImage) || null,
      storagePath: product.originalImage.storagePath,
      thumbnailUrl: product.originalImage.thumbnailUrl,
      text: "Source Image",
      video: null,
      timestamp: product.originalImage.uploadedAt || undefined,
    }),
    []
  );

  /**
   * Renames a gallery image and patches every in-memory copy of it.
   *
   * Gallery content lives in three places here — `studioProducts`,
   * `multiProductScenes` and the `activeViewer` snapshot the modal renders
   * from — so all three are patched, exactly as `syncContentDbId` does. The
   * write is awaited so the viewer can surface a failure and keep the edit
   * open; `filename` moves with `title` because the Details tab reads
   * `filename || title` while the header and downloads read `title` first,
   * and a stale `filename` would leave the two disagreeing.
   */
  const handleRenameGalleryImage = React.useCallback(
    async (contentId: string, title: string) => {
      await renameGeneratedContent(contentId, title);

      const patch = (c: GeneratedContent) =>
        c.id === contentId ? { ...c, title, filename: title } : c;
      setStudioProducts((prev) =>
        prev.map((p) => ({
          ...p,
          generatedContent: p.generatedContent.map(patch),
        }))
      );
      setMultiProductScenes((prev) =>
        prev.map((s) => ({
          ...s,
          generatedContent: s.generatedContent.map(patch),
        }))
      );
      setActiveViewer((prev) =>
        prev && prev.content.id === contentId
          ? { ...prev, content: patch(prev.content) }
          : prev
      );

      trackImageRenamed({
        imageId: contentId,
        surface: "project_library",
        projectId,
        productId: activeGallery?.productId,
      });
    },
    [projectId, activeGallery?.productId]
  );

  const getVisibleGalleryImages = React.useCallback(
    (productId: string): GeneratedContent[] => {
      const product = studioProducts.find((p) => p.id === productId);
      const scene = multiProductScenes.find((s) => s.id === productId);
      const images = product
        ? product.generatedContent
        : scene
          ? scene.generatedContent
          : [];

      return images
        .filter((content) => !isInpaintSceneContent(content))
        .slice()
        .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
    },
    [isInpaintSceneContent, multiProductScenes, studioProducts]
  );

  const activeGalleryProduct = React.useMemo(
    () =>
      activeGallery
        ? studioProducts.find((p) => p.id === activeGallery.productId)
        : undefined,
    [activeGallery, studioProducts]
  );

  // Saved direction sketches for the open product/scene — reusable in the
  // Direction Sketch Creator without regenerating.
  const activeDirectionSketches = React.useMemo<ImageFile[]>(() => {
    const owner =
      activeGalleryProduct ??
      (activeGallery
        ? multiProductScenes.find((s) => s.id === activeGallery.productId)
        : undefined);
    return (owner?.generatedContent ?? [])
      .filter((c) => c.imageType === "Direction Sketch")
      .map((c) => ({
        storagePath: c.storagePath,
        thumbnailUrl: c.thumbnailUrl ?? c.image ?? undefined,
        fullUrl: c.fullUrl,
        base64: "",
        mimeType: c.mimeType ?? "image/png",
        name: c.title ?? "Sketch",
      }));
  }, [activeGalleryProduct, activeGallery, multiProductScenes]);

  /**
   * The active gallery's FULL image list, unfiltered and unsorted.
   *
   * `activeGalleryVisibleImages` drops inpaint scene rows, so uniquifying a
   * rename against it could mint a title a hidden sibling already holds —
   * generation uniquifies against the whole set, and the two should agree.
   */
  const activeGalleryAllImages = React.useMemo<GeneratedContent[]>(() => {
    if (!activeGallery) return [];
    const owner =
      studioProducts.find((p) => p.id === activeGallery.productId) ??
      multiProductScenes.find((s) => s.id === activeGallery.productId);
    return owner?.generatedContent ?? [];
  }, [activeGallery, studioProducts, multiProductScenes]);

  const activeGalleryVisibleImages = React.useMemo(
    () =>
      activeGallery ? getVisibleGalleryImages(activeGallery.productId) : [],
    [activeGallery, getVisibleGalleryImages]
  );

  const activeGalleryCurrentIndex = React.useMemo(() => {
    if (!activeGallery) return undefined;
    if (activeGallery.currentContentId === null) {
      return activeGalleryProduct ? -1 : undefined;
    }

    const index = activeGalleryVisibleImages.findIndex(
      (content) => content.id === activeGallery.currentContentId
    );
    return index >= 0 ? index : undefined;
  }, [activeGallery, activeGalleryProduct, activeGalleryVisibleImages]);

  const activeGalleryContent = React.useMemo(() => {
    if (!activeGallery) return viewedContent;
    if (activeGallery.currentContentId === null) {
      return activeGalleryProduct
        ? getSourceGalleryContent(activeGalleryProduct)
        : viewedContent;
    }

    return (
      activeGalleryVisibleImages.find(
        (content) => content.id === activeGallery.currentContentId
      ) ?? viewedContent
    );
  }, [
    activeGallery,
    activeGalleryProduct,
    activeGalleryVisibleImages,
    getSourceGalleryContent,
    viewedContent,
  ]);

  const runProductAnalysis = useProductAnalysis(setStudioProducts);

  const handleUpdateProductAnalysis = React.useCallback(
    (productId: string, category: string, description: string) => {
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? {
                ...p,
                productCategory: category || undefined,
                productDescription: description || undefined,
                analysisStatus: "success",
                analysisError: null,
              }
            : p
        )
      );
    },
    []
  );

  const handleRetryProductAnalysis = React.useCallback(
    (productId: string) => {
      const product = studioProducts.find((p) => p.id === productId);
      if (!product) return;
      void runProductAnalysis(product.id, product.originalImage);
    },
    [runProductAnalysis, studioProducts]
  );

  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Initialize project-scoped state once per project, keyed by id. The
  // GetProject query refetches while the user works (focus refetch, and every
  // generation bumps project.updatedAt), which rebuilds the currentProject
  // object — re-running this per object identity wiped live studio controls
  // mid-session.
  const initializedProjectIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (
      !currentProject ||
      initializedProjectIdRef.current === currentProject.id
    )
      return;
    initializedProjectIdRef.current = currentProject.id;

    // Do NOT overwrite studioProducts from blob, as we load them from Data Connect now
    setMultiProductScenes(currentProject.data.multiProductScenes || []);

    setRetailerProfiles(currentProject.data.retailerProfiles);
    setHighlightsConfigs(currentProject.data.highlightsConfigs);

    setDefaultBackgroundColor(
      currentProject.data.settings.defaultBackgroundColor
    );
    setLogoPosition(currentProject.data.settings.logoPosition);
    setLogoScale(currentProject.data.settings.logoScale ?? 0.15);
    setDimensionMarker(currentProject.data.settings.dimensionMarker);
    // Not a persisted project setting — reset so a style chosen in one
    // project doesn't leak into the next one's generations.
    setDimensionCapStyle("arrow");
    setDimensionTargetFormat("original");
    // softLighting, neverHideParts, alwaysCenter, desiredRoomStyle, namingTemplate
    // are now user-level settings managed by useUserSettings hook
    setCompanyLogo(currentProject.data.settings.companyLogo);
    setUseAvatar(currentProject.data.settings.useAvatar || false);

    // Vibes are project-specific: clear on switching to a different project
    // (no-op on first mount, where the vibe is still at its default).
    setStudioVibe("");
  }, [currentProject]);

  // Preset ids whose preview URL has been requested this session: in flight,
  // retrying, or given up on. The resolver is uncached, so this is what stops
  // the effect below from re-issuing a download — see the comment there.
  const requestedPreviewIds = React.useRef<Set<string>>(new Set());
  // Lets a retry timer that outlives the component skip its work instead of
  // resolving into a store nobody renders any more.
  const previewEffectAlive = React.useRef(true);
  React.useEffect(() => {
    previewEffectAlive.current = true;
    return () => {
      previewEffectAlive.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (!projectId || !user) {
      setCustomLifestylePresets([]);
      setCustomLifestylePresetPreviews({});
      // The map is wiped, so nothing may still count as requested — a preset
      // that was resolving would otherwise never be fetched again. A retry
      // timer still pending for one of these ids sees it is gone and stops.
      requestedPreviewIds.current.clear();
      return;
    }

    let cancelled = false;

    void listLifestylePresets(projectId)
      .then((presets) => {
        if (cancelled) return;
        setCustomLifestylePresets(presets);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load custom lifestyle presets", error);
        setCustomLifestylePresets([]);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, user]);

  // Resolve preview URLs for presets that don't have one yet.
  //
  // This effect reads AND writes `customLifestylePresetPreviews`, which is
  // what produced a permanent re-render + refetch loop in the studio layout
  // (React error #185): the writer always produced a fresh `{ ...prev }`, so a
  // pass that resolved nothing still changed the object identity, re-ran the
  // effect and re-issued the same downloads for a preset whose reference
  // object was deleted or unreadable. Three rules make it loop-free:
  //
  // 1. A preset is only requested when the map has no URL for it AND it has
  //    not been requested this session (`requestedPreviewIds`). Reading the
  //    map is what lets a preview written elsewhere
  //    (`handleCreateLifestylePreset` seeds the freshly uploaded thumbnail)
  //    short-circuit the fetch, instead of re-fetching and overwriting it
  //    with the full-resolution URL.
  // 2. A write happens only when a URL resolved AND differs from the stored
  //    one — a failed resolve writes nothing, so it cannot re-trigger the
  //    effect. Writes are therefore bounded by the number of presets.
  // 3. A failed preset is NOT re-armed for the effect. Its retries are decided
  //    per preset by `shouldRetryPreviewResolve` (bounded, terminal on a
  //    missing or forbidden object) and scheduled inside its own resolve
  //    chain with backoff, so another preview landing — which re-runs this
  //    effect — never re-requests it. Re-arming on failure meant one broken
  //    preset was fetched and reported once per healthy preset that resolved.
  //
  // There is deliberately no cancellation flag: a resolved URL is keyed by
  // preset id, which is valid regardless of how the list changed while it was
  // in flight. Dropping it would leave the id marked and the card blank —
  // same reasoning as `requestedUrlPaths` in `useColorSwatches`.
  React.useEffect(() => {
    const unresolved = customLifestylePresets.filter(
      (preset) =>
        !customLifestylePresetPreviews[preset.id] &&
        !requestedPreviewIds.current.has(preset.id)
    );
    if (unresolved.length === 0) return;

    const resolvePreview = (
      preset: (typeof unresolved)[number],
      attempt: number
    ) => {
      // The id was cleared (project or user went away) or the layout is gone:
      // nothing is waiting for this URL any more.
      if (
        !previewEffectAlive.current ||
        !requestedPreviewIds.current.has(preset.id)
      )
        return;
      getLifestylePresetPreviewUrl(preset).then(
        (url) => {
          setCustomLifestylePresetPreviews((prev) =>
            prev[preset.id] === url ? prev : { ...prev, [preset.id]: url }
          );
        },
        (error) => {
          if (shouldRetryPreviewResolve(attempt, error)) {
            window.setTimeout(
              () => resolvePreview(preset, attempt + 1),
              previewRetryDelayMs(attempt)
            );
            return;
          }
          // Given up for this session. A never-resolving preview is exactly
          // the input that used to spin the layout, so it must be visible in
          // Sentry rather than only inferable from a burst of #185s — once
          // per preset, not once per attempt.
          reportError(error, {
            context: "lifestylePresetPreview",
            presetId: preset.id,
            referenceImagePath: preset.referenceImagePath,
            attempts: attempt,
          });
        }
      );
    };

    for (const preset of unresolved) {
      requestedPreviewIds.current.add(preset.id);
      resolvePreview(preset, 1);
    }
  }, [customLifestylePresets, customLifestylePresetPreviews]);

  const lifestylePresets = React.useMemo(
    () => [
      ...LIFESTYLE_PRESETS,
      ...customLifestylePresets.map((preset) => ({
        id: preset.id,
        label: preset.label,
        description: preset.description,
        referenceImage:
          customLifestylePresetPreviews[preset.id] ||
          `data:${preset.referenceImageMimeType};base64,`,
        referenceImageMimeType: preset.referenceImageMimeType,
        referenceImagePath: preset.referenceImagePath,
        isCustom: true,
      })),
    ],
    [customLifestylePresetPreviews, customLifestylePresets]
  );

  const handleCreateLifestylePreset = React.useCallback(
    async (image: ImageFile) => {
      if (!projectId) throw new Error("No project selected");

      const analysis =
        await geminiService.analyzeLifestyleReferenceImage(image);
      const savedPreset = await createLifestylePreset(projectId, image, {
        label: analysis.label?.trim() || image.name.replace(/\.[^.]+$/, ""),
        description:
          analysis.description?.trim() ||
          analysis.label?.trim() ||
          image.name.replace(/\.[^.]+$/, ""),
      });

      setCustomLifestylePresets((prev) => [savedPreset, ...prev]);
      // Use thumbnailUrl for display, or fetch preview from storage
      const previewUrl =
        image.thumbnailUrl || (await getLifestylePresetPreviewUrl(savedPreset));
      setCustomLifestylePresetPreviews((prev) => ({
        ...prev,
        [savedPreset.id]: previewUrl,
      }));

      return {
        id: savedPreset.id,
        label: savedPreset.label,
        description: savedPreset.description,
        referenceImage: previewUrl,
        referenceImageMimeType: image.mimeType,
        referenceImagePath: savedPreset.referenceImagePath,
        isCustom: true,
      };
    },
    [projectId]
  );

  const handleDeleteLifestylePreset = React.useCallback(
    async (presetId: string) => {
      if (!projectId) return;
      setCustomLifestylePresets((prev) =>
        prev.filter((p) => p.id !== presetId)
      );
      setCustomLifestylePresetPreviews((prev) => {
        const next = { ...prev };
        delete next[presetId];
        return next;
      });
      await deleteLifestylePreset(projectId, presetId).catch((err) =>
        console.warn("Failed to delete lifestyle preset", err)
      );
    },
    [projectId]
  );

  const handleSetVibes = async (value: React.SetStateAction<Vibe[]>) => {
    if (!currentBrand) {
      setError(
        "No brand identity linked. Please link a brand to manage vibes."
      );
      return;
    }

    const newVibes = typeof value === "function" ? value(vibes) : value;
    try {
      const updatedBrand = { ...currentBrand, vibes: newVibes };
      await updateBrandTemplate(updatedBrand);
      await refetchBrands();
    } catch (e) {
      console.error("Failed to update brand vibes", e);
      setError("Failed to update brand vibes");
    }
  };

  const handleSetScenes = async (value: React.SetStateAction<Scene[]>) => {
    if (!currentBrand) {
      setError(
        "No brand identity linked. Please link a brand to manage scenes."
      );
      return;
    }

    const newScenes = typeof value === "function" ? value(scenes) : value;
    try {
      const updatedBrand = { ...currentBrand, scenes: newScenes };
      await updateBrandTemplate(updatedBrand);
      await refetchBrands();
    } catch (e) {
      console.error("Failed to update brand scenes", e);
      setError("Failed to update brand scenes");
    }
  };

  const handleBackToProjects = async () => {
    try {
      await flushUserSettings();
    } catch (err) {
      console.error("Failed to flush user settings:", err);
      setError("Failed to save settings.");
    }
    navigate({ to: "/", search: { returnTab: "projects" } });
  };

  const normalizeResolution = (value?: string | null): "1K" | "2K" | "4K" => {
    const normalized = (value || "").trim().toUpperCase();
    if (normalized === "2K") return "2K";
    if (normalized === "4K") return "4K";
    return "1K";
  };

  const applyInpaintPromptRequirements = (
    prompt: string,
    styleConfig?: Pick<StyleConfig, "softLighting" | "desiredRoomStyle">
  ) => {
    const directives = [
      "The existing product in the base image is final and must remain unchanged.",
      "Do not rotate, restage, redesign, re-angle, rescale, recolor, relight, crop, or alter the product's geometry, silhouette, materials, or proportions.",
      "Treat the black mask region as locked and immutable. Only generate content in the white region around the product.",
    ];

    if (styleConfig?.softLighting) {
      directives.push(
        "Use only soft, diffused lighting in the generated environment. Avoid harsh shadows, direct sunlight streaks, strong sun patches, and dramatic high-contrast flares."
      );
    }

    if (styleConfig?.desiredRoomStyle?.trim()) {
      directives.push(
        `Design the environment in a ${styleConfig.desiredRoomStyle.trim()} room style.`
      );
    }

    return `${prompt}\n\nCritical Requirements:\n- ${directives.join("\n- ")}`;
  };

  const resolveWorkflowCanvasDimensions = (
    aspectRatio: string,
    resolution: "1K" | "2K" | "4K"
  ) => {
    const shortSide =
      resolution === "4K" ? 4096 : resolution === "2K" ? 2048 : 1024;
    const [rawWidth, rawHeight] = aspectRatio.split(":").map(Number);
    const safeWidth = Number.isFinite(rawWidth) && rawWidth > 0 ? rawWidth : 1;
    const safeHeight =
      Number.isFinite(rawHeight) && rawHeight > 0 ? rawHeight : 1;

    if (safeWidth >= safeHeight) {
      return {
        width: Math.round((shortSide * safeWidth) / safeHeight),
        height: shortSide,
      };
    }

    return {
      width: shortSide,
      height: Math.round((shortSide * safeHeight) / safeWidth),
    };
  };

  const loadWorkflowImage = (image: ImageFile): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      let objectUrl: string | null = null;

      const cleanup = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      };

      img.onload = () => {
        cleanup();
        resolve(img);
      };
      img.onerror = () => {
        cleanup();
        reject(
          new Error(
            `Failed to load image for workflow inpaint (${image.name || "unnamed image"}).`
          )
        );
      };

      void (async () => {
        try {
          const localSrc = image.base64?.trim()
            ? image.base64.trim().startsWith("data:")
              ? image.base64.trim()
              : `data:${image.mimeType || "image/png"};base64,${image.base64.replace(/\s/g, "")}`
            : getImageSrc(image);

          if (!localSrc) {
            throw new Error("Workflow image has no usable src.");
          }

          const response = await fetch(localSrc);
          if (!response.ok) {
            throw new Error(
              `Failed to fetch workflow image (${response.status} ${response.statusText}).`
            );
          }

          const blob = await response.blob();
          objectUrl = URL.createObjectURL(blob);
          img.src = objectUrl;
        } catch (error) {
          cleanup();
          reject(
            error instanceof Error
              ? error
              : new Error("Failed to decode image for workflow inpaint.")
          );
        }
      })();
    });

  const isPngImageFile = (image: ImageFile) =>
    image.mimeType?.toLowerCase().includes("image/png");

  const hasTransparentAlphaChannel = async (
    image: AppImageFile
  ): Promise<boolean> => {
    const loadedImage = await ensureImageBase64(image);
    if (!isPngImageFile(loadedImage)) return false;

    const base64Trimmed = loadedImage.base64?.trim() ?? "";
    if (!base64Trimmed) return false;

    const dataUrl = base64Trimmed.startsWith("data:")
      ? base64Trimmed
      : `data:${loadedImage.mimeType || "image/png"};base64,${base64Trimmed.replace(/\s/g, "")}`;

    return detectTransparentPixels(dataUrl);
  };

  // Flattens an already-transparent cutout onto a white background locally
  // (canvas) and uploads the JPEG to GCS. Used for the "white fill" option when
  // the input is already transparent: the alpha already isolates the subject,
  // so compositing white behind it IS the requested fill — no Bria call needed
  // (it would have nothing to remove and would still charge a credit). Returns
  // a GCS-backed AppImageFile, never base64 into state.
  const buildWhiteFilledFromTransparent = async (
    image: AppImageFile,
    productId: string
  ): Promise<AppImageFile> => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      throw new AppError(
        "auth/not_authenticated",
        "User must be authenticated to remove background."
      );
    }
    const loaded = await ensureImageBase64(image);
    const base64Trimmed = loaded.base64?.trim() ?? "";
    const dataUrl = base64Trimmed.startsWith("data:")
      ? base64Trimmed
      : `data:${loaded.mimeType || "image/png"};base64,${base64Trimmed.replace(/\s/g, "")}`;
    const sourceBlob = await (await fetch(dataUrl)).blob();
    const whiteBlob = await applyXmpDisclosure(
      await convertBlobFormat(sourceBlob, "jpeg"),
      { source: await sourceBlob.arrayBuffer() }
    );
    return uploadAndResolve(
      whiteBlob,
      `f/${userId}/${projectId}/${productId}/generated/cutout_white_${crypto.randomUUID()}.jpg`,
      "image/jpeg",
      "cutout_white.jpg",
      // Client-composed pixels that land in the gallery as a real result
      // (imageType "Cutout"), so they go through the signing choke point —
      // flattening the transparent cutout onto white is one ancestor.
      {
        parents: image.storagePath?.startsWith("f/") ? [image.storagePath] : [],
        editKind: "edit",
      }
    );
  };

  const normalizeWorkflowInpaintSourceImage = async (
    image: AppImageFile,
    _aspectRatio: string,
    productId: string
  ): Promise<AppImageFile> => {
    return ensureTransparentProductImage(image, productId);
  };

  const ensureTransparentProductImage = async (
    image: AppImageFile,
    productId: string,
    options?: { notifyForDecoration?: boolean }
  ): Promise<AppImageFile> => {
    if (await hasTransparentAlphaChannel(image)) {
      return image;
    }

    if (options?.notifyForDecoration && !isQueueProcessing) {
      toast.info(
        t("studio.decorationTransparencyAutoRemove", {
          defaultValue:
            "Decoration needs a transparent PNG cutout. We'll remove the background first and then continue automatically.",
        }),
        { id: "decoration-auto-cutout" }
      );
    }

    setStudioProducts((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, isProcessing: true } : p))
    );

    try {
      const cutoutResult = await executeBackgroundRemoval(
        image,
        undefined,
        projectId,
        productId
      );

      const cutoutAppImage = toAppImageFile(cutoutResult, productId);

      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? {
                ...p,
                image: cutoutAppImage,
                isProcessing: false,
              }
            : p
        )
      );

      updateStudioProductCurrentImage(
        projectId,
        productId,
        cutoutAppImage
      ).catch((e) =>
        console.error("Failed to persist transparent product image", e)
      );

      return cutoutAppImage;
    } catch (error) {
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === productId ? { ...p, isProcessing: false } : p
        )
      );
      throw error;
    }
  };

  const getLegacyWorkflowInpaintScale = (
    preset: WorkflowInpaintPlacementPreset | undefined
  ) => {
    switch (preset) {
      case "small":
        return 0.3;
      case "large":
        return 0.8;
      case "xlarge":
        return 1.2;
      case "fit":
        return 0.9;
      case "medium":
      default:
        return 0.55;
    }
  };

  const getLegacyWorkflowInpaintCenter = (
    alignment: "left" | "center" | "right" | "top" | "middle" | "bottom"
  ) => {
    if (alignment === "left" || alignment === "top") return 0.28;
    if (alignment === "right" || alignment === "bottom") return 0.72;
    return 0.5;
  };

  const getWorkflowInpaintMaxScale = (
    outputWidth: number,
    outputHeight: number,
    imageAspectRatio: number
  ) => {
    const minDim = Math.min(outputWidth, outputHeight);
    const unitWidth =
      imageAspectRatio >= 1 ? minDim : minDim * imageAspectRatio;
    const unitHeight =
      imageAspectRatio >= 1 ? minDim / imageAspectRatio : minDim;

    return Math.min(outputWidth / unitWidth, outputHeight / unitHeight);
  };

  const resolveWorkflowInpaintPlacement = (
    node: WorkflowNode
  ): WorkflowInpaintPlacement => {
    if (node.config.inpaintPlacement) {
      return {
        x: Number.isFinite(node.config.inpaintPlacement.x)
          ? node.config.inpaintPlacement.x
          : 0.5,
        y: Number.isFinite(node.config.inpaintPlacement.y)
          ? node.config.inpaintPlacement.y
          : 0.5,
        scale: Math.min(
          1.4,
          Math.max(0.15, node.config.inpaintPlacement.scale)
        ),
      };
    }

    return {
      x: getLegacyWorkflowInpaintCenter(
        node.config.productHorizontalPosition || "center"
      ),
      y: getLegacyWorkflowInpaintCenter(
        node.config.productVerticalPosition || "middle"
      ),
      scale: getLegacyWorkflowInpaintScale(node.config.inpaintPlacementPreset),
    };
  };

  const buildWorkflowInpaintAssets = async (
    image: AppImageFile,
    aspectRatio: string,
    resolution: "1K" | "2K" | "4K",
    placement: WorkflowInpaintPlacement,
    productId: string
  ) => {
    const normalizedImage = await normalizeWorkflowInpaintSourceImage(
      image,
      aspectRatio,
      productId
    );
    const sourceImage = await loadWorkflowImage(normalizedImage);
    const { width: outputWidth, height: outputHeight } =
      resolveWorkflowCanvasDimensions(aspectRatio, resolution);
    const imageAspectRatio =
      sourceImage.naturalWidth / sourceImage.naturalHeight;
    const minDim = Math.min(outputWidth, outputHeight);
    const placementScale = Math.min(
      getWorkflowInpaintMaxScale(outputWidth, outputHeight, imageAspectRatio),
      Math.max(0.15, placement.scale)
    );
    const appliedPlacement = {
      ...placement,
      scale: placementScale,
    };

    let productWidth: number;
    let productHeight: number;
    if (imageAspectRatio >= 1) {
      productWidth = placementScale * minDim;
      productHeight = productWidth / imageAspectRatio;
    } else {
      productHeight = placementScale * minDim;
      productWidth = productHeight * imageAspectRatio;
    }

    const centerX = placement.x * outputWidth;
    const centerY = placement.y * outputHeight;
    const left = centerX - productWidth / 2;
    const top = centerY - productHeight / 2;

    const baseCanvas = document.createElement("canvas");
    baseCanvas.width = outputWidth;
    baseCanvas.height = outputHeight;
    const baseCtx = baseCanvas.getContext("2d");
    if (!baseCtx) {
      throw new Error("Failed to create workflow inpaint base canvas.");
    }
    baseCtx.drawImage(sourceImage, left, top, productWidth, productHeight);

    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = outputWidth;
    maskCanvas.height = outputHeight;
    const maskCtx = maskCanvas.getContext("2d");
    if (!maskCtx) {
      throw new Error("Failed to create workflow inpaint mask canvas.");
    }
    maskCtx.fillStyle = "#FFFFFF";
    maskCtx.fillRect(0, 0, outputWidth, outputHeight);
    maskCtx.globalCompositeOperation = "destination-out";
    maskCtx.drawImage(sourceImage, left, top, productWidth, productHeight);
    maskCtx.globalCompositeOperation = "source-over";

    const maskData = maskCtx.getImageData(0, 0, outputWidth, outputHeight);
    const pixels = maskData.data;
    for (let i = 0; i < pixels.length; i += 4) {
      const alpha = pixels[i + 3];
      const value = alpha < 128 ? 0 : 255;
      pixels[i] = value;
      pixels[i + 1] = value;
      pixels[i + 2] = value;
      pixels[i + 3] = 255;
    }
    maskCtx.putImageData(maskData, 0, 0);

    const userId = auth.currentUser?.uid;
    if (!userId) {
      throw new AppError(
        "auth/not_authenticated",
        "User must be authenticated to run workflow inpaint."
      );
    }
    const stamp = Date.now();
    const [baseBlob, maskBlob] = await Promise.all([
      new Promise<Blob>((resolve, reject) =>
        baseCanvas.toBlob(
          (b) =>
            b
              ? resolve(b)
              : reject(
                  new Error("Failed to encode workflow inpaint base canvas.")
                ),
          "image/png"
        )
      ),
      new Promise<Blob>((resolve, reject) =>
        maskCanvas.toBlob(
          (b) =>
            b
              ? resolve(b)
              : reject(
                  new Error("Failed to encode workflow inpaint mask canvas.")
                ),
          "image/png"
        )
      ),
    ]);
    const [baseImage, maskImage] = await Promise.all([
      uploadAndResolve(
        baseBlob,
        `f/${userId}/${projectId}/${productId}/workflow_inpaint/base_${stamp}.png`,
        "image/png",
        "workflow_inpaint_base.png"
      ),
      uploadAndResolve(
        maskBlob,
        `f/${userId}/${projectId}/${productId}/workflow_inpaint/mask_${stamp}.png`,
        "image/png",
        "workflow_inpaint_mask.png"
      ),
    ]);

    return {
      baseImage: {
        ...baseImage,
        width: outputWidth,
        height: outputHeight,
      },
      maskImage: {
        ...maskImage,
        width: outputWidth,
        height: outputHeight,
      },
      appliedPlacement,
    };
  };

  const executeWorkflow = async (
    workflow: Workflow,
    product: StudioProduct,
    settings: GenerationSettings,
    size: "1K" | "2K" | "4K",
    /**
     * Names one node's output. Called immediately before that node's
     * generation, because the backend writes each node's DB row from the
     * context set there — a rename after the run never reaches it, which is
     * how workflow outputs came to reload as "Generated Image" (SIS-517).
     * `details` is the node's own label, the segment the naming template
     * renders after the workflow type.
     */
    nameResult: (details: string) => string
  ) => {
    assertWorkflowPreflight(workflow, product.image);
    const missingStyleNodeIds = getMilieuFixedNodeIdsMissingStyle(workflow);
    if (missingStyleNodeIds.length > 0) {
      throw new Error(
        getWorkflowMilieuFixedStyleRequiredMessage(workflow.name)
      );
    }

    const startNode = workflow.nodes.find((n) => n.type === "trigger");
    if (!startNode) throw new Error("Invalid workflow: No start node.");

    const loadedImage = product.image;

    const results = new Map<string, AppImageFile[]>();
    results.set(startNode.id, [loadedImage]);

    const finalOutputs: GeneratedContent[] = [];
    const processed = new Set<string>();
    const nodeQueue = [startNode];

    const processNode = async (
      node: WorkflowNode,
      inputImage: AppImageFile
    ): Promise<GeneratedContent[]> => {
      const now = Date.now();
      const aspectRatio =
        node.config.aspectRatio || settings.aspectRatio || "1:1";
      const resolution = normalizeResolution(
        (node.type === "inpaint"
          ? settings.resolution || size
          : node.config.resolution || settings.resolution || size) as
          "1K" | "2K" | "4K" | undefined
      );
      const nodeImageGeneration =
        node.config.imageGeneration ||
        ((node.type === "rotate" &&
          node.config.rotationModel === "gpt-image-2") ||
        (node.type === "recolor" && node.config.recolorModel === "gpt-image-2")
          ? {
              ...workflowImageGeneration(node),
              resolution:
                node.type === "recolor"
                  ? node.config.recolorResolution || "1K"
                  : resolution,
            }
          : undefined);
      const model = "Nano Banana Pro";

      // Persist the node's mode + settings on the backend record. Backend
      // generation (`callGenerateImage`) reads the image type and serialized
      // settings from the generation context, so without this the persisted
      // record stores a null imageType and the image details panel shows "—"
      // for the mode after reload. Set it right before each generation call.
      //
      // Returns the name the output will carry: the same context is the only
      // thing the backend writes the row from, so the node has to be named
      // here and not when its result comes back (SIS-517).
      const applyNodeGenerationContext = (
        genSettings: Record<string, unknown>,
        details: string
      ): string => {
        const title = nameResult(details);
        setGenerationContext(
          projectId,
          product.id,
          (genSettings.imageType as string | undefined) ?? undefined,
          serializeImageGenerationSettings(node.config.imageGeneration, {
            ...genSettings,
            title,
          })
        );
        return title;
      };

      switch (node.type) {
        case "trigger":
          return [];
        case "remove_bg": {
          // Pass the mode + settings to the Cloud Function so it persists them
          // on the backed record (it stores `imageType ?? null` otherwise), and
          // mark the result dbPersisted so the save loop skips re-persisting.
          const cfg = resolveBackgroundRemovalConfig(node.config);
          // `remove_bg` is a retired node type (no longer creatable; superseded
          // by `cutout`). Its persisted config never carries `backgroundRemoval`
          // or `cutoutShadows`, so the resolved mode is always "simple". Guard
          // defensively: if a hand-edited or migrated workflow ever supplies a
          // complex config, route it through the `cutout` handler (which honors
          // shadows + resolution) instead of silently downgrading to the simple
          // remover.
          if (cfg.mode === "complex") {
            return processNode({ ...node, type: "cutout" }, inputImage);
          }
          const title = nameResult("Background Removed");
          const removeBgSettings = {
            imageType: "Remove BG",
            title,
            model,
            backgroundRemoval: cfg,
          };
          const noBg = await executeBackgroundRemoval(
            inputImage,
            undefined,
            projectId,
            product.id,
            "Remove BG",
            serializeGenerationSettings(removeBgSettings),
            { fillBackgroundWhite: cfg.fill === "white" }
          );
          return [
            {
              id: noBg.dbId ?? crypto.randomUUID(),
              image: noBg.downloadUrl,
              storagePath: noBg.storagePath,
              thumbnailUrl: noBg.downloadUrl,
              text: "Background Removed",
              title,
              video: null,
              mimeType: noBg.mimeType,
              dimensions: { width: noBg.width, height: noBg.height },
              generationSettings: removeBgSettings,
              dbPersisted: !!noBg.dbId,
              timestamp: now,
            },
          ];
        }
        case "recolor": {
          const colors = node.config.targetColors || [];
          if (colors.length === 0) return [];
          // Recolor each swatch sequentially. Each color generates a separate
          // backend record, and the generation context (which carries the
          // per-swatch value / material / reference) is a shared mutable
          // singleton — recoloring concurrently would race it, so every variant
          // would inherit one swatch's settings and the persisted records would
          // all show the same color after reload. Mirrors the studio recolor
          // path, which sets the context per color in a sequential loop.
          // Node-level recolor options (mirror the Studio Image Type controls,
          // persisted on the node). Creativity uses node.config.temperature.
          const recolorModel = node.config.recolorModel ?? "nano-banana-pro";
          const recolorQuality = node.config.recolorQuality ?? "high";
          // Default to "1K" to match the WorkflowBuilder selector default
          // (rendered as `recolorResolution ?? "1K"`). Passing undefined would
          // let recolorImage auto-detect 2K/4K from the source, so a node left
          // at the displayed "1K" default could silently produce a larger,
          // costlier output than configured.
          const recolorResolution = node.config.recolorResolution ?? "1K";
          const recolorGrain = node.config.recolorGrainSketch;
          const recolorTemperature = node.config.temperature;
          const recolorUsageOnlyFlag = node.config.recolorUsageOnly ?? false;
          const recolorAdditionalContext =
            node.config.recolorAdditionalContext?.trim() || undefined;
          const recolorResults: PromiseSettledResult<GeneratedContent>[] = [];
          for (const colorItem of colors) {
            const colorValue =
              typeof colorItem === "string" ? colorItem : colorItem.value;
            const swatchRef =
              typeof colorItem === "string"
                ? undefined
                : colorItem.referenceImage;
            const usageRefImage =
              typeof colorItem === "string"
                ? undefined
                : colorItem.usageReferenceImage;
            const material =
              typeof colorItem === "string" ? undefined : colorItem.material;
            // "In-use photo only" applies per swatch (only when it has one);
            // then the close-up reference is dropped.
            const perUsageOnly = recolorUsageOnlyFlag && !!usageRefImage;
            const refImage = perUsageOnly ? undefined : swatchRef;
            // A grain sketch forces GPT Image 2 in the service — persist the
            // model that is actually used, not the pre-coercion choice.
            const effectiveRecolorModel = resolveEffectiveRecolorModel(
              recolorModel,
              !!recolorGrain
            );
            const recolorExtra = {
              model: imageModelDisplayName(effectiveRecolorModel),
              recolorModel: effectiveRecolorModel,
              recolorQuality,
              recolorUsageReferenceOnly: perUsageOnly,
              recolorAdditionalContext,
              resolution: recolorResolution,
            };
            try {
              const title = applyNodeGenerationContext(
                buildRecolorGenerationSettings(
                  colorValue,
                  undefined,
                  refImage,
                  usageRefImage,
                  material,
                  recolorExtra
                ),
                `Recolor ${colorValue}`
              );
              const res = await recolorImage(
                inputImage,
                colorValue,
                { category: product.productCategory },
                refImage,
                usageRefImage,
                material,
                recolorGrain ?? undefined,
                recolorModel,
                recolorQuality,
                recolorResolution,
                perUsageOnly,
                recolorTemperature,
                recolorAdditionalContext
              );
              recolorResults.push({
                status: "fulfilled",
                value: {
                  ...res,
                  title,
                  generationSettings: mergeGeneratedImageSettings(
                    res.generationSettings,
                    {
                      ...buildRecolorGenerationSettings(
                        colorValue,
                        res.generationSettings?.aspectRatio,
                        refImage,
                        usageRefImage,
                        material,
                        recolorExtra
                      ),
                      title,
                      imageGeneration: nodeImageGeneration,
                    }
                  ),
                  timestamp: now,
                },
              });
            } catch (reason) {
              recolorResults.push({ status: "rejected", reason });
            }
          }

          const successfulResults = recolorResults.flatMap((result) =>
            result.status === "fulfilled" ? [result.value] : []
          );

          if (successfulResults.length > 0) {
            const failedColors = recolorResults
              .map((result, index) => ({ result, colorItem: colors[index] }))
              .filter(
                (
                  entry
                ): entry is {
                  result: PromiseRejectedResult;
                  colorItem: (typeof colors)[number];
                } => entry.result.status === "rejected"
              )
              .map(({ colorItem }) =>
                typeof colorItem === "string"
                  ? colorItem
                  : getColorSwatchDisplayLabel(colorItem)
              );

            if (failedColors.length > 0) {
              console.warn("Workflow recolor skipped failed swatches", {
                workflowId: workflow.id,
                failedColors,
              });
            }

            return successfulResults;
          }

          const firstFailure = recolorResults.find(
            (result): result is PromiseRejectedResult =>
              result.status === "rejected"
          );
          if (firstFailure) throw firstFailure.reason;
          return [];
        }
        case "rotate": {
          const view = node.config.view || "Frontal view";
          const rotateAspectRatio =
            inputImage.width && inputImage.height
              ? getClosestAspectRatio(inputImage.width, inputImage.height)
              : aspectRatio;
          const nodeRotationModel =
            node.config.rotationModel || "nano-banana-pro";
          const nodeRotationQuality = node.config.rotationQuality || "high";
          const rotationGenSettings = {
            imageType: "Rotation",
            rotationAngle: view,
            rotationMode: node.config.rotationMode,
            rotationModel: nodeRotationModel,
            rotationQuality: nodeRotationQuality,
            aspectRatio: rotateAspectRatio,
            resolution,
            model: imageModelDisplayName(nodeRotationModel),
          };
          const title = applyNodeGenerationContext(
            rotationGenSettings,
            `Rotation ${view}`
          );
          const rotated = await generateRotationImage(
            inputImage,
            view,
            "",
            rotateAspectRatio,
            node.config.rotationMode,
            resolution,
            nodeRotationModel,
            nodeRotationQuality
          );
          return [
            {
              ...rotated,
              title,
              generationSettings: mergeGeneratedImageSettings(
                rotated.generationSettings,
                {
                  ...rotationGenSettings,
                  title,
                  imageGeneration: nodeImageGeneration,
                }
              ),
              timestamp: now,
            },
          ];
        }
        case "cutout": {
          const cfg = resolveBackgroundRemovalConfig(node.config);

          if (cfg.mode === "simple") {
            const alreadyTransparent =
              await hasTransparentAlphaChannel(inputImage);
            if (alreadyTransparent) {
              if (cfg.fill === "white") {
                // White fill on an already-transparent input: flatten onto
                // white locally instead of calling Bria (nothing to remove, and
                // it would charge a credit for a no-op). Without this the
                // white-fill request was silently dropped and the user got the
                // original transparent image back.
                const whiteImg = await buildWhiteFilledFromTransparent(
                  inputImage,
                  product.id
                );

                const whiteTitle = nameResult("Cutout (White)");
                return [
                  {
                    id: crypto.randomUUID(),
                    image: whiteImg.fullUrl || whiteImg.thumbnailUrl || "",
                    storagePath: whiteImg.storagePath,
                    thumbnailUrl: whiteImg.thumbnailUrl,
                    fullUrl: whiteImg.fullUrl,
                    text: "Background Removed (White Fill)",
                    title: whiteTitle,
                    video: null,
                    tags: ["Cutout", "No Background", "White Filled"],
                    imageType: "Cutout",
                    generationSettings: {
                      imageType: "Cutout",
                      title: whiteTitle,
                      aspectRatio,
                      resolution,
                      backgroundRemoval: cfg,
                      model: "White fill (no AI)",
                      imageGeneration: undefined,
                    },
                    timestamp: now,
                    filename: `cutout_white_${Date.now()}.jpg`,
                    mimeType: "image/jpeg",
                    dimensions:
                      typeof whiteImg.width === "number" &&
                      typeof whiteImg.height === "number"
                        ? { width: whiteImg.width, height: whiteImg.height }
                        : undefined,
                  },
                ];
              }

              const displayUrl =
                inputImage.thumbnailUrl || inputImage.fullUrl || "";

              const passthroughTitle = nameResult("Cutout (PNG)");
              return [
                {
                  id: crypto.randomUUID(),
                  image: displayUrl,
                  storagePath: inputImage.storagePath,
                  thumbnailUrl: inputImage.thumbnailUrl,
                  fullUrl: inputImage.fullUrl,
                  text: "Transparent Cutout",
                  title: passthroughTitle,
                  video: null,
                  tags: ["Cutout", "No Background", "Transparent"],
                  imageType: "Cutout",
                  generationSettings: {
                    imageType: "Cutout",
                    title: passthroughTitle,
                    aspectRatio,
                    resolution,
                    model: "Existing transparent image",
                    imageGeneration: nodeImageGeneration,
                  },
                  timestamp: now,
                  filename: inputImage.name,
                  mimeType: inputImage.mimeType,
                  dimensions:
                    typeof inputImage.width === "number" &&
                    typeof inputImage.height === "number"
                      ? {
                          width: inputImage.width,
                          height: inputImage.height,
                        }
                      : undefined,
                  sizeBytes: inputImage.sizeBytes,
                },
              ];
            }

            const isWhiteFill = cfg.fill === "white";
            const title = nameResult("Cutout (PNG)");
            const cutoutPngSettings = {
              imageType: "Cutout",
              title,
              aspectRatio,
              resolution,
              backgroundRemoval: cfg,
              model: "showcase AI",
            };
            // Persist the mode + settings on the backend record (the Cloud
            // Function stores `imageType ?? null` otherwise) and mark the
            // result dbPersisted so the save loop skips re-persisting.
            const noBg = await executeBackgroundRemoval(
              inputImage,
              undefined,
              projectId,
              product.id,
              "Cutout",
              serializeGenerationSettings({
                imageType: "Cutout",
                title,
                aspectRatio,
                resolution,
                backgroundRemoval: cfg,
              }),
              { fillBackgroundWhite: isWhiteFill }
            );

            return [
              {
                id: noBg.dbId ?? crypto.randomUUID(),
                image: noBg.downloadUrl,
                storagePath: noBg.storagePath,
                thumbnailUrl: noBg.downloadUrl,
                text: isWhiteFill
                  ? "Background Removed (White Fill)"
                  : "Background Removed",
                title,
                video: null,
                tags: isWhiteFill
                  ? ["Cutout", "No Background", "White Filled"]
                  : ["Cutout", "No Background", "Transparent"],
                imageType: "Cutout",
                generationSettings: cutoutPngSettings,
                dbPersisted: !!noBg.dbId,
                timestamp: now,
                filename: `cutout_${Date.now()}.png`,
                mimeType: noBg.mimeType,
                dimensions: { width: noBg.width, height: noBg.height },
              },
            ];
          } else {
            // Use Nano Banana Pro for JPEG with shadows
            const workflowVibeId = node.config.vibeId || settings.vibe;
            const workflowVibeLabel = workflowVibeId
              ? vibes.find((v) => v.id === workflowVibeId)?.label
              : undefined;
            const cutoutResolution = cfg.resolution ?? "1K";
            const cutoutGenSettings = {
              imageType: "Cutout",
              aspectRatio,
              resolution: cutoutResolution,
              model,
            };
            const cutoutTitle = applyNodeGenerationContext(
              cutoutGenSettings,
              "Cutout"
            );
            const cutout = await generateCutoutImage(
              inputImage,
              aspectRatio,
              node.config.prompt,
              cfg.shadows,
              workflowVibeLabel,
              inputImage.name.split(".")[0], // productName
              "original", // productColor
              undefined,
              cutoutResolution
            );
            return [
              {
                ...cutout,
                title: cutoutTitle,
                generationSettings: mergeGeneratedImageSettings(
                  cutout.generationSettings,
                  {
                    ...cutoutGenSettings,
                    title: cutoutTitle,
                    imageGeneration: nodeImageGeneration,
                  }
                ),
                timestamp: now,
              },
            ];
          }
        }
        case "scaling": {
          // Client-side 2D drawing — the same pipeline as the Dimensions
          // quick-action editor, so workflow results are identical and carry
          // a re-editable annotation in generationSettings.dimensionsData.
          const scalingResult = await generateDimensional2DDrawing(inputImage, {
            // Resolved at run time like the Studio: an unset id follows the
            // pinned default, a deleted one falls back to the node settings.
            preset: resolveDimensionPreset(
              dimensionDrawingSettings,
              node.config.dimensionPresetId
            ),
            projectId,
            productId: product.id,
            productWidth: product.width || undefined,
            productHeight: product.height || undefined,
            markerPreference: node.config.dimensionMarker || dimensionMarker,
            // "arrow", not studio state: new nodes always carry an explicit
            // value, so this only hits workflows saved before cap styles
            // existed — which must keep rendering the arrows their config
            // UI displays, regardless of the current panel selection.
            capStyle: node.config.dimensionCapStyle || "arrow",
            showLabel: node.config.dimensionShowLabel ?? true,
            // Same back-compat rule: workflows saved before SIS-530 keep the
            // source image's own ratio.
            targetAspectRatio: node.config.dimensionTargetFormat || "original",
            companyLogo: node.config.companyLogo || companyLogo,
            logoPosition: node.config.logoPosition || logoPosition,
            logoScale: node.config.logoScale ?? logoScale,
          });
          const scalingTitle = nameResult("Dimensional Drawing");
          return [
            {
              ...scalingResult,
              title: scalingTitle,
              generationSettings: {
                ...scalingResult.generationSettings,
                title: scalingTitle,
                imageGeneration: nodeImageGeneration,
              },
              timestamp: now,
            },
          ];
        }
        case "aspect_ratio": {
          const aspectRatioGenSettings = {
            imageType: "Aspect Ratio",
            aspectRatio,
            resolution,
            model,
          };
          const aspectRatioTitle = applyNodeGenerationContext(
            aspectRatioGenSettings,
            `Aspect Ratio ${aspectRatio}`
          );
          const aspectRatioRes = await generateAspectRatioConversion(
            inputImage,
            aspectRatio,
            resolution
          );
          return [
            {
              ...aspectRatioRes,
              title: aspectRatioTitle,
              generationSettings: mergeGeneratedImageSettings(
                aspectRatioRes.generationSettings,
                {
                  ...aspectRatioGenSettings,
                  title: aspectRatioTitle,
                  imageGeneration: nodeImageGeneration,
                }
              ),
              timestamp: now,
            },
          ];
        }
        case "upscale": {
          const factor = node.config.upscaleFactor || "x2";
          // Workflows saved before the fal migration carry no upscaler, so
          // they keep running on the standard (1 credit) tier.
          const nodeUpscaler = node.config.upscaler || "seedvr";
          const upscaleTitle = nameResult(`Upscale ${factor}`);
          const upscaleSettings = {
            imageType: "Upscale",
            title: upscaleTitle,
            upscaleFactor: factor,
            upscaler: nodeUpscaler,
          };
          // Pass the mode + settings to the Cloud Function so it persists them
          // on the backend record (it stores `imageType ?? null` otherwise) and
          // mark the result dbPersisted so the save loop skips re-persisting.
          const upscaled = await executeUpscale(
            inputImage,
            factor,
            nodeUpscaler,
            undefined,
            projectId,
            product.id,
            "Upscale",
            serializeGenerationSettings(upscaleSettings)
          );

          return [
            {
              id: upscaled.dbId ?? crypto.randomUUID(),
              image: upscaled.downloadUrl,
              storagePath: upscaled.storagePath,
              thumbnailUrl: upscaled.downloadUrl,
              text: `Upscaled ${factor}`,
              title: upscaleTitle,
              video: null,
              tags: ["Upscaled", factor],
              imageType: "Upscale",
              generationSettings: upscaleSettings,
              dbPersisted: !!upscaled.dbId,
              timestamp: now,
              filename: upscaled.name,
              mimeType: upscaled.mimeType,
              dimensions: { width: upscaled.width, height: upscaled.height },
            },
          ];
        }
        case "staging": {
          const vibeId = node.config.vibeId || settings.vibe;
          const sceneId = node.config.sceneId || settings.scene;
          const { productLightsOn, productLightsWarmth } =
            resolveProductLightsSettings(node.config, settings);
          const vibe = vibes.find((v) => v.id === vibeId);
          const scene = scenes.find((s) => s.id === sceneId);
          const bgConfig = {
            vibe: vibe?.description || "Studio",
            context: scene?.context || "",
          };

          let placementStr = "";
          if (
            node.config.productHorizontalPosition &&
            node.config.productVerticalPosition
          ) {
            placementStr += `Positioning: Place the product towards the ${node.config.productHorizontalPosition} and ${node.config.productVerticalPosition} of the frame. ADJUST PLACEMENT: If Image 1 has the product centered, you MUST shift it to the ${node.config.productHorizontalPosition} and ${node.config.productVerticalPosition} if requested here.`;
          }

          if (node.config.prompt) {
            bgConfig.context += ` ${node.config.prompt}`;
          }
          if (placementStr) {
            bgConfig.context += ` ${placementStr}`;
          }

          const effectiveStyleConfig =
            node.config.styleConfig || settings.styleConfig;

          const stagingGenSettings = {
            imageType: MILIEU_CREATIVE_LABEL,
            vibe: vibe?.label || vibeId,
            scene: scene?.label || sceneId,
            // Workflow results regenerate through the same viewer path as
            // studio results, so they need the ids too (SIS-502) — without
            // them a post-reload regeneration falls back to the panel.
            ...environmentIdSettings({ vibe: vibeId, scene: sceneId }),
            aspectRatio,
            resolution,
            model,
            workflowName: workflow.name,
            context: node.config.prompt || undefined,
            productLightsOn,
            productLightsWarmth: productLightsOn
              ? productLightsWarmth
              : undefined,
          };
          const stagingTitle = applyNodeGenerationContext(
            stagingGenSettings,
            `${vibe?.label || MILIEU_CREATIVE_LABEL} ${scene?.label || ""}`.trim()
          );
          const staging = await geminiService.generateGuidedStagedImage(
            [inputImage],
            bgConfig,
            undefined,
            parseProductDimensionCm(product.width, 0),
            parseProductDimensionCm(product.height, 0),
            resolution,
            aspectRatio,
            false,
            node.config.avatarImage,
            effectiveStyleConfig,
            undefined,
            undefined,
            undefined,
            undefined, // productDetails
            productLightsOn,
            productLightsWarmth,
            {
              category: product.productCategory,
              description: product.productDescription,
            }
          );
          return [
            {
              ...staging,
              imageType: MILIEU_CREATIVE_LABEL,
              title: stagingTitle,
              generationSettings: mergeGeneratedImageSettings(
                staging.generationSettings,
                {
                  ...stagingGenSettings,
                  title: stagingTitle,
                  imageGeneration: nodeImageGeneration,
                }
              ),
              timestamp: now,
            },
          ];
        }
        case "inpaint": {
          const requestedPlacement = resolveWorkflowInpaintPlacement(node);
          const workflowInpaintAssets = await buildWorkflowInpaintAssets(
            inputImage,
            aspectRatio,
            resolution,
            requestedPlacement,
            product.id
          );
          const placement = workflowInpaintAssets.appliedPlacement;
          const selectedLifestylePresetId =
            node.config.lifestylePresetId?.trim();
          const relightScene = !!node.config.relightScene;
          const includeSceneDecoration =
            node.config.includeSceneDecoration ?? true;

          if (selectedLifestylePresetId) {
            // resolvedLabel/resolvedDescription are only known after the call,
            // so the context carries the settings available up front and the
            // in-memory result below keeps the resolved prompt for display.
            // The name has the same limit (it must exist before the context is
            // set, SIS-517) but the preset's *stored* label is available now —
            // only the AI-refreshed label of a custom preset is post-call, and
            // that falls back to the stored label anyway.
            const workflowPresetLabel = lifestylePresets
              .find((entry) => entry.id === selectedLifestylePresetId)
              ?.label.trim();
            const presetTitle = applyNodeGenerationContext(
              {
                imageType: MILIEU_FIXED_LABEL,
                aspectRatio,
                resolution,
                model:
                  `bria-product-shot + ${DEFAULT_INPAINT_STYLE_MODEL}` +
                  ` (temp ${INPAINT_STYLE_TEMPERATURE})`,
                workflowName: workflow.name,
                lifestylePresetId: selectedLifestylePresetId,
                relightScene,
                includeSceneDecoration,
                inpaintPlacement: placement,
                inpaintPlacementPreset:
                  node.config.inpaintPlacementPreset || "medium",
                productHorizontalPosition:
                  node.config.productHorizontalPosition,
                productVerticalPosition: node.config.productVerticalPosition,
              },
              workflowPresetLabel
                ? `${MILIEU_FIXED_LABEL} ${workflowPresetLabel}`
                : MILIEU_FIXED_LABEL
            );
            const { styledResult, resolvedLabel, resolvedDescription } =
              await generateLifestyleSceneVariant(
                workflowInpaintAssets.baseImage,
                selectedLifestylePresetId,
                aspectRatio,
                relightScene,
                includeSceneDecoration
              );

            return [
              {
                ...styledResult,
                imageType: MILIEU_FIXED_LABEL,
                title: presetTitle,
                generationSettings: mergeGeneratedImageSettings(
                  styledResult.generationSettings,
                  {
                    ...styledResult.generationSettings,
                    imageType: MILIEU_FIXED_LABEL,
                    title: presetTitle,
                    aspectRatio,
                    resolution,
                    model: styledResult.generationSettings?.model,
                    prompt: `${resolvedLabel}: ${resolvedDescription}`,
                    workflowName: workflow.name,
                    lifestylePresetId: selectedLifestylePresetId,
                    lifestylePresetLabel: resolvedLabel,
                    relightScene,
                    includeSceneDecoration,
                    inpaintPlacement: placement,
                    inpaintPlacementPreset:
                      node.config.inpaintPlacementPreset || "medium",
                    productHorizontalPosition:
                      node.config.productHorizontalPosition,
                    productVerticalPosition:
                      node.config.productVerticalPosition,
                    imageGeneration: nodeImageGeneration,
                  }
                ),
                timestamp: now,
              },
            ];
          }

          const scene = scenes.find((s) => s.id === node.config.sceneId);
          const promptParts = [
            scene?.context?.trim(),
            node.config.prompt?.trim(),
          ].filter(Boolean);
          const inpaintStyleConfig =
            node.config.styleConfig || settings.styleConfig;
          let inpaintPrompt =
            promptParts.join("\n\n") ||
            "Create a photorealistic background around the preserved product.";
          inpaintPrompt = applyInpaintPromptRequirements(
            inpaintPrompt,
            inpaintStyleConfig
          );
          const productContext = [
            product.productCategory,
            product.productDescription,
          ]
            .filter(Boolean)
            .join(". ");
          if (productContext) {
            inpaintPrompt += `\n\nProduct Context (use this to match the environment to the product and do not modify the product itself): ${productContext}`;
          }

          const inpaintGenSettings = {
            imageType: "Inpaint",
            scene: scene?.label || node.config.sceneId || "",
            aspectRatio,
            resolution,
            model: "gemini-3-pro-image",
            // Only the user-authored parts. `inpaintPrompt` is the assembled
            // request — Critical Requirements block, product-context line,
            // fallback sentence — and the details panel prints `context` back
            // as the user's own words (SIS-516).
            context: promptParts.join("\n\n"),
            inpaintPlacement: placement,
            inpaintPlacementPreset:
              node.config.inpaintPlacementPreset || "medium",
            productHorizontalPosition: node.config.productHorizontalPosition,
            productVerticalPosition: node.config.productVerticalPosition,
          };
          const inpaintTitle = applyNodeGenerationContext(
            inpaintGenSettings,
            scene?.label ? `Inpaint ${scene.label}` : "Inpaint Scene"
          );
          const inpaintResult = await geminiService.editImageWithAnnotation(
            workflowInpaintAssets.baseImage,
            workflowInpaintAssets.maskImage,
            inpaintPrompt,
            null,
            aspectRatio,
            resolution
          );
          return [
            {
              ...inpaintResult,
              title: inpaintTitle,
              generationSettings: mergeGeneratedImageSettings(
                inpaintResult.generationSettings,
                {
                  ...inpaintGenSettings,
                  title: inpaintTitle,
                  imageGeneration: nodeImageGeneration,
                }
              ),
              timestamp: now,
            },
          ];
        }
        case "highlights": {
          const config = highlightsConfigs.find(
            (c) => c.id === node.config.highlightsConfigId
          );
          if (!config || !config.referenceImage || !config.template) return [];

          const hvibe = vibes.find(
            (v) => v.id === (node.config.vibeId || config.vibeId)
          );
          const hscene = scenes.find(
            (s) => s.id === (node.config.sceneId || config.sceneId)
          );

          const highlightsGenSettings = {
            imageType: "Highlights",
            highlightsConfigId: config.id,
            highlightsInputs: node.config.highlightsInputs || {},
            vibe: hvibe?.label || node.config.vibeId || config.vibeId || "",
            scene: hscene?.label || node.config.sceneId || config.sceneId || "",
            // See the staging node above — regeneration needs the ids.
            ...environmentIdSettings({
              vibe: node.config.vibeId || config.vibeId || "",
              scene: node.config.sceneId || config.sceneId || "",
            }),
            aspectRatio,
            resolution,
            model,
          };
          const highlightsTitle = applyNodeGenerationContext(
            highlightsGenSettings,
            "Highlights"
          );
          const hRes = await geminiService.generateHighlightsImage(
            inputImage,
            config.referenceImage,
            config.template,
            node.config.highlightsInputs || {},
            aspectRatio,
            hvibe?.description,
            hscene?.context,
            resolution
          );
          return [
            {
              ...hRes,
              title: highlightsTitle,
              generationSettings: mergeGeneratedImageSettings(
                hRes.generationSettings,
                {
                  ...highlightsGenSettings,
                  title: highlightsTitle,
                  imageGeneration: nodeImageGeneration,
                }
              ),
              timestamp: now,
            },
          ];
        }
        case "transform_technical_drawing": {
          const tdMode = node.config.technicalDrawingMode || "milieu";
          const tdShadowStyle =
            node.config.technicalDrawingShadowStyle || "lightbox";
          const tdVibe = vibes.find((v) => v.id === node.config.vibeId);
          const tdScene = scenes.find((s) => s.id === node.config.sceneId);
          const tdGenSettings = {
            imageType: "Transform Technical Drawing",
            aspectRatio,
            resolution,
            model,
            technicalDrawingMode: tdMode,
            technicalDrawingMaterial: node.config.technicalDrawingMaterial,
            technicalDrawingShadowStyle:
              tdMode === "cutout" ? tdShadowStyle : undefined,
            // Only the milieu mode uses an environment — mirror the studio
            // path and persist label + id so regeneration can restore it.
            ...(tdMode === "milieu"
              ? {
                  vibe: tdVibe?.label || node.config.vibeId || "",
                  scene: tdScene?.label || node.config.sceneId || "",
                  ...environmentIdSettings({
                    vibe: node.config.vibeId,
                    scene: node.config.sceneId,
                  }),
                }
              : {}),
          };
          const tdTitle = applyNodeGenerationContext(
            tdGenSettings,
            "Transform Technical Drawing"
          );
          const tdRes =
            await geminiService.generateTransformTechnicalDrawingImage(
              inputImage,
              tdMode,
              aspectRatio,
              resolution,
              tdMode === "milieu" ? tdVibe?.description || "" : undefined,
              tdMode === "milieu" ? tdScene?.context || "" : undefined,
              node.config.technicalDrawingMaterialImage || null,
              node.config.technicalDrawingMaterial || undefined,
              tdMode === "cutout" ? tdShadowStyle : "lightbox"
            );
          return [
            {
              ...tdRes,
              title: tdTitle,
              generationSettings: mergeGeneratedImageSettings(
                tdRes.generationSettings,
                {
                  ...tdGenSettings,
                  title: tdTitle,
                  imageGeneration: nodeImageGeneration,
                }
              ),
              timestamp: now,
            },
          ];
        }
        case "detail": {
          const dvibe = vibes.find((v) => v.id === node.config.vibeId);
          const dscene = scenes.find((s) => s.id === node.config.sceneId);
          const detailGenSettings = {
            imageType: "Details",
            detailsFocus: node.config.detailFocus,
            detailsMode: node.config.detailMode,
            detailsCameraPerspective: node.config.detailCameraPerspective,
            detailsMaterial: node.config.detailMaterial,
            detailsSceneDescription: node.config.detailSceneDescription,
            vibe: dvibe?.label || node.config.vibeId || "",
            scene: dscene?.label || node.config.sceneId || "",
            // See the staging node above — regeneration needs the ids.
            ...environmentIdSettings({
              vibe: node.config.vibeId,
              scene: node.config.sceneId,
            }),
            aspectRatio,
            resolution,
            model,
          };
          const detailTitle = applyNodeGenerationContext(
            detailGenSettings,
            "Details"
          );
          const detailRes = await generateDetailsImage(
            inputImage,
            node.config.detailFocus || "Texture",
            node.config.detailMode || "milieu",
            aspectRatio,
            dvibe?.description,
            dscene?.context,
            node.config.detailCameraPerspective,
            node.config.detailMaterial,
            node.config.detailSceneDescription,
            node.config.detailMaterialReferenceImage ?? undefined,
            dvibe?.label,
            resolution
          );
          return [
            {
              ...detailRes,
              title: detailTitle,
              generationSettings: mergeGeneratedImageSettings(
                detailRes.generationSettings,
                {
                  ...detailGenSettings,
                  title: detailTitle,
                  imageGeneration: nodeImageGeneration,
                }
              ),
              timestamp: now,
            },
          ];
        }
        case "free_prompt": {
          const prompt = (node.config.prompt || "").trim();
          if (!prompt) return [];
          const refs = node.config.referenceImages || [];
          const freePromptGenSettings = {
            imageType: "Free Prompt",
            aspectRatio,
            resolution,
            model,
            workflowName: workflow.name,
          };
          const freePromptTitle = applyNodeGenerationContext(
            freePromptGenSettings,
            "Free Prompt"
          );
          const freeRes = await generateFreePromptImage(
            inputImage,
            prompt,
            refs,
            aspectRatio,
            node.config.temperature,
            resolution
          );
          return [
            {
              ...freeRes,
              title: freePromptTitle,
              generationSettings: mergeGeneratedImageSettings(
                freeRes.generationSettings,
                {
                  ...freePromptGenSettings,
                  title: freePromptTitle,
                  imageGeneration: nodeImageGeneration,
                }
              ),
              timestamp: now,
            },
          ];
        }
        default:
          return [];
      }
    };

    while (nodeQueue.length > 0) {
      const currentNode = nodeQueue.shift()!;
      if (processed.has(currentNode.id)) continue;

      let inputImages: AppImageFile[] = [];
      if (currentNode.type === "trigger") {
        inputImages = [loadedImage];
      } else {
        const incomingEdge = workflow.edges.find(
          (e) => e.target === currentNode.id
        );
        if (incomingEdge) {
          const parentResults = results.get(incomingEdge.source);
          if (parentResults) inputImages = parentResults;
        }
      }

      if (inputImages.length > 0) {
        let nodeResults: GeneratedContent[] = [];

        if (currentNode.type !== "trigger") {
          for (const img of inputImages) {
            const batch = await processNode(currentNode, img);
            nodeResults.push(...batch);
          }
        }

        if (nodeResults.length > 0) {
          // Save results from every non-trigger node, not just terminal ones
          finalOutputs.push(...nodeResults);

          const outputFiles: AppImageFile[] = nodeResults
            .filter((r) => r.image)
            .map((r, idx) => ({
              id: crypto.randomUUID(),
              base64: "",
              mimeType: r.mimeType || "image/png",
              name: r.filename || `node_${currentNode.id}_${idx}.png`,
              storagePath: r.storagePath,
              thumbnailUrl: r.thumbnailUrl || r.image!,
              fullUrl: r.fullUrl || r.thumbnailUrl || r.image!,
              width: r.dimensions?.width,
              height: r.dimensions?.height,
            }));

          results.set(currentNode.id, outputFiles);
        } else if (currentNode.type === "trigger") {
          results.set(currentNode.id, inputImages);
        }

        processed.add(currentNode.id);

        const outgoingEdges = workflow.edges.filter(
          (e) => e.source === currentNode.id
        );
        outgoingEdges.forEach((e) => {
          const targetNode = workflow.nodes.find((n) => n.id === e.target);
          if (targetNode) nodeQueue.push(targetNode);
        });
      }
    }
    return finalOutputs;
  };

  const handleGenerateForSinglePlant = async (
    id: string,
    size: "1K" | "2K" | "4K" = "1K",
    settingsOverride?: GenerationSettings,
    imageGenerationOverride?: ImageGenerationSettings,
    workflowOverride?: Workflow
  ): Promise<{ ok: true } | { ok: false; reason: "credits" | "error" }> => {
    const product = studioProducts.find((p) => p.id === id);
    if (!product) return { ok: false, reason: "error" };

    // The backend resolves the product image from GCS by storagePath. A product
    // added to the studio before its upload finished has only base64 in memory
    // (SIS-395); block generation with a clear message instead of an opaque
    // failed Cloud Function call.
    if (!hasStoragePath(product.image)) {
      toast.error(t("studio.errorProductImageNotReady"));
      return { ok: false, reason: "error" as const };
    }

    // Set product context for backend GCS path
    setGenerationContext(projectId, id);

    const baseSettings: GenerationSettings = settingsOverride || {
      imageGeneration: imageGenerationOverride || {
        ...studioImageGeneration,
        resolution: size,
        aspectRatio: studioImageGeneration.aspectRatio,
      },
      vibe: studioVibe,
      scene: studioScene,
      imageType: studioImageType,
      aspectRatio: studioAspectRatio,
      vibeContext: hidesCustomContext(studioImageType, {
        detailsMode: studioDetailsMode,
        technicalDrawingMode: studioTechnicalDrawingMode,
      })
        ? ""
        : studioVibeContext,
      useTrueToneStudio: useTrueToneStudio,
      productLightsOn,
      productLightsWarmth,
      decorationSurfaceOverride: studioDecorationSurfaceOverride,
      decorationFramingPreference: studioDecorationFramingPreference,
      decorationRoomPlacementPreference:
        studioDecorationRoomPlacementPreference,
      decorationViewAngle: studioDecorationViewAngle,
      profileId: studioSelectedProfileId,
      colorSwatches: colorSwatches,
      recolorAdditionalContext: studioRecolorOptions.additionalContext,
      rotationAngle: studioRotationAngle,
      rotationMode: studioRotationMode,
      rotationModel: studioRotationModel,
      rotationQuality: studioRotationQuality,
      useAvatar: useAvatar,
      avatarImage: studioAvatar,
      highlightsConfigId: studioHighlightsConfigId,
      highlightsInputs: studioHighlightsInputs,
      technicalDrawingMode: studioTechnicalDrawingMode,
      technicalDrawingMaterialImage: studioTechnicalDrawingMaterialImage,
      technicalDrawingMaterial: studioTechnicalDrawingMaterial,
      technicalDrawingShadowStyle: studioTechnicalDrawingShadowStyle,
      detailsFocus: studioDetailsFocus,
      detailsMode: studioDetailsMode,
      detailsCameraPerspective: studioDetailsCameraPerspective,
      detailsMaterial: studioDetailsMaterial,
      detailsMaterialReferenceImage: studioDetailsMaterialReference,
      detailsSceneDescription: studioDetailsSceneDescription,
      resolution: normalizeResolution(size),
      cutoutShadows: studioCutoutShadows,
      backgroundRemoval: studioBackgroundRemoval,
      productDetails: studioProductDetails,
      dimensionMarker,
      dimensionCapStyle,
      dimensionShowLabel:
        dimensionShowLabel ?? activeDimensionPreset?.style.showLabel ?? true,
      dimensionTargetFormat,
      dimensionPreset: activeDimensionPreset ?? null,
      styleConfig: {
        softLighting,
        neverHideParts,
        alwaysCenter,
        desiredRoomStyle,
      },
    };

    const settings = workflowOverride
      ? { ...baseSettings, imageType: workflowOverride.id }
      : baseSettings;
    const generationDimensionPreset =
      settings.dimensionPreset === undefined
        ? activeDimensionPreset
        : (settings.dimensionPreset ?? undefined);
    const selectedWorkflow =
      workflowOverride || workflows.find((w) => w.id === settings.imageType);
    const decorationHeightCm =
      settings.imageType === "Decoration"
        ? parseProductDimensionCm(product.height, 0)
        : 0;
    const decorationWidthCm =
      settings.imageType === "Decoration"
        ? parseProductDimensionCm(product.width, 0)
        : 0;

    let cost = COST_IMAGE_1K;
    if (settings.imageType === "Cutout") {
      const brCfg = resolveBackgroundRemovalConfig(settings);
      // Studio resolution comes from the product generate button (settings.resolution),
      // not from the controls — the controls hide their resolution picker here.
      cost = backgroundRemovalCreditCost(
        {
          mode: brCfg.mode,
          resolution: settings.resolution as "1K" | "2K" | "4K" | undefined,
        },
        {
          bgRemove: COST_BG_REMOVE,
          image1k: COST_IMAGE_1K,
          image2k: COST_IMAGE_2K,
          image4k: COST_IMAGE_4K,
        }
      );
    } else if (settings.imageType === "Re-Coloring") {
      const swatchCount = settings.colorSwatches
        ? settings.colorSwatches.filter((s) => s.isSelected).length
        : 1;
      cost = (swatchCount || 1) * COST_RECOLOR;
    } else if (selectedWorkflow) {
      cost = COST_IMAGE_1K;
    }

    // Note: Credit checking is now handled by geminiService functions
    // They will throw InsufficientCreditsError if user doesn't have enough credits

    if (
      settings.imageType === "Standard" &&
      settings.useAvatar &&
      !settings.avatarImage
    ) {
      toast.error(t("studio.errorAvatarMissingReferenceImage"));
      return { ok: false, reason: "error" as const };
    }

    if (selectedWorkflow && hasMilieuFixedNodesMissingStyle(selectedWorkflow)) {
      const message = getWorkflowMilieuFixedStyleRequiredMessage(
        selectedWorkflow.name
      );
      setError(message);
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, isGenerating: false, error: message } : p
        )
      );
      return { ok: false, reason: "error" as const };
    }

    if (
      settings.imageType === "Decoration" &&
      decorationHeightCm <= 0 &&
      decorationWidthCm <= 0
    ) {
      const message = t("studio.decorationSizeRequiredError", {
        defaultValue:
          "Decoration needs at least a width or height on the product card before generating.",
      });
      setError(message);
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, isGenerating: false, error: message } : p
        )
      );
      toast.error(message);
      return { ok: false, reason: "error" as const };
    }

    // Backstop for the required-dimension rule: the card/header buttons are
    // disabled when dimensions are missing, but other entry points (the queue,
    // and any future caller) flow through here. Validate at the chokepoint so a
    // dimensionless drawing can never be generated or charged for.
    if (
      settings.imageType === "Scaling" &&
      // The snapshot's marker, not the panel's: a queued width-only job must
      // not fail here just because the panel moved to "both" since queueing.
      !scalingDimensionsComplete(
        product,
        generationDimensionPreset?.markerPreference ??
          settings.dimensionMarker ??
          dimensionMarker
      )
    ) {
      const message = t("studio.scalingDimensionRequiredError");
      setError(message);
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, isGenerating: false, error: message } : p
        )
      );
      toast.error(message);
      return { ok: false, reason: "error" as const };
    }

    setStudioProducts((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, isGenerating: true, error: null } : p
      )
    );

    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: product.originalImage.name,
      imageType: settings.imageType || "Standard",
      productId: id,
    });

    try {
      // Ensure full-res image is loaded before any generation
      let currentImage = product.image;
      if (settings.imageType === "Decoration") {
        currentImage = await ensureTransparentProductImage(product.image, id, {
          notifyForDecoration: true,
        });
      }
      currentImage = await ensureImageBase64(currentImage);

      let results: GeneratedContent[] = [];
      let completionError: string | null = null;
      const cleanName = product.originalImage.name;

      if (selectedWorkflow) {
        // Each node names its own output, immediately before it generates —
        // the backend writes that node's DB row from the context set there,
        // so renaming the finished results here (as this used to) never
        // reached the database (SIS-517). The list accumulates across nodes so
        // the numbering stays unique over the whole run.
        const workflowExistingTitles: Array<string | undefined> =
          product.generatedContent.map((c) => c.title);
        const nameWorkflowResult = (details: string) => {
          const title = generateUniqueFilename(
            namingTemplate,
            {
              originalName: cleanName,
              type: "Workflow",
              details,
            },
            workflowExistingTitles
          );
          workflowExistingTitles.push(title);
          return title;
        };

        const workflowResults = await executeWorkflow(
          selectedWorkflow,
          product,
          settings,
          size,
          nameWorkflowResult
        );

        const processedResults = workflowResults.map((res) => ({
          ...res,
          tags: ["Workflow", selectedWorkflow.name],
          generationSettings: mergeGeneratedImageSettings(
            res.generationSettings,
            {
              imageGeneration: settings.imageGeneration,
              ...(res.generationSettings || {}),
              imageType: res.generationSettings?.imageType || "Workflow",
              workflowName: selectedWorkflow.name,
            }
          ),
          fullSettings: {
            ...settings,
            ...generationProductLightsSnapshot(res.generationSettings),
          },
        }));
        results.push(...processedResults);
        touchWorkflowLastUsed({ workflowId: selectedWorkflow.id }).catch(
          () => {}
        );
      } else if (settings.imageType === "Decoration") {
        const decorationAspectRatio = "1:1";
        const selectedVibeObj = vibes.find((v) => v.id === settings.vibe);
        const selectedSceneObj = scenes.find((s) => s.id === settings.scene);
        const resolvedDecorationDimensions = await resolveDecorationDimensions({
          image: currentImage,
          widthInput: product.width,
          heightInput: product.height,
        });
        const decorationHeightCm = resolvedDecorationDimensions.heightCm;
        const decorationWidthCm = resolvedDecorationDimensions.widthCm;

        // Single resolution value for generation and persisted metadata, so
        // the details panel can never show a different resolution than the
        // one actually generated.
        const decorationResolution = normalizeResolution(
          settings.resolution || size || "1K"
        );

        // One settings object for both the backend-persisted context and the
        // in-memory result — Decoration results are persisted by the backend
        // (dbPersisted), so without this the decoration rows in the details
        // panel would exist only until the next reload. The context itself is
        // set inside generateDecorationImage right before the backend call
        // (not here), so its long analysis/composition phase can't race a
        // concurrent generation for another product.
        // Named before the call — the backend writes the DB row from these
        // settings, so a title added to the result afterwards never reaches
        // it (SIS-517). Uniquified like every sibling path: this name is now
        // persisted, so repeated Decoration runs must not collide.
        const title = generateUniqueFilename(
          namingTemplate,
          {
            originalName: cleanName,
            type: "Decoration",
            scene: selectedSceneObj?.label,
            vibe: selectedVibeObj?.label,
          },
          product.generatedContent.map((c) => c.title)
        );
        const decorationPersistedSettings = {
          imageType: "Decoration",
          title,
          vibe: selectedVibeObj?.label || settings.vibe,
          scene: selectedSceneObj?.label || settings.scene,
          ...environmentIdSettings(settings),
          aspectRatio: decorationAspectRatio,
          resolution: decorationResolution,
          model: "Gemini 3 Pro Image Preview",
          context: settings.vibeContext,
          desiredRoomStyle: settings.styleConfig?.desiredRoomStyle,
          decorationSurfaceOverride: settings.decorationSurfaceOverride,
          decorationFramingPreference: settings.decorationFramingPreference,
          decorationRoomPlacementPreference:
            settings.decorationRoomPlacementPreference,
          decorationViewAngle: settings.decorationViewAngle,
          decorationResolvedDimensions: {
            widthCm: decorationWidthCm,
            heightCm: decorationHeightCm,
            imageAspectRatio: resolvedDecorationDimensions.imageAspectRatio,
            anchor: resolvedDecorationDimensions.anchor,
          },
        };

        const decoration = await generateDecorationImage({
          productImage: currentImage,
          productHeightCm: decorationHeightCm,
          productWidthCm: decorationWidthCm,
          aspectRatio: decorationAspectRatio,
          resolution: decorationResolution,
          serializedGenerationSettings: serializeImageGenerationSettings(
            settings.imageGeneration,
            decorationPersistedSettings
          ),
          projectId,
          productId: id,
          customPrompt: settings.vibeContext || "",
          vibe: selectedVibeObj || null,
          scene: selectedSceneObj || null,
          desiredRoomStyle: settings.styleConfig?.desiredRoomStyle,
          surfaceOverride: settings.decorationSurfaceOverride,
          framingPreference: settings.decorationFramingPreference,
          roomPlacementPreference: settings.decorationRoomPlacementPreference,
          viewAngle: settings.decorationViewAngle,
          productAnalysis: {
            category: product.productCategory,
            description: product.productDescription,
          },
        });

        results.push({
          ...decoration.content,
          imageType: "Decoration",
          title,
          tags: [
            "Decoration",
            selectedVibeObj?.label || "",
            selectedSceneObj?.label || "",
          ].filter(Boolean),
          generationSettings: mergeGeneratedImageSettings(
            decoration.content.generationSettings,
            {
              imageGeneration: settings.imageGeneration,
              ...decorationPersistedSettings,
              decorationAutoStage: decoration.debug.autoStage,
            }
          ),
          fullSettings: settings,
          debugBundle: import.meta.env.DEV
            ? {
                type: "decoration",
                request: decoration.debug.request,
                assets: decoration.debug.assets,
                analysis: decoration.debug.analysis,
                autoStage: decoration.debug.autoStage,
              }
            : undefined,
        });
      } else if (settings.imageType === "Scaling") {
        // Client-side 2D drawing — the same pipeline as the Dimensions
        // quick-action editor, so both entry points render identically. The
        // annotation in generationSettings.dimensionsData makes the result
        // re-openable in the editor for further changes.
        const dimResult = await generateDimensional2DDrawing(product.image, {
          preset: generationDimensionPreset,
          projectId,
          productId: product.id,
          productWidth: product.width || undefined,
          productHeight: product.height || undefined,
          markerPreference: settings.dimensionMarker ?? dimensionMarker,
          capStyle: settings.dimensionCapStyle ?? dimensionCapStyle,
          showLabel: settings.dimensionShowLabel ?? dimensionShowLabel,
          targetAspectRatio:
            settings.dimensionTargetFormat ?? dimensionTargetFormat,
          companyLogo,
          logoPosition,
          logoScale,
        });

        const title = generateUniqueFilename(
          namingTemplate,
          {
            originalName: cleanName,
            type: "Scaling",
            details: "Dimensional",
          },
          [
            ...product.generatedContent.map((c) => c.title),
            ...results.map((r) => r.title),
          ]
        );

        results.push({
          ...dimResult,
          title,
          tags: ["Scaling"],
          fullSettings: settings,
        });
      } else if (settings.imageType === "Re-Coloring") {
        const colors = settings.colorSwatches
          ? settings.colorSwatches.filter((s) => s.isSelected)
          : [];
        if (colors.length === 0 && settings.recolorColor) {
          // Persist the same shape the details panel reads (recolorColor,
          // model, recolorModel) — this legacy path previously stored a bare
          // `color` field, so dbPersisted results showed an empty recolor
          // section after reload.
          const legacyRecolorExtra = {
            model: imageModelDisplayName("nano-banana-pro"),
            recolorModel: "nano-banana-pro" as const,
            recolorAdditionalContext:
              settings.recolorAdditionalContext?.trim() || undefined,
          };
          // Named before the call, not after: the backend writes the DB row
          // from this context, so a title added to the result afterwards never
          // reaches it and the image reloads as "Generated Image" (SIS-517).
          const title = generateUniqueFilename(
            namingTemplate,
            {
              originalName: cleanName,
              type: "Recolor",
              details: settings.recolorColor,
            },
            [
              ...product.generatedContent.map((c) => c.title),
              ...results.map((r) => r.title),
            ]
          );
          setGenerationContext(
            projectId,
            id,
            "Re-Coloring",
            serializeImageGenerationSettings(settings.imageGeneration, {
              ...buildRecolorGenerationSettings(
                settings.recolorColor,
                settings.aspectRatio,
                undefined,
                undefined,
                undefined,
                legacyRecolorExtra
              ),
              title,
            })
          );
          const res = await recolorImage(
            currentImage,
            settings.recolorColor,
            { category: product.productCategory },
            undefined,
            undefined,
            undefined,
            undefined,
            "nano-banana-pro",
            "high",
            undefined,
            undefined,
            undefined,
            settings.recolorAdditionalContext
          );
          results.push({
            ...res,
            title,
            tags: ["Recolor", settings.recolorColor],
            generationSettings: mergeGeneratedImageSettings(
              res.generationSettings,
              {
                imageGeneration: settings.imageGeneration,
                ...buildRecolorGenerationSettings(
                  settings.recolorColor,
                  res.generationSettings?.aspectRatio,
                  undefined,
                  undefined,
                  undefined,
                  legacyRecolorExtra
                ),
                title,
              }
            ),
            fullSettings: settings,
          });
        } else {
          const failedSwatches: string[] = [];
          let haltedByError: Error | null = null;

          for (const color of colors) {
            const perUsageOnly =
              studioRecolorOptions.usageOnly && !!color.usageReferenceImage;
            const referenceImage = perUsageOnly
              ? undefined
              : color.referenceImage;
            const usageReferenceImage = color.usageReferenceImage;
            // A grain sketch forces GPT Image 2 in the service — persist the
            // model that is actually used, not the pre-coercion choice.
            const effectiveRecolorModel = resolveEffectiveRecolorModel(
              studioRecolorOptions.model,
              !!studioRecolorOptions.grainSketch
            );
            const recolorExtra = {
              model: imageModelDisplayName(effectiveRecolorModel),
              recolorModel: effectiveRecolorModel,
              recolorQuality: studioRecolorOptions.quality,
              recolorUsageReferenceOnly: perUsageOnly,
              recolorAdditionalContext:
                settings.recolorAdditionalContext?.trim() || undefined,
              resolution: studioRecolorOptions.resolution,
            };
            try {
              // Named before the call — the backend writes the DB row from
              // this context (SIS-517).
              const title = generateUniqueFilename(
                namingTemplate,
                {
                  originalName: cleanName,
                  type: "Recolor",
                  details: color.value,
                },
                [
                  ...product.generatedContent.map((c) => c.title),
                  ...results.map((r) => r.title),
                ]
              );
              setGenerationContext(
                projectId,
                id,
                "Re-Coloring",
                serializeImageGenerationSettings(settings.imageGeneration, {
                  ...buildRecolorGenerationSettings(
                    color.value,
                    undefined,
                    referenceImage,
                    usageReferenceImage,
                    color.material,
                    recolorExtra
                  ),
                  title,
                })
              );
              const res = await recolorImage(
                currentImage,
                color.value,
                { category: product.productCategory },
                referenceImage,
                usageReferenceImage,
                color.material,
                studioRecolorOptions.grainSketch ?? undefined,
                studioRecolorOptions.model,
                studioRecolorOptions.quality,
                studioRecolorOptions.resolution,
                perUsageOnly,
                studioRecolorOptions.temperature,
                settings.recolorAdditionalContext
              );

              results.push({
                ...res,
                title,
                tags: ["Recolor", color.value],
                generationSettings: mergeGeneratedImageSettings(
                  res.generationSettings,
                  {
                    imageGeneration: settings.imageGeneration,
                    ...buildRecolorGenerationSettings(
                      color.value,
                      res.generationSettings?.aspectRatio,
                      referenceImage,
                      usageReferenceImage,
                      color.material,
                      recolorExtra
                    ),
                    title,
                  }
                ),
                fullSettings: settings,
              });
            } catch (error) {
              console.error("Recolor swatch generation failed", {
                productId: id,
                swatch: color,
                error,
              });

              failedSwatches.push(getColorSwatchDisplayLabel(color));

              if (
                error instanceof InsufficientCreditsError ||
                error instanceof geminiService.ResourceExhaustedError
              ) {
                haltedByError =
                  error instanceof Error
                    ? error
                    : new Error("Recolor generation failed");
                break;
              }
            }
          }

          if (results.length === 0) {
            if (haltedByError) throw haltedByError;
            if (failedSwatches.length > 0) {
              throw new Error(
                `Failed to generate recolors for ${failedSwatches.join(", ")}.`
              );
            }
          }

          if (failedSwatches.length > 0) {
            const failedSummary =
              failedSwatches.length > 3
                ? `${failedSwatches.slice(0, 3).join(", ")} +${failedSwatches.length - 3} more`
                : failedSwatches.join(", ");

            completionError = haltedByError
              ? `Generated ${results.length} recolor${results.length === 1 ? "" : "s"}, then stopped: ${haltedByError.message}`
              : `Generated ${results.length} recolor${results.length === 1 ? "" : "s"}. Failed swatches: ${failedSummary}`;
          }
        }
      } else if (settings.imageType === "Rotation") {
        const rotationSource =
          settings.rotationMode === "milieu"
            ? product.originalImage
            : currentImage;
        const rotationAspectRatio =
          rotationSource.width && rotationSource.height
            ? getClosestAspectRatio(rotationSource.width, rotationSource.height)
            : settings.aspectRatio || "1:1";
        const rotationResolution = normalizeResolution(
          settings.resolution || size || "1K"
        );
        const rotationModelChoice = settings.rotationModel || "nano-banana-pro";
        const rotationQualityChoice = settings.rotationQuality || "high";
        // Named before the call — the backend writes the DB row from this
        // context (SIS-517).
        const title = generateUniqueFilename(
          namingTemplate,
          {
            originalName: cleanName,
            type: "Rotation",
            details: settings.rotationAngle,
          },
          [
            ...product.generatedContent.map((c) => c.title),
            ...results.map((r) => r.title),
          ]
        );
        setGenerationContext(
          projectId,
          id,
          "Rotation",
          serializeImageGenerationSettings(settings.imageGeneration, {
            imageType: "Rotation",
            title,
            rotationAngle: settings.rotationAngle,
            rotationMode: settings.rotationMode,
            rotationModel: rotationModelChoice,
            rotationQuality: rotationQualityChoice,
            aspectRatio: rotationAspectRatio,
            resolution: rotationResolution,
          })
        );
        const res = await generateRotationImage(
          rotationSource,
          settings.rotationAngle || "Frontal view",
          settings.vibeContext,
          rotationAspectRatio,
          settings.rotationMode,
          rotationResolution,
          rotationModelChoice,
          rotationQualityChoice,
          {
            category: product.productCategory,
            description: product.productDescription,
          }
        );

        results.push({
          ...res,
          title,
          tags: ["Rotation", settings.rotationAngle || ""],
          generationSettings: mergeGeneratedImageSettings(
            res.generationSettings,
            {
              imageGeneration: settings.imageGeneration,
              imageType: "Rotation",
              title,
              rotationAngle: settings.rotationAngle,
              rotationMode: settings.rotationMode,
              rotationModel: rotationModelChoice,
              rotationQuality: rotationQualityChoice,
              aspectRatio: rotationAspectRatio,
              resolution: rotationResolution,
              model: imageModelDisplayName(rotationModelChoice),
              context: settings.vibeContext,
            }
          ),
          fullSettings: settings,
        });
      } else if (settings.imageType === "Cutout") {
        const cfg = resolveBackgroundRemovalConfig(settings);

        if (cfg.mode === "simple") {
          const alreadyTransparent =
            await hasTransparentAlphaChannel(currentImage);
          if (alreadyTransparent && cfg.fill !== "white") {
            // Graceful skip: the image already has transparency and the user
            // did not request a white fill, so background removal is a no-op.
            // There is no `finally` block on this function — cleanup of the
            // product's `isGenerating` flag happens in the success path (below)
            // and in the catch. So we replicate the minimal cleanup here (clear
            // `isGenerating`, no error), surface an info notification instead of
            // an error toast, and return `{ ok: true }` so bulk runs continue
            // to the next product without overwriting the info notification with
            // the generic success update.
            updateNotification(notificationId, {
              type: "info",
              message: t("studio.removeBgAlreadyTransparent"),
            });
            setStudioProducts((prev) =>
              prev.map((p) =>
                p.id === id ? { ...p, isGenerating: false, error: null } : p
              )
            );
            return { ok: true };
          }

          if (alreadyTransparent && cfg.fill === "white") {
            // Already-transparent input + white fill: flatten onto white
            // locally instead of calling Bria (nothing to remove; that would be
            // a credit-charged no-op). Gallery-only — we deliberately keep the
            // user's transparent cutout as the product's working image rather
            // than replacing it with a flattened JPEG.
            const whiteImg = await buildWhiteFilledFromTransparent(
              currentImage,
              id
            );
            const title = generateUniqueFilename(
              namingTemplate,
              {
                originalName: cleanName,
                type: "Cutout_PNG",
              },
              [
                ...product.generatedContent.map((c) => c.title),
                ...results.map((r) => r.title),
              ],
              "jpg"
            );
            results.push({
              id: crypto.randomUUID(),
              image: whiteImg.fullUrl || whiteImg.thumbnailUrl || "",
              storagePath: whiteImg.storagePath,
              thumbnailUrl: whiteImg.thumbnailUrl,
              fullUrl: whiteImg.fullUrl,
              text: "Background Removed (White Fill)",
              title,
              video: null,
              tags: ["Cutout", "No Background", "White Filled"],
              imageType: "Cutout",
              generationSettings: {
                imageType: "Cutout",
                aspectRatio: settings.aspectRatio,
                backgroundRemoval: cfg,
                model: "White fill (no AI)",
                imageGeneration: undefined,
                context: settings.vibeContext,
              },
              fullSettings: settings,
              timestamp: Date.now(),
              filename: title,
              mimeType: "image/jpeg",
              dimensions:
                typeof whiteImg.width === "number" &&
                typeof whiteImg.height === "number"
                  ? { width: whiteImg.width, height: whiteImg.height }
                  : undefined,
            });
          } else {
            const isWhiteFill = cfg.fill === "white";
            // Named before the call — the settings handed to the backend are
            // what it writes the DB row from, so a title added to the result
            // afterwards never reaches it (SIS-517).
            const title = generateUniqueFilename(
              namingTemplate,
              {
                originalName: cleanName,
                type: "Cutout_PNG",
              },
              [
                ...product.generatedContent.map((c) => c.title),
                ...results.map((r) => r.title),
              ]
            );
            const cutoutPngSettings = {
              imageType: "Cutout",
              title,
              aspectRatio: settings.aspectRatio,
              backgroundRemoval: cfg,
              model: "showcase AI",
              context: settings.vibeContext,
            };
            const noBg = await executeBackgroundRemoval(
              currentImage,
              undefined,
              projectId,
              id,
              "Cutout",
              serializeImageGenerationSettings(settings.imageGeneration, {
                ...cutoutPngSettings,
                backgroundRemoval: cfg,
              }),
              { fillBackgroundWhite: isWhiteFill }
            );

            results.push({
              id: noBg.dbId ?? crypto.randomUUID(),
              image: noBg.downloadUrl,
              storagePath: noBg.storagePath,
              thumbnailUrl: noBg.downloadUrl,
              text: isWhiteFill
                ? "Background Removed (White Fill)"
                : "Background Removed",
              title,
              video: null,
              tags: isWhiteFill
                ? ["Cutout", "No Background", "White Filled"]
                : ["Cutout", "No Background", "Transparent"],
              imageType: "Cutout",
              generationSettings: cutoutPngSettings,
              fullSettings: settings,
              timestamp: Date.now(),
              filename: title,
              mimeType: noBg.mimeType,
              dimensions: { width: noBg.width, height: noBg.height },
              dbPersisted: !!noBg.dbId,
            });

            // Update product.image so it shows in Studio Assets
            const noBgAppImage = toAppImageFile(noBg, id);

            setStudioProducts((prev) =>
              prev.map((p) => (p.id === id ? { ...p, image: noBgAppImage } : p))
            );

            // Persist the updated product image to the database
            updateStudioProductCurrentImage(projectId, id, noBgAppImage).catch(
              (e) => console.error("Failed to persist updated product image", e)
            );
          }
        } else {
          // Use Nano Banana Pro for JPEG with shadows
          const selectedVibeObj = vibes.find((v) => v.id === settings.vibe);
          const vibeLabel = settings.useTrueToneStudio
            ? "True-Tone Studio"
            : selectedVibeObj?.label;
          // Named before the call — the backend writes the DB row from this
          // context (SIS-517).
          const title = generateUniqueFilename(
            namingTemplate,
            {
              originalName: cleanName,
              type: "Cutout",
            },
            [
              ...product.generatedContent.map((c) => c.title),
              ...results.map((r) => r.title),
            ],
            "jpg"
          );
          setGenerationContext(
            projectId,
            id,
            "Cutout",
            serializeImageGenerationSettings(settings.imageGeneration, {
              imageType: "Cutout",
              title,
              backgroundRemoval: cfg,
              aspectRatio: settings.aspectRatio,
            })
          );
          const res = await generateCutoutImage(
            currentImage,
            settings.aspectRatio || "1:1",
            settings.vibeContext,
            cfg.shadows,
            vibeLabel,
            product.originalImage.name.split(".")[0], // productName
            "original", // productColor
            {
              category: product.productCategory,
              description: product.productDescription,
            },
            // Studio resolution is chosen on the product generate button.
            (settings.resolution as "1K" | "2K" | "4K") || "1K"
          );

          results.push({
            ...res,
            title,
            tags: ["Cutout"],
            generationSettings: mergeGeneratedImageSettings(
              res.generationSettings,
              {
                imageGeneration: settings.imageGeneration,
                imageType: "Cutout",
                title,
                aspectRatio: settings.aspectRatio,
                model: "Nano Banana Pro",
                context: settings.vibeContext,
                backgroundRemoval: cfg,
              }
            ),
            fullSettings: settings,
          });
        }
      } else if (settings.imageType === "Transform Technical Drawing") {
        const tdMode = settings.technicalDrawingMode || "milieu";
        const tdShadowStyle =
          settings.technicalDrawingShadowStyle || "lightbox";
        const selectedVibeObj = vibes.find((v) => v.id === settings.vibe);
        const selectedSceneObj = scenes.find((s) => s.id === settings.scene);
        const vibeDescription =
          tdMode === "milieu"
            ? settings.useTrueToneStudio
              ? t("studio.trueToneStudioDescription")
              : selectedVibeObj?.description || settings.vibe || ""
            : undefined;
        // The Custom Context textarea is only shown for the milieu mode —
        // compose it into the scene exactly like the Standard path (SIS-500).
        const sceneContext =
          tdMode === "milieu"
            ? composeSceneContext(
                selectedSceneObj?.context ||
                  (settings.scene === "auto" ? "" : settings.scene || ""),
                settings.vibeContext
              )
            : undefined;

        const technicalDrawingEnvironmentSettings =
          tdMode === "milieu"
            ? {
                vibe: settings.useTrueToneStudio
                  ? "True-Tone Studio"
                  : selectedVibeObj?.label || settings.vibe,
                scene: selectedSceneObj?.label || settings.scene,
                ...environmentIdSettings(settings),
                // Persist a blank context explicitly (like the Standard path):
                // dropping the key makes a deliberately empty prompt
                // indistinguishable from "never stored", so a regeneration
                // after reload would inherit the live textarea instead.
                context: settings.vibeContext ?? "",
                ...environmentSnapshotSettings(settings),
              }
            : {};
        // Single resolution value for generation and persisted metadata.
        const technicalDrawingResolution = normalizeResolution(
          settings.resolution || size || "1K"
        );
        // Named before the call — the backend writes the DB row from these
        // settings, so a title added to the result afterwards never reaches
        // it (SIS-517).
        const title = generateUniqueFilename(
          namingTemplate,
          {
            originalName: cleanName,
            type: "TechnicalDrawing",
          },
          [
            ...product.generatedContent.map((c) => c.title),
            ...results.map((r) => r.title),
          ]
        );
        const technicalDrawingPersistedSettings = {
          imageType: "Transform Technical Drawing",
          title,
          aspectRatio: settings.aspectRatio,
          resolution: technicalDrawingResolution,
          model: "Nano Banana Pro",
          technicalDrawingMode: tdMode,
          technicalDrawingMaterial: settings.technicalDrawingMaterial,
          technicalDrawingShadowStyle:
            tdMode === "cutout" ? tdShadowStyle : undefined,
          ...technicalDrawingEnvironmentSettings,
        };

        setGenerationContext(
          projectId,
          id,
          "Transform Technical Drawing",
          serializeImageGenerationSettings(
            settings.imageGeneration,
            technicalDrawingPersistedSettings
          )
        );
        const res = await generateTransformTechnicalDrawingImage(
          currentImage,
          tdMode,
          settings.aspectRatio || "1:1",
          technicalDrawingResolution,
          vibeDescription,
          sceneContext,
          settings.technicalDrawingMaterialImage ?? undefined,
          settings.technicalDrawingMaterial || undefined,
          tdMode === "cutout" ? tdShadowStyle : "lightbox",
          {
            category: product.productCategory,
            description: product.productDescription,
          }
        );

        results.push({
          ...res,
          title,
          tags: ["TechnicalDrawing"],
          generationSettings: mergeGeneratedImageSettings(
            res.generationSettings,
            technicalDrawingPersistedSettings,
            settings.imageGeneration
          ),
          fullSettings: settings,
        });
      } else if (settings.imageType === "Details") {
        const selectedVibeObj = vibes.find((v) => v.id === settings.vibe);
        const selectedSceneObj = scenes.find((s) => s.id === settings.scene);

        // Use True-Tone Studio if enabled, otherwise use selected vibe
        const vibeLabel = settings.useTrueToneStudio
          ? "True-Tone Studio"
          : selectedVibeObj?.label || settings.vibe;
        const vibeDescription = settings.useTrueToneStudio
          ? t("studio.trueToneStudioDescription")
          : selectedVibeObj?.description || settings.vibe || "";

        const detailsBaseSceneContext =
          selectedSceneObj?.context ||
          (settings.scene === "auto" ? "" : settings.scene || "");
        // The Custom Context textarea is only shown for the milieu mode —
        // compose it into the scene exactly like the Standard path (SIS-500).
        const detailsSceneContext =
          settings.detailsMode !== "cutout"
            ? composeSceneContext(detailsBaseSceneContext, settings.vibeContext)
            : detailsBaseSceneContext;

        const detailsEnvironmentSettings =
          settings.detailsMode !== "cutout"
            ? {
                vibe: vibeLabel,
                scene: selectedSceneObj?.label || settings.scene,
                ...environmentIdSettings(settings),
                // Blank means blank — see the Technical Drawing path above.
                context: settings.vibeContext ?? "",
                ...environmentSnapshotSettings(settings),
              }
            : {};

        // Single resolution value for generation and persisted metadata.
        const detailsResolution = normalizeResolution(
          settings.resolution || size || "1K"
        );

        // One settings object for both the backend-persisted context and the
        // in-memory result, so the details panel shows the same rows before
        // and after a reload. Keys must match what the panel reads
        // (detailsFocus/detailsMode etc.).
        // Named before the call — the backend writes the DB row from these
        // settings (SIS-517).
        const title = generateUniqueFilename(
          namingTemplate,
          {
            originalName: cleanName,
            type: "Details",
            details: settings.detailsFocus,
          },
          [
            ...product.generatedContent.map((c) => c.title),
            ...results.map((r) => r.title),
          ]
        );
        const detailsPersistedSettings = {
          imageType: "Details",
          title,
          aspectRatio: settings.aspectRatio,
          resolution: detailsResolution,
          model: modelType === "standard" ? "Nano Banana" : "Nano Banana Pro",
          detailsFocus: settings.detailsFocus,
          detailsMode: settings.detailsMode,
          detailsCameraPerspective: settings.detailsCameraPerspective,
          detailsMaterial: settings.detailsMaterial,
          detailsSceneDescription: settings.detailsSceneDescription,
          ...detailsEnvironmentSettings,
        };

        setGenerationContext(
          projectId,
          id,
          "Details",
          serializeImageGenerationSettings(
            settings.imageGeneration,
            detailsPersistedSettings
          )
        );
        const res = await generateDetailsImage(
          currentImage,
          settings.detailsFocus || "Texture",
          settings.detailsMode || "milieu",
          settings.aspectRatio || "1:1",
          vibeDescription,
          detailsSceneContext,
          settings.detailsCameraPerspective,
          settings.detailsMaterial,
          settings.detailsSceneDescription,
          settings.detailsMaterialReferenceImage ?? undefined,
          vibeLabel,
          detailsResolution
        );

        results.push({
          ...res,
          title,
          tags: ["Details", settings.detailsFocus || ""],
          generationSettings: mergeGeneratedImageSettings(
            res.generationSettings,
            {
              imageGeneration: settings.imageGeneration,
              ...detailsPersistedSettings,
              detailsMaterialReferenceImage:
                settings.detailsMaterialReferenceImage,
            }
          ),
          fullSettings: settings,
        });
      } else if (settings.imageType === "Aspect Ratio") {
        const targetRatio = settings.aspectRatio || "16:9";
        const resolution = size ?? "1K";
        // Named before the call — the backend writes the DB row from this
        // context (SIS-517).
        const title = generateUniqueFilename(
          namingTemplate,
          {
            originalName: cleanName,
            type: "AspectRatio",
            details: targetRatio.replace(":", "x"),
          },
          [
            ...product.generatedContent.map((c) => c.title),
            ...results.map((r) => r.title),
          ]
        );
        setGenerationContext(
          projectId,
          id,
          "Aspect Ratio",
          serializeImageGenerationSettings(settings.imageGeneration, {
            imageType: "Aspect Ratio",
            title,
            aspectRatio: targetRatio,
            resolution,
          })
        );
        const res = await generateAspectRatioConversion(
          currentImage,
          targetRatio,
          resolution
        );

        results.push({
          ...res,
          title,
          tags: ["Aspect Ratio", targetRatio],
          timestamp: Date.now(),
          generationSettings: mergeGeneratedImageSettings(
            res.generationSettings,
            {
              imageGeneration: settings.imageGeneration,
              imageType: "Aspect Ratio",
              title,
              aspectRatio: targetRatio,
              resolution: resolution,
              model: "Nano Banana Pro",
            }
          ),
          fullSettings: settings,
        });
      } else if (settings.imageType === "Highlights") {
        const config = highlightsConfigs.find(
          (c) => c.id === settings.highlightsConfigId
        );
        if (!config || !config.referenceImage || !config.template) {
          throw new Error(
            "Missing template configuration for highlights generation"
          );
        }

        const selectedVibeObj = vibes.find(
          (v) => v.id === (config.vibeId || settings.vibe)
        );
        const selectedSceneObj = scenes.find(
          (s) => s.id === (config.sceneId || settings.scene)
        );

        const vibeDesc = selectedVibeObj?.description || settings.vibe || "";
        const sceneDesc =
          selectedSceneObj?.context ||
          (settings.scene === "auto" ? "" : settings.scene || "");

        const highlightsEnvironmentSettings = {
          vibe: selectedVibeObj?.label || settings.vibe,
          scene: selectedSceneObj?.label || settings.scene,
          // The highlights config can carry its own environment, so persist
          // the ids that were actually resolved above.
          ...environmentIdSettings({
            vibe: config.vibeId || settings.vibe,
            scene: config.sceneId || settings.scene,
          }),
          // The marketing copy filled into this configuration's template.
          // Persisted with the config id so a regeneration after reload
          // renders the same text instead of whatever the panel now holds
          // for a different configuration.
          highlightsInputs: settings.highlightsInputs || {},
          ...environmentSnapshotSettings(settings),
        };

        // Named before the call — the backend writes the DB row from this
        // context (SIS-517).
        const title = generateUniqueFilename(
          namingTemplate,
          {
            originalName: cleanName,
            type: "Highlights",
          },
          [
            ...product.generatedContent.map((c) => c.title),
            ...results.map((r) => r.title),
          ]
        );
        setGenerationContext(
          projectId,
          id,
          "Highlights",
          serializeImageGenerationSettings(settings.imageGeneration, {
            imageType: "Highlights",
            title,
            highlightsConfigId: config.id,
            aspectRatio: settings.aspectRatio,
            ...highlightsEnvironmentSettings,
          })
        );
        const res = await geminiService.generateHighlightsImage(
          currentImage,
          config.referenceImage,
          config.template,
          settings.highlightsInputs || {},
          settings.aspectRatio || "1:1",
          vibeDesc,
          sceneDesc
        );

        results.push({
          ...res,
          title,
          tags: ["Highlights"],
          generationSettings: mergeGeneratedImageSettings(
            res.generationSettings,
            {
              imageGeneration: settings.imageGeneration,
              imageType: "Highlights",
              title,
              highlightsConfigId: config.id,
              aspectRatio: settings.aspectRatio,
              model: "Nano Banana Pro",
              context: "Features Template",
              ...highlightsEnvironmentSettings,
            }
          ),
          fullSettings: settings,
        });
      } else {
        const sourceImages: AppImageFile[] = [currentImage];
        if (
          settings.imageType === "Multi-Product" &&
          product.linkedProductIds
        ) {
          product.linkedProductIds.forEach((linkedId) => {
            const linkedP = studioProducts.find((p) => p.id === linkedId);
            if (linkedP) sourceImages.push(linkedP.image);
          });
        }

        const selectedVibeObj = vibes.find((v) => v.id === settings.vibe);
        const selectedSceneObj = scenes.find((s) => s.id === settings.scene);

        // Use True-Tone Studio if enabled, otherwise use selected vibe
        const vibeLabel = settings.useTrueToneStudio
          ? "True-Tone Studio"
          : selectedVibeObj?.label || settings.vibe;
        const vibeDescription = settings.useTrueToneStudio
          ? t("studio.trueToneStudioDescription")
          : selectedVibeObj?.description || settings.vibe || "";

        const backgroundConfig = {
          vibe: vibeDescription,
          context: composeSceneContext(
            selectedSceneObj?.context ||
              (settings.scene === "auto" ? "" : settings.scene || ""),
            settings.vibeContext
          ),
        };

        const commonSettings = {
          imageType: settings.imageType,
          vibe: vibeLabel,
          scene: selectedSceneObj?.label || settings.scene,
          ...environmentIdSettings(settings),
          aspectRatio: settings.aspectRatio,
          resolution: size,
          model: modelType === "standard" ? "Nano Banana" : "Nano Banana Pro",
          context: settings.vibeContext,
          ...environmentSnapshotSettings(settings),
          // Avatar is only applied by the Standard generation path — persist
          // it only there so other outputs never claim an avatar was used.
          ...(settings.imageType === "Standard" && settings.useAvatar
            ? { useAvatar: true }
            : {}),
        };

        const avatarToUse =
          settings.imageType === "Standard" && settings.useAvatar
            ? settings.avatarImage
            : null;

        // Named before the call — the backend writes the DB row from this
        // context, so a title added to the result afterwards never reaches it
        // and the image reloads as "Generated Image" (SIS-517).
        const title = generateUniqueFilename(
          namingTemplate,
          {
            originalName: cleanName,
            type: MILIEU_CREATIVE_LABEL,
            scene: selectedSceneObj?.label,
            vibe: vibeLabel,
          },
          [
            ...product.generatedContent.map((c) => c.title),
            ...results.map((r) => r.title),
          ]
        );

        const res = await generateStandardImage({
          projectId,
          productId: id,
          imageType: MILIEU_CREATIVE_LABEL,
          generationSettings: serializeImageGenerationSettings(
            settings.imageGeneration,
            {
              ...commonSettings,
              title,
            }
          ),
          productImages: sourceImages,
          background: backgroundConfig,
          productWidthCm: parseProductDimensionCm(product.width, 0),
          productHeightCm: parseProductDimensionCm(product.height, 0),
          imageSize: size,
          aspectRatio: settings.aspectRatio || "1:1",
          avatar: avatarToUse,
          styleConfig: settings.styleConfig,
          vibeLabel,
          productDetails: settings.productDetails,
          productLightsOn: settings.productLightsOn,
          productLightsWarmth: settings.productLightsWarmth,
          analysis: {
            category: product.productCategory,
            description: product.productDescription,
          },
        });

        results.push({
          ...res,
          imageType: MILIEU_CREATIVE_LABEL,
          title,
          tags: [vibeLabel || "", selectedSceneObj?.label || ""],
          generationSettings: mergeGeneratedImageSettings(
            res.generationSettings,
            {
              imageGeneration: settings.imageGeneration,
              ...commonSettings,
              title,
            }
          ),
          fullSettings: settings,
        });
      }

      // Ensure every result has a timestamp for consistent ordering
      const now = Date.now();
      results.forEach((r) => {
        if (!r.timestamp) r.timestamp = now;
      });

      // Upload and persist generated images.
      // Backend-persisted results (dbPersisted=true) already have the DB UUID as their id
      // and their GCS path is already stored — skip both upload and DB write for those.
      const uploadedResults = await Promise.all(
        results.map((res) =>
          res.dbPersisted
            ? Promise.resolve(res)
            : uploadGeneratedImage(projectId, id, res).catch(() => res)
        )
      );
      uploadedResults.forEach((res) => {
        if (res.dbPersisted) return; // Backend already persisted; id is already the DB UUID
        const oldId = res.id;
        persistGeneratedContent(projectId, id, res)
          .then((dbId) => syncContentDbId(id, oldId, dbId))
          .catch((e) =>
            console.error("Failed to persist generated content", e)
          );
      });

      setStudioProducts((prev) =>
        prev.map((p) => {
          if (p.id !== id) return p;
          return {
            ...p,
            generatedContent: [...uploadedResults, ...p.generatedContent],
            isGenerating: false,
            error: completionError,
          };
        })
      );
      updateNotification(notificationId, {
        type: completionError ? "info" : "success",
        message: completionError || undefined,
        contentId: uploadedResults[0]?.id,
        thumbnailUrl:
          uploadedResults[0]?.thumbnailUrl ||
          uploadedResults[0]?.image ||
          undefined,
        resultCount: uploadedResults.length,
      });

      return { ok: true };
    } catch (err) {
      console.error("Generation failed", err);

      const userMessage = resolveErrorMessage(err, t);

      updateNotification(notificationId, {
        type: "error",
        message: userMessage,
      });

      if (err instanceof InsufficientCreditsError) {
        setStudioProducts((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, isGenerating: false, error: userMessage } : p
          )
        );
        return { ok: false, reason: "credits" };
      }

      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, isGenerating: false, error: userMessage } : p
        )
      );
      // Note: No credit refund needed - geminiService only deducts on success
      return { ok: false, reason: "error" };
    }
  };

  const handleDownloadImage = async (
    imageUrl: string,
    filename: string,
    format: "png" | "jpeg" | "mp4" = "png"
  ) => {
    // Resolve the originating image record so we can track a stable ID,
    // determine whether this was an original upload or an AI-generated
    // result, and hand the canonical storagePath to the downloader (so it
    // pulls the full-res asset, not the _1024 thumbnail). Match storagePath
    // too — the download menu falls back to a raw storagePath when the display
    // URL hasn't resolved yet (mirrors the global-library handler).
    const matchesUrl = (c: GeneratedContent) =>
      c.image === imageUrl ||
      c.thumbnailUrl === imageUrl ||
      c.video === imageUrl ||
      c.fullUrl === imageUrl ||
      c.storagePath === imageUrl;
    const matchingGeneratedProduct = studioProducts.find((p) =>
      p.generatedContent.some(matchesUrl)
    );
    const matchingContent =
      matchingGeneratedProduct?.generatedContent.find(matchesUrl) ??
      multiProductScenes.flatMap((s) => s.generatedContent).find(matchesUrl);
    // Source images: match both the original upload (what the viewer's
    // source view shows) and the possibly background-removed working image,
    // by any of their URLs/paths — and keep the asset that actually matched,
    // so the download resolves the full-res object the user is looking at
    // rather than a sibling.
    const matchesImageFile = (img: AppImageFile) =>
      img.thumbnailUrl === imageUrl ||
      img.fullUrl === imageUrl ||
      img.storagePath === imageUrl;
    const matchingSource = !matchingContent
      ? studioProducts
          .flatMap((p) => [
            { productId: p.id, image: p.originalImage },
            { productId: p.id, image: p.image },
          ])
          .find(({ image }) => matchesImageFile(image))
      : undefined;
    const owningProductId =
      matchingGeneratedProduct?.id ?? matchingSource?.productId;
    const trackedStoragePath =
      matchingContent?.storagePath || matchingSource?.image.storagePath;
    // Stamp the opt-in "AI-generated" badge on every image download, original
    // uploads included — customers upload already-AI-generated images
    // specifically to deliver them with the badge. Pixels only: the badge
    // never adds an XMP disclosure or a C2PA credential to an upload (the
    // export pipeline carries existing metadata and deliberately refuses to
    // sign sources we didn't produce). Mirrors the global-library handler.
    const stampAiPill =
      !matchingContent?.video && format !== "mp4" && aiWatermarkEnabled;
    // Discriminated result: an unreachable badge library must not silently
    // downgrade a selected marketplace label to the pill — warn, then
    // deliver with the settings pill (analytics stay truthful below).
    const badgeResult = stampAiPill ? await fetchActiveAiBadge() : null;
    if (badgeResult?.status === "unavailable") {
      toast.error(t("errors.watermark/badge_library_unavailable"));
    }
    const activeBadge =
      badgeResult?.status === "ready" ? badgeResult.badge : null;
    const { stamped, deliveryPath } = await downloadAsset({
      url: imageUrl,
      filename,
      format,
      storagePath: trackedStoragePath,
      isVideo: !!matchingContent?.video,
      watermarkText: stampAiPill ? t("studio.aiWatermarkLabel") : null,
      watermarkStyle: aiWatermarkStyle,
      watermarkBadge: activeBadge,
    });
    // Tracked AFTER the download so `aiWatermark` reflects whether the badge
    // actually ended up on the file, not just the request intent.
    trackImageDownloaded({
      format,
      storagePath: trackedStoragePath,
      imageId: matchingContent?.id,
      source: matchingContent ? "generated" : "source",
      projectId,
      productId: owningProductId,
      aiWatermark: stamped,
      aiBadgeKind: stamped ? (activeBadge?.kind ?? "styled") : undefined,
      deliveryPath,
    });
  };

  const handleDeleteImage = (productId: string, contentId: string) => {
    const isScene = multiProductScenes.some((s) => s.id === productId);
    if (isScene) {
      const scene = multiProductScenes.find((s) => s.id === productId);
      const contentToDelete = scene?.generatedContent.find(
        (c) => c.id === contentId
      );
      setMultiProductScenes((prev) =>
        prev.map((s) => {
          if (s.id !== productId) return s;
          return {
            ...s,
            generatedContent: s.generatedContent.filter(
              (c) => c.id !== contentId
            ),
          };
        })
      );
      deleteGeneratedImage(
        contentId,
        contentToDelete?.storagePath || contentToDelete?.image
      ).catch(console.error);
    } else {
      const product = studioProducts.find((p) => p.id === productId);
      if (!product) return;

      const contentToDelete = product.generatedContent.find(
        (c) => c.id === contentId
      );
      if (!contentToDelete) return;

      // Check if the deleted content is a background-removed PNG
      const isBackgroundRemoved =
        contentToDelete.tags?.includes("No Background") ||
        contentToDelete.tags?.includes("Transparent") ||
        contentToDelete.imageType === "Background Removal";

      // Check if the current product.image matches the deleted content
      // Match by filename, storagePath, or by comparing base64 data (if content.image is a data URL)
      const imageMatchesDeletedContent =
        (contentToDelete.filename &&
          product.image.name === contentToDelete.filename) ||
        (contentToDelete.storagePath &&
          product.image.storagePath &&
          contentToDelete.storagePath === product.image.storagePath);

      // Filter out the deleted content
      const updatedGeneratedContent = product.generatedContent.filter(
        (c) => c.id !== contentId
      );

      // Check if any background-removed PNGs remain
      const hasRemainingBackgroundRemoved = updatedGeneratedContent.some(
        (c) =>
          c.tags?.includes("No Background") ||
          c.tags?.includes("Transparent") ||
          c.imageType === "Background Removal"
      );

      // Check if current product.image is a background-removed PNG
      const currentImageIsBackgroundRemoved =
        product.image.mimeType?.toLowerCase().includes("image/png") &&
        (product.image.name?.toLowerCase().includes("no_bg") ||
          product.image.name?.toLowerCase().includes("nobg") ||
          product.image.name?.toLowerCase().endsWith("_nobg.png"));

      // If we deleted a background-removed PNG that matches the current image,
      // OR if current image is a background-removed PNG and all are deleted,
      // revert to original image
      const shouldRevertToOriginal =
        !hasRemainingBackgroundRemoved &&
        ((isBackgroundRemoved && imageMatchesDeletedContent) ||
          currentImageIsBackgroundRemoved);

      setStudioProducts((prev) =>
        prev.map((p) => {
          if (p.id !== productId) return p;
          return {
            ...p,
            generatedContent: updatedGeneratedContent,
            ...(shouldRevertToOriginal ? { image: p.originalImage } : {}),
          };
        })
      );

      deleteGeneratedImage(
        contentId,
        contentToDelete.storagePath || contentToDelete.image
      ).catch(console.error);
    }
  };

  // Closing the viewer, whether from its own close control or from the crash
  // fallback that replaces it. The in-viewer background-removal flags must be
  // cleared on every way out: they are passed to the next viewer opened, and a
  // stale `true` would leave that action spinning and disabled for the rest
  // of the session. (product.isProcessing is deliberately left alone while a
  // removal is still running, so the indicator keeps showing on the card.)
  const handleCloseViewer = () => {
    setActiveViewer(null);
    setIsRemovingBackgroundInViewer(false);
    setBackgroundRemovalProgress(null);
  };

  const handleGalleryNavigation = (direction: "next" | "prev") => {
    if (!activeGallery) return;
    const product = studioProducts.find(
      (p) => p.id === activeGallery.productId
    );
    const images = getVisibleGalleryImages(activeGallery.productId);
    const minIndex = product ? -1 : 0;
    const currentIndex =
      activeGallery.currentContentId === null
        ? minIndex
        : images.findIndex(
            (content) => content.id === activeGallery.currentContentId
          );
    if (currentIndex < minIndex) return;

    let newIndex = currentIndex;
    const step = direction === "next" ? 1 : -1;
    let candidate = newIndex + step;
    if (candidate >= minIndex && candidate < images.length) {
      newIndex = candidate;
    }

    if (newIndex !== currentIndex) {
      if (newIndex === -1 && product) {
        const content = getSourceGalleryContent(product);
        setActiveViewer({
          content,
          productId: activeGallery.productId,
          currentContentId: null,
        });
      } else {
        setActiveViewer({
          content: images[newIndex],
          productId: activeGallery.productId,
          currentContentId: images[newIndex]?.id ?? null,
        });
      }
    }
  };

  const handleGalleryDelete = (contentId: string) => {
    if (!activeGallery) return;
    const product = studioProducts.find(
      (p) => p.id === activeGallery.productId
    );
    const images = getVisibleGalleryImages(activeGallery.productId);

    // If we can't resolve the list, fall back to the previous behavior.
    if (images.length === 0 && !product) {
      handleDeleteImage(activeGallery.productId, contentId);
      setActiveViewer(null);
      return;
    }

    const currentIndex =
      activeGallery.currentContentId === null
        ? -1
        : images.findIndex(
            (content) => content.id === activeGallery.currentContentId
          );
    const total = images.length;

    // Decide where to navigate *after* deletion.
    // - If we delete an item that is not the last one: show the next item, keeping the same index (items shift left).
    // - If we delete the last item: show the previous item.
    // - If it was the only item: show source (if available) or close the viewer.
    let nextIndex: number | null = null;
    let nextContent: GeneratedContent | null = null;

    if (total <= 1) {
      if (product) {
        nextIndex = -1;
        nextContent = getSourceGalleryContent(product);
      } else {
        nextIndex = null;
        nextContent = null;
      }
    } else if (Number.isFinite(currentIndex) && currentIndex < total - 1) {
      nextIndex = Math.max(0, currentIndex);
      nextContent = images[currentIndex + 1] ?? images[total - 1] ?? null;
    } else {
      nextIndex = Math.max(0, Math.min(total - 2, currentIndex - 1));
      nextContent = images[nextIndex] ?? images[0] ?? null;
    }

    // Perform deletion in state.
    handleDeleteImage(activeGallery.productId, contentId);

    // Navigate within viewer instead of closing.
    if (nextIndex == null || nextContent == null) {
      setActiveViewer(null);
      return;
    }

    setActiveViewer({
      content: nextContent,
      productId: activeGallery.productId,
      currentContentId: nextIndex === -1 ? null : nextContent.id,
    });
  };

  const handleRegenerate = async (
    content: GeneratedContent,
    quality?: "1K" | "2K" | "4K",
    additionalContext?: string,
    // When set, the regenerate dialog pre-filled its field with this image's
    // own Custom Prompt and let the user edit it, so `additionalContext` is the
    // whole context to use and replaces the stored one instead of appending.
    // Only the generation path (never an Edit result) sends this.
    replaceContext?: boolean,
    // A Custom Edit's re-edited change list, rebuilt from the dialog's editable
    // rows. Replaces the source edit's `changes`, `referenceImages` and display
    // prompt — see RegenerateEditOverride for the marking/insert semantics.
    editOverride?: RegenerateEditOverride
  ) => {
    if (!activeGallery || !content.image) return;
    if (content.generationSettings?.roomStaging) return; // Room staging images cannot be re-generated
    if (isBackgroundRemovalGeneratedContent(content)) return;
    setIsRegeneratingInViewer(true);

    try {
      // Custom Edit: re-run editImageWithAnnotation with saved task (input image, mask, prompt, etc.)
      const editTask = content.generationSettings?.editTask;
      if (
        content.imageType === "Edit" &&
        content.generationSettings?.imageType === "Edit" &&
        !editTask
      ) {
        toast.error(t("studio.errorRegenerateEditNoTask"));
        setIsRegeneratingInViewer(false);
        return;
      }
      if (editTask) {
        // `prompt` joins the surviving row texts, so an empty prompt means no
        // instruction survived — whichever wire shape (changes / passthrough)
        // the override carries.
        if (editOverride && !editOverride.prompt.trim()) {
          toast.error(t("studio.editorErrorDescribeRequired"));
          setIsRegeneratingInViewer(false);
          return;
        }

        let { mask: maskToUse, annotation: annotationToUse } =
          selectEditMarkingAssets(
            editTask,
            !editOverride || editOverride.keepMarking
          );
        // The dialog's pre-flight filters a kept marking down to the assets
        // whose objects still resolve — the backend needs only one of the two.
        if (editOverride) {
          if (!editOverride.sendMask) maskToUse = undefined;
          if (!editOverride.sendAnnotation) annotationToUse = undefined;
        }
        // An annotation that never got a storage path cannot travel
        // (geminiService forwards paths only) — degrade to the mask, which is
        // exactly the backend's fallback branch, rather than refusing a
        // regeneration that used to work before annotations existed.
        if (annotationToUse && !annotationToUse.storagePath) {
          annotationToUse = undefined;
        }
        // Guard: stripped legacy edits without storagePaths can't regenerate
        if (
          !editTask.baseImage ||
          editTask.baseImage === "" ||
          (maskToUse && !maskToUse.storagePath && !maskToUse.base64)
        ) {
          toast.error(t("studio.regenerateDisabledLegacy"));
          setIsRegeneratingInViewer(false);
          return;
        }

        const trimmedAdditionalContext = additionalContext?.trim();
        // A re-edited change list (Custom Edit) replaces the source edit's
        // changes, references and prompt wholesale. Otherwise the dialog's
        // optional extra context is appended — to the change list when there is
        // one (the backend ignores `prompt` while `changes` is present), else
        // to the prompt.
        const editChangesRaw = editOverride
          ? editOverride.changes
          : editTask.changes?.length && trimmedAdditionalContext
            ? [...editTask.changes, { text: trimmedAdditionalContext }]
            : editTask.changes;
        const editChanges =
          editChangesRaw && editChangesRaw.length > 0
            ? editChangesRaw
            : undefined;
        const editPrompt = editOverride
          ? editOverride.prompt
          : trimmedAdditionalContext
            ? `${editTask.prompt}\n\n${trimmedAdditionalContext}`
            : editTask.prompt;
        const referenceImagesToUse = editOverride
          ? editOverride.referenceImages
          : editTask.referenceImages;
        // A stale insert image whose object is gone is dropped rather than
        // sent as a dead path — a Custom Edit never needs one (it rides along
        // from an old Add/Swap mode switch).
        const insertImageToUse = editOverride?.dropInsert
          ? undefined
          : editTask.insertImage;
        if (
          editOverride &&
          referenceImagesToUse?.some((detail) => !detail.image.storagePath)
        ) {
          toast.error(t("studio.regenerateDisabledLegacy"));
          setIsRegeneratingInViewer(false);
          return;
        }
        // What the details panel shows for the re-generated image. `editPrompt`
        // is `editTask.prompt`, the assembled directive the model needs — for
        // an Add or a Swap that is our wording, not the user's, so it must not
        // be persisted into the displayed field (SIS-516). The source's own
        // display text is resolved rather than copied raw: a legacy row still
        // stores the assembled directive there, and carrying that forward
        // verbatim would write our wording into a brand-new record. On a replace
        // the edited text *is* the user's words, so it becomes the display text.
        const sourceDisplayPrompt = resolveEditDetailsDisplay({
          imageType:
            typeof content.generationSettings?.imageType === "string"
              ? content.generationSettings.imageType
              : content.imageType,
          prompt:
            typeof content.generationSettings?.prompt === "string"
              ? content.generationSettings.prompt
              : undefined,
          promptSource:
            typeof content.generationSettings?.promptSource === "string"
              ? content.generationSettings.promptSource
              : undefined,
          editTask,
        }).displayPrompt;
        const displayPrompt = editOverride
          ? editOverride.prompt
          : [sourceDisplayPrompt, trimmedAdditionalContext]
              .filter(Boolean)
              .join("\n\n");
        let baseImageFile: ImageFile;
        if (editTask.baseImage.startsWith("data:")) {
          // Legacy: data URL stored directly
          baseImageFile = {
            base64: editTask.baseImage.split(",")[1],
            mimeType:
              editTask.baseImage.match(/data:(.*);base64,/)?.[1] || "image/png",
            name: "base.png",
          };
        } else {
          // New: storagePath stored — load base64 on demand
          const base64 = await loadFullImageBase64(editTask.baseImage);
          baseImageFile = {
            base64,
            mimeType: "image/png",
            name: "base.png",
            storagePath: editTask.baseImage,
          };
        }
        const productId = activeGallery.productId;

        // A re-generation is a sibling of its source, not a replacement — it
        // takes the source's base name uniquified against the gallery
        // (photo_Edit_2.png), never the identical string, which a bulk
        // download would overwrite. A pre-SIS-517 row whose stored settings
        // carry no name shows the loader's placeholder as `content.title`;
        // persisting that would turn a display fallback into stored data, so
        // those are named fresh from the template instead.
        const regenSourceTitle =
          content.title &&
          content.title !== FALLBACK_IMAGE_TITLE &&
          content.title !== FALLBACK_VIDEO_TITLE
            ? content.title
            : undefined;
        const regenTitle = regenSourceTitle
          ? uniquifyFilename(
              regenSourceTitle,
              resolveViewerExistingTitles(productId)
            )
          : nameViewerQuickAction(productId, "Edit");

        // The task the new row carries describes the request that produced
        // it, additional context included — so re-generating *that* row
        // repeats its own operation instead of silently dropping the context,
        // and the displayed prompt above never claims text the model was not
        // sent.
        const regeneratedEditTask = {
          baseImage: editTask.baseImage,
          mask: maskToUse,
          annotation: annotationToUse,
          changes: editChanges,
          prompt: editPrompt,
          insertImage: insertImageToUse,
          referenceImages: referenceImagesToUse,
          aspectRatio: editTask.aspectRatio,
          useFlashModel: editTask.useFlashModel,
          editorMode: editTask.editorMode,
        };

        setGenerationContext(
          projectId,
          productId,
          "Edit",
          serializeImageGenerationSettings(
            content.generationSettings?.imageGeneration ||
              content.fullSettings?.imageGeneration,
            {
              imageType: "Edit",
              prompt: displayPrompt,
              promptSource: "user",
              // Without the name in the context the backend-written row reloads
              // as "Generated Image" (SIS-517).
              title: regenTitle,
              aspectRatio: editTask.aspectRatio,
              editTask: regeneratedEditTask,
            }
          )
        );

        const result = await geminiService.editImageWithAnnotation(
          baseImageFile,
          maskToUse,
          editPrompt,
          insertImageToUse ?? null,
          editTask.aspectRatio,
          quality ?? "1K",
          referenceImagesToUse,
          editTask.useFlashModel,
          annotationToUse,
          editChanges,
          undefined,
          undefined,
          // Edits saved before the modes had their own temperatures carry no
          // mode, and re-generate on the provider default — which is what all
          // of them ran at when those edits were made.
          editTask.editorMode
            ? getTemperatureForImageEditorMode(editTask.editorMode)
            : undefined
        );

        const resultWithId: GeneratedContent = {
          ...result,
          id: result.dbPersisted
            ? (result.id ?? crypto.randomUUID())
            : crypto.randomUUID(),
          title: regenTitle,
          imageType: "Edit",
          timestamp: Date.now(),
          generationSettings: mergeGeneratedImageSettings(
            result.generationSettings,
            {
              ...content.generationSettings,
              // Same text and name the persisted record above carries, so the
              // live view and the one after a reload say the same thing. The
              // marker is set explicitly: a legacy source has none to spread,
              // and the resolved displayPrompt is user-format regardless.
              prompt: displayPrompt,
              promptSource: "user" as const,
              title: regenTitle,
              // Propagate so the new image can also be regenerated — with the
              // applied context absorbed, same as the record above.
              editTask: regeneratedEditTask,
            }
          ),
        };

        const isScene = multiProductScenes.some((s) => s.id === productId);

        const uploaded = resultWithId.dbPersisted
          ? resultWithId
          : await uploadGeneratedImage(
              projectId,
              productId,
              resultWithId
            ).catch(() => resultWithId);

        if (isScene) {
          setMultiProductScenes((prev) =>
            prev.map((s) => {
              if (s.id !== productId) return s;
              return {
                ...s,
                generatedContent: [uploaded, ...s.generatedContent],
              };
            })
          );
        } else {
          setStudioProducts((prev) =>
            prev.map((p) => {
              if (p.id !== productId) return p;
              return {
                ...p,
                generatedContent: [uploaded, ...p.generatedContent],
              };
            })
          );
        }

        if (!uploaded.dbPersisted) {
          const oldId = uploaded.id;
          persistGeneratedContent(projectId, productId, uploaded)
            .then((dbId) => syncContentDbId(productId, oldId, dbId))
            .catch((e) =>
              console.error("Failed to persist regenerated edit content:", e)
            );
        }

        updateViewerContent(uploaded);
        setIsRegeneratingInViewer(false);
        return;
      }

      // Reconstruct settings from content
      // We prioritize fullSettings if available (which stores the exact state at generation time)
      // otherwise fallback to generationSettings partials
      const savedSettings = (content.fullSettings ||
        {}) as Partial<GenerationSettings>;
      const generationSettings = content.generationSettings || {};

      // Merge to create a complete GenerationSettings object
      // We default to current studio state for missing values if necessary,
      // but ideally we should respect what was saved.

      const settingsToUse: GenerationSettings = {
        // Default base
        vibe: studioVibe,
        scene: studioScene,
        // imageType and aspectRatio handled below
        vibeContext: studioVibeContext,
        profileId: studioSelectedProfileId,
        decorationSurfaceOverride: studioDecorationSurfaceOverride,
        decorationFramingPreference: studioDecorationFramingPreference,
        decorationRoomPlacementPreference:
          studioDecorationRoomPlacementPreference,
        decorationViewAngle: studioDecorationViewAngle,
        colorSwatches: colorSwatches,
        rotationAngle: studioRotationAngle,
        rotationMode: studioRotationMode,
        useAvatar: useAvatar,
        avatarImage: studioAvatar,
        highlightsConfigId: studioHighlightsConfigId,
        highlightsInputs: studioHighlightsInputs,
        technicalDrawingMode: studioTechnicalDrawingMode,
        technicalDrawingMaterialImage: studioTechnicalDrawingMaterialImage,
        technicalDrawingMaterial: studioTechnicalDrawingMaterial,
        detailsFocus: studioDetailsFocus,
        detailsMode: studioDetailsMode,
        detailsCameraPerspective: studioDetailsCameraPerspective,
        detailsMaterial: studioDetailsMaterial,
        detailsSceneDescription: studioDetailsSceneDescription,
        cutoutShadows: studioCutoutShadows,
        backgroundRemoval: studioBackgroundRemoval,
        productLightsOn,
        productLightsWarmth,
        styleConfig: {
          softLighting,
          neverHideParts,
          alwaysCenter,
          desiredRoomStyle,
        },

        // The controls this image was actually generated with, rebuilt from
        // its persisted settings (SIS-502). Sits between the live-state
        // defaults and fullSettings so an in-session regeneration still uses
        // the exact snapshot, while a post-reload one stops inheriting
        // whatever the panel happens to show now. The live vibe/scene lists
        // are passed so a stored id a brand save has rotated away is
        // re-matched by label instead of reaching the model as a UUID.
        ...restoreRegenerationSettings(generationSettings, {
          vibes,
          scenes,
          liveHighlightsConfigId: studioHighlightsConfigId,
        }),

        // Overrides from saved full settings (if any)
        ...savedSettings,
        imageGeneration:
          generationSettings.imageGeneration ?? savedSettings.imageGeneration,

        // Overrides from generation settings (specific params)
        imageType:
          (generationSettings.imageType as StudioImageType) ||
          savedSettings.imageType ||
          studioImageType,
        aspectRatio:
          generationSettings.aspectRatio ||
          savedSettings.aspectRatio ||
          studioAspectRatio,
        resolution:
          quality ??
          (generationSettings.resolution as "1K" | "2K" | "4K") ??
          savedSettings.resolution ??
          "1K",
      };

      if (settingsToUse.imageGeneration && quality)
        settingsToUse.imageGeneration = {
          ...settingsToUse.imageGeneration,
          resolution: quality,
        };

      // Specific overrides based on type if not fully captured in fullSettings
      if (generationSettings.recolorColor) {
        settingsToUse.imageType = "Re-Coloring";
        settingsToUse.recolorColor = generationSettings.recolorColor;
        const savedRecolorSwatch = getSavedRecolorSwatch(
          generationSettings.recolorColor,
          savedSettings,
          generationSettings
        );
        settingsToUse.colorSwatches = savedRecolorSwatch
          ? [savedRecolorSwatch]
          : [];
      }

      if (generationSettings.rotationAngle) {
        settingsToUse.imageType = "Rotation";
        settingsToUse.rotationAngle = generationSettings.rotationAngle;
        if (generationSettings.rotationMode) {
          settingsToUse.rotationMode = generationSettings.rotationMode;
        }
        if (generationSettings.rotationModel) {
          settingsToUse.rotationModel = generationSettings.rotationModel;
        }
        if (generationSettings.rotationQuality) {
          settingsToUse.rotationQuality = generationSettings.rotationQuality;
        }
      }

      // Presence, not truthiness: a stored empty context means the image was
      // generated with a blank Custom Context, and must stay blank rather than
      // being refilled from the live textarea after a reload.
      if (typeof generationSettings.context === "string") {
        settingsToUse.vibeContext = generationSettings.context;
      } else if (
        // SIS-500: for modes whose UI hides the custom-context field, only a
        // context stored with the content (or same-session fullSettings) may
        // apply on regeneration — never the live studio textarea that seeded
        // the defaults above.
        hidesCustomContext(settingsToUse.imageType, {
          detailsMode: settingsToUse.detailsMode,
          technicalDrawingMode: settingsToUse.technicalDrawingMode,
        }) &&
        savedSettings.vibeContext === undefined
      ) {
        settingsToUse.vibeContext = "";
      }
      const isRecolorRegeneration = settingsToUse.imageType === "Re-Coloring";
      if (replaceContext) {
        // The field carried this image's own Custom Prompt, edited in place, so
        // it is the whole context now — replace rather than append. An empty
        // value is a deliberate clear and must win over the stored context.
        if (isRecolorRegeneration) {
          settingsToUse.recolorAdditionalContext =
            additionalContext?.trim() ?? "";
        } else {
          settingsToUse.vibeContext = additionalContext?.trim() ?? "";
        }
      } else if (additionalContext?.trim()) {
        if (isRecolorRegeneration) {
          const base = (settingsToUse.recolorAdditionalContext || "").trim();
          settingsToUse.recolorAdditionalContext = base
            ? `${base}\n\n${additionalContext.trim()}`
            : additionalContext.trim();
        } else {
          const base = (settingsToUse.vibeContext || "").trim();
          settingsToUse.vibeContext = base
            ? `${base}\n\n${additionalContext.trim()}`
            : additionalContext.trim();
        }
      }

      // For Cutout, restore shadow-sm settings from saved fullSettings or generationSettings
      // so re-generating preserves shadows even when fullSettings wasn't persisted (e.g. after load)
      if (content.imageType === "Cutout") {
        const savedCutoutShadows =
          savedSettings.cutoutShadows ??
          (generationSettings as Partial<GenerationSettings>).cutoutShadows;
        if (savedCutoutShadows != null) {
          settingsToUse.cutoutShadows = savedCutoutShadows;
        }
      }

      // For Transform Technical Drawing (cutout mode), restore shadow-sm style
      // Prefer generationSettings.imageType because many entries do not set top-level content.imageType.
      const effectiveImageType =
        (generationSettings.imageType as string) || content.imageType;
      if (effectiveImageType === "Transform Technical Drawing") {
        const savedTdShadowStyle =
          savedSettings.technicalDrawingShadowStyle ??
          (generationSettings as Partial<GenerationSettings>)
            .technicalDrawingShadowStyle;
        if (savedTdShadowStyle != null) {
          settingsToUse.technicalDrawingShadowStyle = savedTdShadowStyle;
        }
      }

      // Call the main generation function
      await handleGenerateForSinglePlant(
        activeGallery.productId,
        (settingsToUse.resolution as "1K" | "2K" | "4K") || "1K",
        settingsToUse
      );

      // Note: handleGenerateForSinglePlant updates the product state and persistence automatically.
      // We might want to update viewedContent to the newest result here, but handleGenerateForSinglePlant
      // is async and pushes to the array.
      // We can find the latest result after it finishes.

      const product = studioProducts.find(
        (p) => p.id === activeGallery.productId
      );
      if (product && product.generatedContent.length > 0) {
        // The new image is prepended to the list in handleGenerateForSinglePlant (results + prev)
        // But wait, setStudioProducts uses a functional update there, so 'product' here is stale.
        // We can't easily get the *exact* new object without refetching or using the updated state.
        // However, since we are in the parent component, we can access the state directly *after* the update propagates?
        // No, React state updates are batched/async.
        // The newly generated item is not directly available here because the state
        // update happened in a separate handler, so we leave the current selection as-is.
      }
    } catch (err) {
      console.error(err);
      toast.error(resolveErrorMessage(err, t));
    } finally {
      setIsRegeneratingInViewer(false);
    }
  };

  const handleStartEdit = (
    content: GeneratedContent,
    mode: "expand" | "swap" | "manual" | "inpaint" = "expand"
  ) => {
    if (activeGallery) {
      setEditingContent({
        content,
        productId: activeGallery.productId,
        initialMode: mode,
        isSourceImage: activeGallery.currentContentId === null,
      });
    }
  };

  const handleCancelEdit = () => setEditingContent(null);

  const applyImageEdit = async (
    target: EditTarget,
    payload: ImageEditorApplyPayload
  ): Promise<boolean> => {
    const {
      mask,
      prompt,
      insertImage,
      quality,
      referenceImages,
      aspectRatio,
      baseImageOverride,
      useFlashModel,
      imageGeneration,
      annotationImage,
      changes,
      userInstruction,
      // The mode that was active when Generate ran — not target.initialMode,
      // the mode the editor was *opened* in: the full-screen editor's tab bar
      // is live, and persisting the opening mode mislabelled the result and
      // sent the wrong sampling temperature after a switch.
      editMode,
    } = payload;
    if (!target.content.image) return false;
    setIsApplyingEdit(true);
    const editImageType =
      editMode === "inpaint"
        ? "Milieu Fixed"
        : EDIT_IMAGE_TYPE_BY_EDITOR_MODE[editMode];
    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(target.productId),
      imageType: editImageType,
      productId: target.productId,
    });
    try {
      let baseImageWidth = baseImageOverride?.width ?? 0;
      let baseImageHeight = baseImageOverride?.height ?? 0;

      if (!baseImageWidth || !baseImageHeight) {
        // Prefer the original storage object over the viewer thumbnail so mask
        // resizing is based on the exact asset sent to the backend.
        const fallbackImageUrl =
          target.content.fullUrl ||
          target.content.thumbnailUrl ||
          target.content.image;
        let measurementImageUrl = fallbackImageUrl;

        if (!target.content.fullUrl && target.content.storagePath) {
          try {
            measurementImageUrl = await getUrlFromPath(
              target.content.storagePath
            );
          } catch {
            measurementImageUrl = fallbackImageUrl;
          }
        }

        if (!measurementImageUrl) {
          throw new Error("Failed to resolve base image for edit sizing.");
        }

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = measurementImageUrl;
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () =>
            reject(new Error("Failed to load base image for edit sizing."));
        });

        baseImageWidth = img.naturalWidth;
        baseImageHeight = img.naturalHeight;
      }

      const resizeBinaryMaskToBaseImage = async (maskImage: ImageFile) => {
        // Fast (flash) model appears more sensitive to pixel-perfect mask sizing.
        // Our editor mask is generated against the display image; re-size + re-binarize
        // so it matches the exact backend base image dimensions.
        const baseW = baseImageWidth;
        const baseH = baseImageHeight;

        if (!baseW || !baseH) return maskImage;

        const maskB64 = (maskImage.base64 || "").trim();
        if (!maskB64) return maskImage;

        const mime = maskImage.mimeType || "image/png";
        const maskDataUrl = maskB64.startsWith("data:")
          ? maskB64
          : `data:${mime};base64,${maskB64}`;

        const maskImg = new Image();
        maskImg.crossOrigin = "anonymous";
        maskImg.src = maskDataUrl;
        await new Promise<void>((resolve, reject) => {
          maskImg.onload = () => resolve();
          maskImg.onerror = () =>
            reject(new Error("Failed to load mask image for resizing."));
        });

        const canvas = document.createElement("canvas");
        canvas.width = baseW;
        canvas.height = baseH;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return maskImage;

        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, baseW, baseH);
        ctx.drawImage(maskImg, 0, 0, baseW, baseH);

        const imageData = ctx.getImageData(0, 0, baseW, baseH);
        const data = imageData.data;
        // Threshold to enforce a strict binary mask:
        // R > 128 => white (edit), else black (preserve); alpha forced opaque.
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const v = r > 128 ? 255 : 0;
          data[i] = v;
          data[i + 1] = v;
          data[i + 2] = v;
          data[i + 3] = 255;
        }
        ctx.putImageData(imageData, 0, 0);

        const resizedB64 = canvas.toDataURL("image/png").split(",")[1];
        if (!resizedB64) return maskImage;

        return {
          ...maskImage,
          base64: resizedB64,
          mimeType: "image/png",
          name: maskImage.name || "mask.png",
        };
      };

      const targetRatio =
        aspectRatio ?? getClosestAspectRatio(baseImageWidth, baseImageHeight);

      const baseImageFile: ImageFile = baseImageOverride ?? {
        storagePath: target.content.storagePath,
        thumbnailUrl: target.content.thumbnailUrl,
        fullUrl: target.content.fullUrl,
        base64: "",
        mimeType: "image/png",
        name: "base.png",
      };

      const isScene = multiProductScenes.some((s) => s.id === target.productId);

      // Get the product or scene being edited to determine original name and count existing edits
      let originalName = "image";
      let existingGeneratedContent: GeneratedContent[] = [];
      let contextInfo = "";

      if (isScene) {
        const scene = multiProductScenes.find((s) => s.id === target.productId);
        if (scene) {
          originalName = scene.name;
          existingGeneratedContent = scene.generatedContent;
        }
      } else {
        const product = studioProducts.find((p) => p.id === target.productId);
        if (product) {
          originalName =
            product.originalImage.name.split(".").slice(0, -1).join(".") ||
            product.originalImage.name;
          existingGeneratedContent = product.generatedContent;
          contextInfo = [product.productCategory, product.productDescription]
            .filter(Boolean)
            .join(". ");
        }
      }

      const isModeInpaint = editMode === "inpaint";

      // The three Gemini editor modes share `edit_annotation`, so the backend
      // has no way to tell them apart — each sends its own temperature or gets
      // the provider default. Milieu Fixed is excluded because it does not
      // come through here at all; it runs on Bria.
      const editorTemperature = isModeInpaint
        ? undefined
        : getTemperatureForImageEditorMode(editMode);

      let maskToUse = mask;
      if (useFlashModel && mask && !mask.storagePath && mask.base64?.trim()) {
        maskToUse = await resizeBinaryMaskToBaseImage(mask);
      }

      // Upload ephemeral edit images to GCS so only storagePaths are sent
      // to the backend and persisted in generationSettings.
      const editRefId = crypto.randomUUID();
      const uploadEditImage = (fieldName: string, image: ImageFile) =>
        persistGeneratedSettingsImage(
          projectId,
          target.productId,
          editRefId,
          fieldName,
          image
        );

      if (maskToUse && !maskToUse.storagePath) {
        maskToUse = await uploadEditImage("edit_mask", maskToUse);
      }
      // Read once: a toggle flipped mid-generation must not leave the request
      // asking for an echo that the report never collects, or vice versa.
      const wantsDebugReport = DEBUG_TOOLS_ENABLED && isCustomEditDebugArmed();

      let annotationToUse = annotationImage;
      if (annotationImage && !annotationImage.storagePath) {
        annotationToUse = await uploadEditImage(
          "edit_annotation",
          annotationImage
        );
      }
      let uploadedInsertImage = insertImage;
      if (insertImage && !insertImage.storagePath) {
        uploadedInsertImage = await uploadEditImage("edit_insert", insertImage);
      }
      let uploadedReferenceImages = referenceImages;
      if (referenceImages?.length) {
        uploadedReferenceImages = await Promise.all(
          referenceImages.map(async (detail, i) => {
            if (!detail.image || detail.image.storagePath) return detail;
            const uploaded = await uploadEditImage(
              `edit_ref_${i}`,
              detail.image
            );
            return { ...detail, image: uploaded };
          })
        );
      }

      let finalPrompt = prompt;
      if (isModeInpaint) {
        finalPrompt = applyInpaintPromptRequirements(finalPrompt, {
          softLighting,
          desiredRoomStyle,
        });
      }
      if (isModeInpaint && contextInfo) {
        finalPrompt += `\n\nProduct Context (Keep this in mind to make the background match the product and do not modify the product itself): ${contextInfo}`;
      }

      // What the details panel gets to show. `prompt` is only the user's own
      // words in the modes that send no `userInstruction` — Add and Swap
      // assemble a directive around their one field, and Milieu Fixed appends
      // a Re-Light block, so for those the argument is our prompt engineering
      // and printing it back to the user is the SIS-516 leak.
      const displayPrompt = userAuthoredPrompt(userInstruction, prompt);
      // Which editor mode produced this edit. Read by the details panel to
      // label the result, and by re-generation to restore the temperature.
      const persistedEditorMode = isModeInpaint ? undefined : editMode;
      // Named before the call — the backend writes the DB row from the context
      // below, so a title added to the result afterwards never reaches it and
      // the edit reloads as "Generated Image" (SIS-517).
      const filename = generateUniqueFilename(
        namingTemplate,
        {
          originalName,
          type: isModeInpaint ? "Inpaint" : "Edit",
        },
        existingGeneratedContent.map((c) => c.title)
      );

      if (!isModeInpaint) {
        setGenerationContext(
          projectId,
          target.productId,
          "Edit",
          serializeImageGenerationSettings(imageGeneration, {
            imageType: "Edit",
            // Persist the raw user prompt so image details show what the
            // user typed. editTask.prompt keeps the augmented finalPrompt
            // because re-generation needs it.
            prompt: displayPrompt,
            // The marker the details panel keys the two prompt formats apart
            // by — rows without it may hold an assembled directive (SIS-516).
            promptSource: "user",
            title: filename,
            aspectRatio: targetRatio,
            editTask: {
              baseImage: target.content.storagePath || "",
              mask: maskToUse,
              annotation: annotationToUse,
              changes,
              prompt: finalPrompt,
              insertImage: uploadedInsertImage ?? null,
              referenceImages: uploadedReferenceImages ?? undefined,
              aspectRatio: targetRatio,
              useFlashModel,
              // So a re-generation samples the way the original did. Without
              // it a Custom Edit made at 1.0 would come back at the provider
              // default, and the two would not be the same operation.
              editorMode: persistedEditorMode,
            },
          })
        );
      }
      const result = isModeInpaint
        ? await executeBriaProductShotGeneration(
            baseImageFile.storagePath
              ? { storagePath: baseImageFile.storagePath }
              : { base64Image: baseImageFile.base64 },
            finalPrompt,
            undefined,
            undefined,
            projectId,
            target.productId
          )
        : await geminiService.editImageWithAnnotation(
            baseImageFile,
            maskToUse,
            finalPrompt,
            uploadedInsertImage,
            targetRatio,
            quality,
            uploadedReferenceImages,
            useFlashModel,
            annotationToUse,
            changes,
            wantsDebugReport,
            undefined,
            editorTemperature
          );

      // Dev/staging-only: one HTML file with the inputs, the callable request,
      // the prompt the backend assembled, and the result. Failures here must
      // never cost the user their generation.
      if (wantsDebugReport && !isModeInpaint) {
        try {
          await downloadCustomEditDebugReport({
            editMode,
            projectId,
            productId: target.productId,
            prompt: finalPrompt,
            changes,
            aspectRatio: targetRatio,
            quality,
            useFlashModel,
            baseImage: baseImageFile,
            annotationImage: annotationToUse,
            maskImage: maskToUse,
            insertImage: uploadedInsertImage,
            referenceImages: uploadedReferenceImages,
            // The request the service actually sent, not a rebuild of it. A
            // rebuild here cannot see the project/product context the service
            // injects, nor the debugRequest flag it sets — so a section
            // labelled "callable request" would always be slightly untrue.
            request: result.debugCallable ?? {
              mode: "edit_annotation",
              images: {
                original: baseImageFile.storagePath,
                annotated: annotationToUse?.storagePath,
                mask: maskToUse?.storagePath,
                insert: uploadedInsertImage?.storagePath,
                references: (uploadedReferenceImages || [])
                  .map((detail) => detail.image?.storagePath)
                  .filter(Boolean),
              },
              params: {
                prompt: finalPrompt,
                changes,
                aspectRatio: targetRatio,
                imageSize: quality ?? "1K",
                ...(useFlashModel ? { model: "gemini-nano-banana-2.1" } : {}),
              },
            },
            echo: result.debugRequest,
            resultImage: result.storagePath
              ? {
                  base64: "",
                  mimeType: "image/png",
                  name: "result.png",
                  storagePath: result.storagePath,
                  thumbnailUrl: result.thumbnailUrl,
                  fullUrl: result.fullUrl,
                }
              : null,
          });
        } catch (debugError) {
          // Never surfaced: the report is a diagnostic and must not cost the
          // user the generation they already paid for. Reported anyway, or a
          // report that silently stops being produced on staging looks like
          // nobody armed the checkbox.
          console.error("Custom Edit debug report failed", debugError);
          reportError(debugError, { context: "customEditDebugReport" });
        }
      }

      const resultWithId: GeneratedContent = {
        ...result,
        id: result.dbPersisted
          ? (result.id ?? crypto.randomUUID())
          : crypto.randomUUID(),
        title: filename,
        imageType: isModeInpaint ? "Inpaint" : "Edit",
        timestamp: Date.now(),
        generationSettings: mergeGeneratedImageSettings(
          result.generationSettings,
          {
            imageGeneration,
            imageType: isModeInpaint ? "Inpaint" : "Edit",
            model: useFlashModel
              ? "gemini-nano-banana-2.1"
              : "gemini-3-pro-image",
            // Save the user-typed prompt for display in image details. The
            // augmented `finalPrompt` (with system directives) is preserved in
            // editTask.prompt below so re-generation still works correctly.
            prompt: displayPrompt,
            promptSource: "user",
            title: filename, // Include title in settings so it persists and loads correctly
            // Save edit task so re-generation uses correct input and edit params
            editTask: {
              baseImage: target.content.storagePath || "",
              mask: maskToUse ?? undefined,
              annotation: annotationToUse ?? undefined,
              changes,
              prompt: finalPrompt,
              insertImage: uploadedInsertImage ?? null,
              referenceImages: uploadedReferenceImages ?? undefined,
              aspectRatio: targetRatio,
              useFlashModel,
              // Was missing here while the transient generation context above
              // carried it, so the in-memory result — and every row persisted
              // from it — looked like an edit from before the modes had their
              // own temperatures. Re-generating one fell back to the provider
              // default, and the details panel had no way to tell Add from Swap
              // from Custom Edit.
              editorMode: persistedEditorMode,
            },
          }
        ),
      };

      const uploaded = resultWithId.dbPersisted
        ? resultWithId
        : await uploadGeneratedImage(
            projectId,
            target.productId,
            resultWithId
          ).catch(() => resultWithId);

      if (isScene) {
        setMultiProductScenes((prev) =>
          prev.map((s) => {
            if (s.id !== target.productId) return s;
            return {
              ...s,
              generatedContent: [uploaded, ...s.generatedContent],
            };
          })
        );
      } else {
        setStudioProducts((prev) =>
          prev.map((p) => {
            if (p.id === target.productId) {
              return {
                ...p,
                generatedContent: [uploaded, ...p.generatedContent],
              };
            }
            return p;
          })
        );
      }

      if (activeGallery?.productId === target.productId) {
        updateViewerContent(uploaded);
      }

      // Persist edited image so it is available when re-opening the project
      if (!uploaded.dbPersisted) {
        const editOldId = uploaded.id;
        persistGeneratedContent(projectId, target.productId, uploaded)
          .then((dbId) => syncContentDbId(target.productId, editOldId, dbId))
          .catch((e) => {
            console.error("Failed to persist edited generated content:", e);
            console.error("Content that failed to persist:", {
              id: uploaded.id,
              title: uploaded.title,
              imageType: uploaded.imageType,
              hasImage: !!uploaded.image,
            });
          });
      }
      updateNotification(notificationId, {
        type: "success",
        contentId: uploaded.id,
        thumbnailUrl: uploaded.thumbnailUrl || uploaded.image || undefined,
        resultCount: 1,
      });

      return true;
    } catch (err) {
      updateNotification(notificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
      return false;
    } finally {
      setIsApplyingEdit(false);
    }
  };

  const handleSaveProductMeasurements = useProductMeasurements(
    studioProducts,
    setStudioProductsForProject
  );

  const handleUpdateProductDimensions = React.useCallback(
    (id: string, updates: { width?: string; height?: string }) => {
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === id
            ? { ...p, width: updates.width, height: updates.height }
            : p
        )
      );
    },
    [setStudioProducts]
  );

  const handleApplyEdit = async (payload: ImageEditorApplyPayload) => {
    if (!editingContent) return;
    const didApply = await applyImageEdit(editingContent, payload);
    if (didApply) {
      setEditingContent(null);
    }
  };

  const [isLifestyleGenerating, setIsLifestyleGenerating] =
    React.useState(false);

  const blobToBase64 = React.useCallback((blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () =>
        reject(new Error("Failed to read lifestyle preset reference image."));
      reader.onloadend = () => {
        const dataUrl = typeof reader.result === "string" ? reader.result : "";
        const base64 = dataUrl.split(",")[1] ?? "";
        if (!base64) {
          reject(new Error("Lifestyle preset reference image was empty."));
          return;
        }
        resolve(base64);
      };
      reader.readAsDataURL(blob);
    });
  }, []);

  const resolveLifestylePresetReferenceImage = React.useCallback(
    async (
      preset: LifestylePreset
    ): Promise<{ base64: string; mimeType: string }> => {
      const dataUrlMatch = preset.referenceImage.match(
        /^data:(.*?);base64,(.*)$/
      );
      if (dataUrlMatch?.[2]) {
        return {
          mimeType:
            dataUrlMatch[1] || preset.referenceImageMimeType || "image/png",
          base64: dataUrlMatch[2],
        };
      }

      if (preset.referenceImage) {
        const refResponse = await fetch(preset.referenceImage, {
          cache: "no-store",
        });
        const contentType =
          refResponse.headers.get("content-type") ??
          preset.referenceImageMimeType ??
          "";
        if (refResponse.ok && contentType.startsWith("image/")) {
          const refBlob = await refResponse.blob();
          return {
            mimeType:
              refBlob.type || preset.referenceImageMimeType || "image/png",
            base64: await blobToBase64(refBlob),
          };
        }
      }

      if (preset.referenceImagePath) {
        return {
          mimeType: preset.referenceImageMimeType || "image/png",
          base64: await getLifestylePresetReferenceBase64(
            preset.referenceImagePath
          ),
        };
      }

      throw new Error("Lifestyle preset reference image is unavailable.");
    },
    [blobToBase64]
  );

  const generateLifestyleSceneVariant = React.useCallback(
    async (
      baseImage: ImageFile,
      presetId: string,
      _aspectRatio: string,
      relightScene?: boolean,
      includeSceneDecoration?: boolean,
      capturedContext = snapshotGenerationContext()
    ) => {
      const preset = lifestylePresets.find((entry) => entry.id === presetId);
      if (!preset) throw new Error(`Unknown lifestyle preset: ${presetId}`);

      let resolvedLabel = preset.label.trim() || "Custom";
      let resolvedDescription =
        preset.description.trim() || preset.label.trim();
      let refMimeType = preset.referenceImageMimeType || "image/png";

      // Prefer the preset reference image, but fall back to text if the asset
      // is missing so the flow still works in dev/local environments.
      let refBase64 = "";
      try {
        const resolvedReference =
          await resolveLifestylePresetReferenceImage(preset);
        refBase64 = resolvedReference.base64;
        refMimeType = resolvedReference.mimeType || refMimeType;

        if (preset.isCustom) {
          const styleAnalysis =
            await geminiService.analyzeLifestyleReferenceImage({
              base64: resolvedReference.base64,
              mimeType: resolvedReference.mimeType,
              name: `${preset.label || "custom-room-style"}.png`,
            });

          const nextLabel = styleAnalysis.label?.trim() || preset.label;
          const nextDescription =
            styleAnalysis.description?.trim() || resolvedDescription;
          resolvedLabel = nextLabel;
          resolvedDescription = nextDescription;

          if (
            projectId &&
            (nextLabel !== preset.label ||
              nextDescription !== preset.description)
          ) {
            const updatedPreset = await updateLifestylePreset(
              projectId,
              preset.id,
              {
                label: nextLabel,
                description: nextDescription,
              }
            );
            if (updatedPreset) {
              setCustomLifestylePresets((prev) =>
                prev.map((entry) =>
                  entry.id === updatedPreset.id ? updatedPreset : entry
                )
              );
            }
          }
        }
      } catch (error) {
        console.warn(
          "[LifestyleScene] Falling back to prompt-based Bria generation.",
          {
            presetId,
            referenceImage: preset.referenceImage,
            error: error instanceof Error ? error.message : String(error),
          }
        );
      }

      const allowDecoration = includeSceneDecoration !== false;
      const briaEnvironmentInstruction = allowDecoration
        ? "Build a premium interior environment around it with realistic lighting and proportions."
        : "Stage a clean, minimal space: keep only architecture, floors, walls, and lighting that match the style. Do not add decorative props or accessories (no books, cushions, vases, plants, small objects, or styled shelf clutter). Maintain realistic lighting and proportions.";
      const briaPrompt = `Create a photorealistic product shot using this room style: ${resolvedDescription}. Preserve the product exactly as placed. ${briaEnvironmentInstruction}`;

      let stylePrompt = relightScene
        ? `Apply a ${resolvedDescription} style to the environment. Unify the lighting across the entire image — product and surroundings — so everything looks naturally photographed together. Change scene styling details such as wall textures, decor, materials, and background atmosphere.`
        : `Apply a ${resolvedDescription} style to the environment only. Keep the product exactly as-is: do not redesign, alter, move, resize, rotate, recolor, replace, crop, or partially hide it. Change only scene styling details such as wall textures, decor, materials, lighting mood, and background atmosphere.`;
      if (!allowDecoration) {
        stylePrompt +=
          "\n\nStaging density: Prefer an uncluttered result. Do not add or emphasize decorative objects (e.g. books, throw pillows, vases, plants, trinkets, or shelf styling). Surfaces and shelves should stay visually clean aside from the product and inherent fixed architecture. If the intermediate scene shows extra props, remove or soften them.";
      }

      if (!baseImage.storagePath) {
        throw new Error(
          "generateLifestyleSceneVariant requires a base image with a storagePath."
        );
      }
      // The resolved style label/description are only known here (after preset
      // resolution, incl. AI analysis for custom presets), but the generation
      // context was set by the caller beforehand. Merge them in so the
      // persisted backend record matches the in-memory result, which both
      // callers build with the same `prompt`/`lifestylePresetLabel` fields.
      const generationSettings = mergeSerializedGenerationSettings(
        capturedContext.generationSettings,
        {
          prompt: `${resolvedLabel}: ${resolvedDescription}`,
          lifestylePresetLabel: resolvedLabel,
        }
      );
      const styledResult = await callGenerateImage(
        "lifestyle_scene",
        { image: baseImage.storagePath },
        {
          briaPrompt: refBase64 ? undefined : briaPrompt,
          refImageBase64: refBase64 || undefined,
          refImageMimeType: refBase64 ? refMimeType : undefined,
          stylePrompt,
          model: DEFAULT_INPAINT_STYLE_MODEL,
          relightScene,
          includeSceneDecoration: allowDecoration,
          aspectRatio: _aspectRatio,
        },
        { ...capturedContext, generationSettings }
      );

      return {
        styledResult,
        preset,
        resolvedLabel,
        resolvedDescription,
      };
    },
    [lifestylePresets, projectId, resolveLifestylePresetReferenceImage]
  );

  /** Lifestyle Scene: combined Bria Product Shot → Nano Banana 2 style transfer.
   *  Runs both steps back-to-back and saves the final result.
   *  The user can close the editor mid-generation — the promise still resolves and saves. */
  const handleGenerateLifestyleScene = async (
    base64Image: string,
    presetId: string,
    aspectRatio: string,
    relightScene?: boolean,
    includeSceneDecoration?: boolean,
    imageGeneration?: ImageGenerationSettings
  ): Promise<GeneratedContent> => {
    const targetId = editingContent?.productId ?? activeGallery?.productId;
    const shouldCloseLegacyEditor = !!editingContent;
    if (!targetId) {
      throw new Error("No lifestyle generation target");
    }
    setIsLifestyleGenerating(true);

    // Resolve product name for the notification
    const lifestyleProduct = studioProducts.find((p) => p.id === targetId);
    const lifestyleScene = multiProductScenes.find((s) => s.id === targetId);
    const lifestyleNotificationTitle =
      lifestyleProduct?.originalImage.name || lifestyleScene?.name || "image";

    const lifestyleNotificationId = addNotification({
      type: "progress",
      projectId,
      title: lifestyleNotificationTitle,
      imageType: MILIEU_FIXED_LABEL,
      productId: targetId,
    });

    // Where the result will be filed, resolved up front because the name has
    // to travel with the generation context below — the backend writes the DB
    // row from it, so a title set on the result afterwards never reaches it
    // and the image reloads as "Generated Image" (SIS-517).
    const isScene = multiProductScenes.some((s) => s.id === targetId);
    let originalName = "image";
    let existingGeneratedContent: GeneratedContent[] = [];
    if (isScene) {
      const scene = multiProductScenes.find((s) => s.id === targetId);
      if (scene) {
        originalName = scene.name;
        existingGeneratedContent = scene.generatedContent;
      }
    } else {
      const product = studioProducts.find((p) => p.id === targetId);
      if (product) {
        originalName =
          product.originalImage.name.split(".").slice(0, -1).join(".") ||
          product.originalImage.name;
        existingGeneratedContent = product.generatedContent;
      }
    }
    const filename = generateUniqueFilename(
      namingTemplate,
      {
        originalName,
        type: "Lifestyle",
      },
      existingGeneratedContent.map((c) => c.title)
    );

    try {
      setGenerationContext(
        projectId,
        targetId,
        MILIEU_FIXED_LABEL,
        serializeImageGenerationSettings(imageGeneration, {
          imageType: MILIEU_FIXED_LABEL,
          title: filename,
          aspectRatio,
          presetId,
          relightScene,
        })
      );
      const requestContext = snapshotGenerationContext();
      const base64Blob = await (
        await fetch(`data:image/png;base64,${base64Image}`)
      ).blob();
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new AppError(
          "auth/not_authenticated",
          "User must be authenticated to generate lifestyle scenes."
        );
      }
      const baseImage = await uploadAndResolve(
        base64Blob,
        `f/${userId}/${projectId}/${targetId}/lifestyle_inpaint/base_${Date.now()}.png`,
        "image/png",
        "lifestyle_inpaint_base.png"
      );
      const { styledResult, resolvedLabel, resolvedDescription } =
        await generateLifestyleSceneVariant(
          baseImage,
          presetId,
          aspectRatio,
          relightScene,
          includeSceneDecoration,
          requestContext
        );

      // --- Save the final result ---
      const resultWithId: GeneratedContent = {
        ...styledResult,
        id: styledResult.dbPersisted
          ? (styledResult.id ?? crypto.randomUUID())
          : crypto.randomUUID(),
        title: filename,
        imageType: MILIEU_FIXED_LABEL,
        timestamp: Date.now(),
        generationSettings: mergeGeneratedImageSettings(
          styledResult.generationSettings,
          {
            ...styledResult.generationSettings,
            imageGeneration,
            imageType: MILIEU_FIXED_LABEL,
            aspectRatio,
            model: styledResult.generationSettings?.model,
            prompt: `${resolvedLabel}: ${resolvedDescription}`,
            title: filename,
            relightScene: !!relightScene,
            includeSceneDecoration: includeSceneDecoration !== false,
          }
        ),
      };

      const uploaded = resultWithId.dbPersisted
        ? resultWithId
        : await uploadGeneratedImage(projectId, targetId, resultWithId).catch(
            () => resultWithId
          );

      if (isScene) {
        setMultiProductScenes((prev) =>
          prev.map((s) => {
            if (s.id !== targetId) return s;
            return {
              ...s,
              generatedContent: [uploaded, ...s.generatedContent],
            };
          })
        );
      } else {
        setStudioProducts((prev) =>
          prev.map((p) => {
            if (p.id === targetId) {
              return {
                ...p,
                generatedContent: [uploaded, ...p.generatedContent],
              };
            }
            return p;
          })
        );
      }

      if (activeGallery?.productId === targetId) {
        updateViewerContent(uploaded);
      }

      if (!uploaded.dbPersisted) {
        const lifestyleOldId = uploaded.id;
        persistGeneratedContent(projectId, targetId, uploaded)
          .then((dbId) => syncContentDbId(targetId, lifestyleOldId, dbId))
          .catch((e) => {
            console.error("Failed to persist lifestyle scene result:", e);
          });
      }

      updateNotification(lifestyleNotificationId, {
        type: "success",
        contentId: uploaded.id,
        thumbnailUrl: uploaded.thumbnailUrl || uploaded.image || undefined,
        resultCount: 1,
      });

      if (shouldCloseLegacyEditor) {
        setEditingContent(null);
      }
      return uploaded;
    } catch (err) {
      updateNotification(lifestyleNotificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
      throw err;
    } finally {
      setIsLifestyleGenerating(false);
    }
  };

  const handleApplyInlineEdit = async (
    content: GeneratedContent,
    mode: EditTarget["initialMode"],
    payload: ImageEditorApplyPayload
  ) => {
    if (!activeGallery) return;
    await applyImageEdit(
      {
        content,
        productId: activeGallery.productId,
        initialMode: mode,
        isSourceImage: activeGallery.currentContentId === null,
      },
      payload
    );
  };

  /** Resolve the best source name for naming-template generation in viewer handlers. */
  const resolveViewerSourceName = (
    currentContent: GeneratedContent | null | undefined,
    productId: string
  ): string => {
    // For products, use the original uploaded filename
    const product = studioProducts.find((p) => p.id === productId);
    if (product) return product.originalImage.name;
    // For multi-product scenes, use the scene name
    const scene = multiProductScenes.find((s) => s.id === productId);
    if (scene) return scene.name;
    // Fallback to the current content's title
    return currentContent?.title || "image";
  };

  /** Titles already taken in a viewer target's gallery (product or scene). */
  const resolveViewerExistingTitles = (
    productId: string
  ): Array<string | undefined> =>
    (
      multiProductScenes.find((s) => s.id === productId)?.generatedContent ??
      studioProducts.find((p) => p.id === productId)?.generatedContent ??
      []
    ).map((c) => c.title);

  /**
   * Names a viewer quick-action result the way every other path does.
   *
   * The viewer panels generate on their own and hand the finished result
   * back, so the result callback is the natural place to name it — and much
   * too late: the backend writes the DB row from the generation context set
   * in the start callback, and nothing can update it afterwards (SIS-517).
   * So the start callback names the result and returns the title to the
   * panel, which attaches it to the result it hands back — the name travels
   * with the request instead of through shared state, so a failed or
   * concurrent action can never leak its name onto an unrelated result.
   */
  const nameViewerQuickAction = (
    productId: string,
    type: string,
    details?: string,
    options?: {
      /** Titles claimed by earlier results of the same batch. */
      extraExisting?: Iterable<string | null | undefined>;
      extension?: string;
    }
  ): string => {
    const currentContent = activeGalleryContent ?? viewedContent;
    return generateUniqueFilename(
      namingTemplate,
      {
        originalName: resolveViewerSourceName(currentContent, productId),
        type,
        details,
      },
      [
        ...resolveViewerExistingTitles(productId),
        ...(options?.extraExisting ?? []),
      ],
      options?.extension
    );
  };

  /** Mirrors a named result's title into the fields readers expect. */
  const withViewerQuickActionTitle = (
    result: GeneratedContent,
    title: string | undefined
  ): GeneratedContent => {
    if (!title) return result;
    return {
      ...result,
      title,
      filename: title,
      generationSettings: mergeGeneratedImageSettings(
        result.generationSettings,
        { ...result.generationSettings, title }
      ),
    };
  };

  const resolveNotificationTitle = (productId: string): string => {
    const product = studioProducts.find((p) => p.id === productId);
    if (product) return product.originalImage.name;
    const scene = multiProductScenes.find((s) => s.id === productId);
    return scene?.name || "image";
  };

  const handleRecolorFromViewer = async (
    swatches: ColorSwatch[],
    options: {
      imageGeneration?: ImageGenerationSettings;
      grainDirectionImage?: ImageFile;
      model?: RecolorModel;
      quality?: RecolorQuality;
      resolution?: "1K" | "2K" | "4K";
      usageOnly: boolean;
      temperature?: number;
      additionalContext?: string;
    }
  ) => {
    const currentContent = activeGalleryContent ?? viewedContent;
    if (!activeGallery || !currentContent?.image || swatches.length === 0)
      return;
    const {
      grainDirectionImage,
      model = "nano-banana-pro",
      quality = "high",
      resolution,
      usageOnly,
      temperature,
      additionalContext,
    } = options;
    setIsRecoloringInViewer(true);
    const productId = activeGallery.productId;
    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(productId),
      imageType: "Re-Coloring",
      productId,
    });

    const baseImageFile: ImageFile = {
      storagePath: currentContent.storagePath,
      thumbnailUrl: currentContent.thumbnailUrl,
      fullUrl: currentContent.fullUrl,
      base64: "",
      mimeType: "image/png",
      name: "base.png",
    };
    const viewerProduct = studioProducts.find((p) => p.id === productId);
    const isScene = multiProductScenes.some((s) => s.id === productId);

    // A grain sketch forces GPT Image 2 in the service — persist the model
    // that is actually used, not the pre-coercion choice.
    const effectiveRecolorModel = resolveEffectiveRecolorModel(
      model,
      !!grainDirectionImage
    );

    // Generate one image per selected swatch, sequentially — the generation
    // context is a shared mutable singleton, so recoloring concurrently would
    // race it (mirrors the studio recolor loop). Each recolorImage call checks
    // and deducts credits on its own, so cost scales with the swatch count.
    const uploadedResults: GeneratedContent[] = [];
    const failedSwatches: string[] = [];
    let lastError: unknown = null;

    for (const swatch of swatches) {
      const color = swatch.value || "#000000";
      // "In-use photo only" applies only when the swatch has an in-use photo;
      // then the close-up reference is dropped so the surface is re-covered.
      const perUsageOnly = usageOnly && !!swatch.usageReferenceImage;
      const referenceImage = perUsageOnly ? undefined : swatch.referenceImage;
      const usageReferenceImage = swatch.usageReferenceImage;
      const material = swatch.material;
      // Named before the call — the backend writes the DB row from this
      // context (SIS-517).
      const title = nameViewerQuickAction(productId, "Recolor", color, {
        extraExisting: uploadedResults.map((r) => r.title),
      });
      try {
        setGenerationContext(
          projectId,
          productId,
          "Re-Coloring",
          serializeImageGenerationSettings(options.imageGeneration, {
            ...buildRecolorGenerationSettings(
              color,
              currentContent.generationSettings?.aspectRatio,
              referenceImage,
              usageReferenceImage,
              material,
              currentContent.generationSettings || {}
            ),
            title,
            model: imageModelDisplayName(effectiveRecolorModel),
            recolorModel: effectiveRecolorModel,
            recolorQuality: quality,
            recolorUsageReferenceOnly: perUsageOnly,
            recolorAdditionalContext: additionalContext?.trim() || undefined,
            ...(resolution ? { resolution } : {}),
          })
        );

        const recolored = await recolorImage(
          baseImageFile,
          color,
          { category: viewerProduct?.productCategory },
          referenceImage,
          usageReferenceImage,
          material,
          grainDirectionImage,
          model,
          quality,
          resolution,
          perUsageOnly,
          temperature,
          additionalContext
        );

        const result: GeneratedContent = {
          ...recolored,
          id: recolored.dbPersisted
            ? (recolored.id ?? crypto.randomUUID())
            : crypto.randomUUID(),
          title,
          generationSettings: mergeGeneratedImageSettings(
            recolored.generationSettings,
            {
              ...buildRecolorGenerationSettings(
                color,
                recolored.generationSettings?.aspectRatio,
                referenceImage,
                usageReferenceImage,
                material,
                currentContent.generationSettings || {}
              ),
              ...recolored.generationSettings,
              imageGeneration:
                options.imageGeneration ??
                recolored.generationSettings?.imageGeneration,
              title,
              model:
                recolored.generationSettings?.model ??
                imageModelDisplayName(effectiveRecolorModel),
              recolorModel: effectiveRecolorModel,
              recolorQuality: quality,
              recolorUsageReferenceOnly: perUsageOnly,
              recolorAdditionalContext: additionalContext?.trim() || undefined,
              ...(resolution ? { resolution } : {}),
            }
          ),
          tags: ["Recolor", color],
        };

        const uploaded = result.dbPersisted
          ? result
          : await uploadGeneratedImage(projectId, productId, result).catch(
              () => result
            );
        uploadedResults.push(uploaded);
      } catch (err) {
        console.error("Recolor swatch generation failed", { swatch, err });
        lastError = err;
        failedSwatches.push(getColorSwatchDisplayLabel(swatch));
      }
    }

    try {
      if (uploadedResults.length > 0) {
        // Prepend newest results (in selection order) to the gallery.
        if (isScene) {
          setMultiProductScenes((prev) =>
            prev.map((s) =>
              s.id === productId
                ? {
                    ...s,
                    generatedContent: [
                      ...uploadedResults,
                      ...s.generatedContent,
                    ],
                  }
                : s
            )
          );
        } else {
          setStudioProducts((prev) =>
            prev.map((p) =>
              p.id === productId
                ? {
                    ...p,
                    generatedContent: [
                      ...uploadedResults,
                      ...p.generatedContent,
                    ],
                  }
                : p
            )
          );
        }
        updateViewerContent(uploadedResults[0]);
        // Some swatches succeeded but others failed: surface a partial-success
        // message (credits are charged per attempt) instead of a plain success
        // that hides the dropped swatches, mirroring the studio recolor loop.
        const hadFailures = failedSwatches.length > 0;
        updateNotification(notificationId, {
          type: hadFailures ? "info" : "success",
          message: hadFailures
            ? t("studio.recolorPartialFailure", {
                success: uploadedResults.length,
                failed: failedSwatches.length,
              })
            : undefined,
          contentId: uploadedResults[0].id,
          thumbnailUrl:
            uploadedResults[0].thumbnailUrl ||
            uploadedResults[0].image ||
            undefined,
          resultCount: uploadedResults.length,
        });

        for (const uploaded of uploadedResults) {
          if (!uploaded.dbPersisted) {
            const recolorOldId = uploaded.id;
            persistGeneratedContent(projectId, productId, uploaded)
              .then((dbId) => syncContentDbId(productId, recolorOldId, dbId))
              .catch((e) =>
                console.error("Failed to persist recolored content", e)
              );
          }
        }

        if (failedSwatches.length > 0) {
          console.warn("Recolor skipped failed swatches", { failedSwatches });
        }
      } else {
        updateNotification(notificationId, {
          type: "error",
          message: resolveErrorMessage(lastError, t),
        });
      }
    } finally {
      setIsRecoloringInViewer(false);
    }
  };

  const handleRotateFromViewer = async (
    view: string,
    mode: "cutout" | "milieu",
    resolution: "1K" | "2K" | "4K",
    model: RotationModel,
    quality: RotationQuality,
    imageGeneration?: ImageGenerationSettings
  ) => {
    const currentContent = activeGalleryContent ?? viewedContent;
    if (!activeGallery || !currentContent?.image) return;
    setIsRotatingInViewer(true);
    const productId = activeGallery.productId;
    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(productId),
      imageType: "Rotation",
      productId,
    });

    try {
      const baseImageFile: ImageFile = {
        storagePath: currentContent.storagePath,
        thumbnailUrl: currentContent.thumbnailUrl,
        fullUrl: currentContent.fullUrl,
        base64: "",
        mimeType: currentContent.mimeType || "image/png",
        name: "base.png",
      };

      const aspectRatio =
        currentContent.generationSettings?.aspectRatio ||
        studioAspectRatio ||
        "1:1";
      const rotationMode = mode;
      // Only the image's own stored context — the studio textarea belongs to
      // other modes and must not leak into a viewer rotation (SIS-500).
      const context = currentContent.generationSettings?.context || "";

      // Named before the call — the backend writes the DB row from this
      // context (SIS-517).
      const title = nameViewerQuickAction(productId, "Rotation", view);

      setGenerationContext(
        projectId,
        productId,
        "Rotation",
        serializeImageGenerationSettings(imageGeneration, {
          imageType: "Rotation",
          title,
          rotationAngle: view,
          rotationMode,
          rotationModel: model,
          rotationQuality: quality,
          aspectRatio,
          resolution,
        })
      );

      const rotated = await generateRotationImage(
        baseImageFile,
        view,
        context,
        aspectRatio,
        rotationMode,
        resolution,
        model,
        quality
      );

      const result: GeneratedContent = {
        ...rotated,
        id: rotated.dbPersisted
          ? (rotated.id ?? crypto.randomUUID())
          : crypto.randomUUID(),
        title,
        generationSettings: mergeGeneratedImageSettings(
          rotated.generationSettings,
          {
            ...currentContent.generationSettings,
            ...rotated.generationSettings,
            imageGeneration,
            imageType: "Rotation",
            title,
            rotationAngle: view,
            rotationMode,
            rotationModel: model,
            rotationQuality: quality,
            aspectRatio,
            resolution,
          }
        ),
        tags: ["Rotation", view],
      };

      const uploaded = result.dbPersisted
        ? result
        : await uploadGeneratedImage(projectId, productId, result).catch(
            () => result
          );

      const isScene = multiProductScenes.some((s) => s.id === productId);
      if (isScene) {
        setMultiProductScenes((prev) =>
          prev.map((s) =>
            s.id === productId
              ? {
                  ...s,
                  generatedContent: [uploaded, ...s.generatedContent],
                }
              : s
          )
        );
      } else {
        setStudioProducts((prev) =>
          prev.map((p) => {
            if (p.id === productId) {
              return {
                ...p,
                generatedContent: [uploaded, ...p.generatedContent],
              };
            }
            return p;
          })
        );
      }
      updateViewerContent(uploaded);
      updateNotification(notificationId, {
        type: "success",
        contentId: uploaded.id,
        thumbnailUrl: uploaded.thumbnailUrl || uploaded.image || undefined,
        resultCount: 1,
      });

      if (!uploaded.dbPersisted) {
        const rotateOldId = uploaded.id;
        persistGeneratedContent(projectId, productId, uploaded)
          .then((dbId) => syncContentDbId(productId, rotateOldId, dbId))
          .catch((e) => console.error("Failed to persist rotated content", e));
      }
    } catch (err) {
      console.error(err);
      updateNotification(notificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
    } finally {
      setIsRotatingInViewer(false);
    }
  };

  const handleAspectRatioFromViewer = async (
    result: GeneratedContent,
    productId?: string
  ) => {
    const id = productId ?? activeGallery?.productId;
    if (!id) return;
    const product = studioProducts.find((p) => p.id === id);
    const scene = multiProductScenes.find((s) => s.id === id);

    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(id),
      imageType: "Aspect Ratio",
      productId: id,
    });

    try {
      const uploaded = await uploadGeneratedImage(projectId, id, result).catch(
        () => result
      );

      if (product) {
        setStudioProducts((prev) =>
          prev.map((p) =>
            p.id === id
              ? { ...p, generatedContent: [uploaded, ...p.generatedContent] }
              : p
          )
        );
      } else if (scene) {
        setMultiProductScenes((prev) =>
          prev.map((s) =>
            s.id === id
              ? { ...s, generatedContent: [uploaded, ...s.generatedContent] }
              : s
          )
        );
      } else {
        return;
      }
      setActiveViewer((prev) =>
        prev
          ? { ...prev, content: uploaded, currentContentId: uploaded.id }
          : { content: uploaded, productId: id, currentContentId: uploaded.id }
      );
      if (!uploaded.dbPersisted) {
        const arOldId = uploaded.id;
        persistGeneratedContent(projectId, id, uploaded)
          .then((dbId) => syncContentDbId(id, arOldId, dbId))
          .catch((e) =>
            console.error("Failed to persist aspect ratio content", e)
          );
      }
      updateNotification(notificationId, {
        type: "success",
        contentId: uploaded.id,
        thumbnailUrl: uploaded.thumbnailUrl || uploaded.image || undefined,
        resultCount: 1,
      });
    } catch (err) {
      updateNotification(notificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
      throw err;
    }
  };

  // Details / close-up quick action applied to an already-generated image in
  // the viewer. Mirrors handleAspectRatioFromViewer: uploads the result to GCS
  // and appends it to the owning product/scene's generatedContent.
  const handleDetailsFromViewer = async (
    result: GeneratedContent,
    productId?: string
  ) => {
    const id = productId ?? activeGallery?.productId;
    if (!id) return;
    const product = studioProducts.find((p) => p.id === id);
    const scene = multiProductScenes.find((s) => s.id === id);
    // Bail before creating a notification when the target no longer exists
    // (e.g. product deleted), so the Notification Hub never shows a spinner
    // that can't transition to success/error.
    if (!product && !scene) return;

    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(id),
      imageType: "Details",
      productId: id,
    });

    try {
      const uploaded = await uploadGeneratedImage(projectId, id, result).catch(
        () => result
      );

      if (product) {
        setStudioProducts((prev) =>
          prev.map((p) =>
            p.id === id
              ? { ...p, generatedContent: [uploaded, ...p.generatedContent] }
              : p
          )
        );
      } else {
        setMultiProductScenes((prev) =>
          prev.map((s) =>
            s.id === id
              ? { ...s, generatedContent: [uploaded, ...s.generatedContent] }
              : s
          )
        );
      }
      setActiveViewer((prev) =>
        prev
          ? { ...prev, content: uploaded, currentContentId: uploaded.id }
          : { content: uploaded, productId: id, currentContentId: uploaded.id }
      );
      if (!uploaded.dbPersisted) {
        const detailsOldId = uploaded.id;
        persistGeneratedContent(projectId, id, uploaded)
          .then((dbId) => syncContentDbId(id, detailsOldId, dbId))
          .catch((e) => console.error("Failed to persist details content", e));
      }
      updateNotification(notificationId, {
        type: "success",
        contentId: uploaded.id,
        thumbnailUrl: uploaded.thumbnailUrl || uploaded.image || undefined,
        resultCount: 1,
      });
    } catch (err) {
      // Surface the failure via the notification only; re-throwing would risk
      // an unhandled rejection in the fire-and-forget caller.
      updateNotification(notificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
    }
  };

  const handleMilieuCreativeFromViewer = async (
    result: GeneratedContent,
    productId?: string
  ) => {
    const id = productId ?? activeGallery?.productId;
    if (!id) return;
    const product = studioProducts.find((p) => p.id === id);
    const scene = multiProductScenes.find((s) => s.id === id);
    // Bail before creating a notification when the target no longer exists
    // (e.g. product deleted), so the Notification Hub never shows a spinner
    // that can't transition to success/error.
    if (!product && !scene) return;

    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(id),
      imageType: MILIEU_CREATIVE_LABEL,
      productId: id,
    });

    try {
      const uploaded = await uploadGeneratedImage(projectId, id, result).catch(
        (e) => {
          // Deliberate fallback to the un-uploaded result so the image still
          // reaches the gallery — but credits are already spent here, so the
          // failure must be visible in Sentry, not swallowed.
          reportError(e, { context: "milieuCreativeFromViewer_upload" });
          return result;
        }
      );

      if (product) {
        setStudioProducts((prev) =>
          prev.map((p) =>
            p.id === id
              ? { ...p, generatedContent: [uploaded, ...p.generatedContent] }
              : p
          )
        );
      } else {
        setMultiProductScenes((prev) =>
          prev.map((s) =>
            s.id === id
              ? { ...s, generatedContent: [uploaded, ...s.generatedContent] }
              : s
          )
        );
      }
      // Only advance a viewer that is still on this product: without the
      // guard a request finishing late would reopen a closed viewer, or
      // inject this product's result into another product's viewer session
      // (keeping that session's productId). The result stays reachable via
      // the gallery and the success notification's contentId deep link.
      setActiveViewer((prev) =>
        prev?.productId === id
          ? { ...prev, content: uploaded, currentContentId: uploaded.id }
          : prev
      );
      if (!uploaded.dbPersisted) {
        const milieuOldId = uploaded.id;
        persistGeneratedContent(projectId, id, uploaded)
          .then((dbId) => syncContentDbId(id, milieuOldId, dbId))
          // Credits are already spent at this point — a lost gallery row
          // must be visible in Sentry, not just the console.
          .catch((e) =>
            reportError(e, { context: "milieuCreativeFromViewer_persist" })
          );
      }
      updateNotification(notificationId, {
        type: "success",
        contentId: uploaded.id,
        thumbnailUrl: uploaded.thumbnailUrl || uploaded.image || undefined,
        resultCount: 1,
      });
    } catch (err) {
      // Surface the failure via the notification only (re-throwing would risk
      // an unhandled rejection in the fire-and-forget caller) — but credits
      // are already spent here, so the cause must also reach Sentry.
      reportError(err, { context: "milieuCreativeFromViewer" });
      updateNotification(notificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
    }
  };

  // A failed viewer Milieu Creative generation must reach the user even when
  // the sidebar was closed mid-flight, so it lands in the notification system
  // (toast + Notification Hub) instead of the sidebar's inline error. Sentry
  // reporting already happened viewer-side.
  const handleMilieuCreativeErrorFromViewer = (
    error: unknown,
    productId?: string
  ) => {
    const id = productId ?? activeGallery?.productId;
    addNotification({
      type: "error",
      projectId,
      // Unlike the success path, the error must surface even when the target
      // product is already gone — resolveNotificationTitle falls back then.
      title: resolveNotificationTitle(id ?? ""),
      imageType: MILIEU_CREATIVE_LABEL,
      productId: id,
      message: resolveErrorMessage(error, t),
    });
  };

  // Persist a freshly generated direction sketch onto the active product so it
  // shows in the gallery and can be reused in the sketch creator without
  // regenerating. Does not switch the viewer (the creator modal stays open).
  const handleDirectionSketchGenerated = async (
    result: GeneratedContent,
    provenance?: ClientImageProvenance
  ) => {
    const id = activeGallery?.productId;
    if (!id) return;
    const product = studioProducts.find((p) => p.id === id);
    const scene = multiProductScenes.find((s) => s.id === id);
    if (!product && !scene) return;

    // A generated sketch arrives already named (the modal attaches the title
    // it was given before the call — the backend wrote its row from that
    // context); the flattened composite the user "uses" comes from the canvas
    // untitled and is named here, where it is persisted (SIS-517).
    const title =
      result.title ??
      generateUniqueFilename(
        namingTemplate,
        { originalName: resolveNotificationTitle(id), type: "Sketch" },
        resolveViewerExistingTitles(id)
      );

    const tagged: GeneratedContent = {
      ...result,
      id: result.dbPersisted
        ? (result.id ?? crypto.randomUUID())
        : crypto.randomUUID(),
      imageType: "Direction Sketch",
      title,
      generationSettings: mergeGeneratedImageSettings(
        result.generationSettings,
        { ...result.generationSettings, title }
      ),
      tags: ["Direction Sketch"],
    };

    try {
      const uploaded = await uploadGeneratedImage(
        projectId,
        id,
        tagged,
        provenance
      ).catch(() => tagged);
      if (product) {
        setStudioProducts((prev) =>
          prev.map((p) =>
            p.id === id
              ? { ...p, generatedContent: [uploaded, ...p.generatedContent] }
              : p
          )
        );
      } else {
        setMultiProductScenes((prev) =>
          prev.map((s) =>
            s.id === id
              ? { ...s, generatedContent: [uploaded, ...s.generatedContent] }
              : s
          )
        );
      }
      if (!uploaded.dbPersisted) {
        const oldId = uploaded.id;
        persistGeneratedContent(projectId, id, uploaded)
          .then((dbId) => syncContentDbId(id, oldId, dbId))
          .catch((e) => console.error("Failed to persist direction sketch", e));
      }
    } catch (e) {
      console.error("Failed to save direction sketch", e);
    }
  };

  const handleCropFromViewer = async (
    rawResult: GeneratedContent,
    productId?: string,
    provenance?: ClientImageProvenance
  ) => {
    const id = productId ?? activeGallery?.productId;
    if (!id) return;
    const product = studioProducts.find((p) => p.id === id);
    const scene = multiProductScenes.find((s) => s.id === id);

    // Crop/adjust results are canvas-produced and arrive untitled — name them
    // here, where the naming template and the target gallery are known, so
    // they don't reload as "Generated Image" (SIS-517). "Edit" matches the
    // imageType these results carry.
    const result = withViewerQuickActionTitle(
      rawResult,
      rawResult.title ? undefined : nameViewerQuickAction(id, "Edit")
    );

    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(id),
      imageType: result.generationSettings?.imageType ?? "Crop",
      productId: id,
    });

    try {
      // Do not swallow upload failures: falling back to `result` would keep the
      // base64 data URL in React state (violating the no-base64-in-state rule)
      // and report a false success. Let it throw so the editor stays open and
      // surfaces the error.
      const uploaded = await uploadGeneratedImage(
        projectId,
        id,
        result,
        provenance
      );

      if (product) {
        setStudioProducts((prev) =>
          prev.map((p) =>
            p.id === id
              ? { ...p, generatedContent: [uploaded, ...p.generatedContent] }
              : p
          )
        );
      } else if (scene) {
        setMultiProductScenes((prev) =>
          prev.map((s) =>
            s.id === id
              ? { ...s, generatedContent: [uploaded, ...s.generatedContent] }
              : s
          )
        );
      } else {
        return;
      }
      setActiveViewer((prev) =>
        prev
          ? { ...prev, content: uploaded, currentContentId: uploaded.id }
          : { content: uploaded, productId: id, currentContentId: uploaded.id }
      );
      if (!uploaded.dbPersisted) {
        const oldId = uploaded.id;
        persistGeneratedContent(projectId, id, uploaded)
          .then((dbId) => syncContentDbId(id, oldId, dbId))
          .catch((e) => console.error("Failed to persist crop content", e));
      }
      updateNotification(notificationId, {
        type: "success",
        contentId: uploaded.id,
        thumbnailUrl: uploaded.thumbnailUrl || uploaded.image || undefined,
        resultCount: 1,
      });
    } catch (err) {
      updateNotification(notificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
      throw err;
    }
  };

  const handleDimensionFromViewer = async (
    rawResult: GeneratedContent,
    productId?: string
  ) => {
    const id = productId ?? activeGallery?.productId;
    if (!id) return;
    const product = studioProducts.find((p) => p.id === id);
    const scene = multiProductScenes.find((s) => s.id === id);
    // Bail before creating a notification when the target no longer exists
    // (e.g. product deleted), so the Notification Hub never shows a spinner
    // that can't transition to success/error.
    if (!product && !scene) return;

    // Dimensions renders are canvas-produced and arrive untitled — name them
    // before they are persisted below, so they don't reload as "Generated
    // Image" (SIS-517).
    const result = withViewerQuickActionTitle(
      rawResult,
      rawResult.title ? undefined : nameViewerQuickAction(id, "Dimensions")
    );

    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(id),
      imageType: "Dimensions",
      productId: id,
    });

    try {
      // The dimensions modal already uploaded the flattened image to GCS, so
      // the result carries a storagePath + resolved URLs (never base64). Persist
      // it to the database *before* inserting it into the gallery: the modal
      // awaits this handler and only closes once it resolves, so awaiting the
      // write means a DB failure keeps the editor open with an error instead of
      // dismissing as if the save succeeded (leaving the image only in memory +
      // GCS, lost on refresh). Persisting first also leaves nothing behind on
      // failure, so a retry can't create a duplicate gallery entry.
      let stored = result;
      if (!stored.dbPersisted) {
        const dbId = await persistGeneratedContent(projectId, id, stored);
        stored = { ...stored, id: dbId, dbPersisted: true };
      }

      if (product) {
        setStudioProducts((prev) =>
          prev.map((p) =>
            p.id === id
              ? { ...p, generatedContent: [stored, ...p.generatedContent] }
              : p
          )
        );
      } else {
        setMultiProductScenes((prev) =>
          prev.map((s) =>
            s.id === id
              ? { ...s, generatedContent: [stored, ...s.generatedContent] }
              : s
          )
        );
      }
      setActiveViewer((prev) =>
        prev
          ? { ...prev, content: stored, currentContentId: stored.id }
          : { content: stored, productId: id, currentContentId: stored.id }
      );
      updateNotification(notificationId, {
        type: "success",
        contentId: stored.id,
        thumbnailUrl: stored.thumbnailUrl || stored.image || undefined,
        resultCount: 1,
      });
    } catch (err) {
      // Surface the failure via the notification and re-throw so the modal's
      // onSave rejects and the editor stays open for the user to retry — rather
      // than closing on a save that never reached the database. The modal awaits
      // and catches this rejection, so it does not become an unhandled rejection.
      updateNotification(notificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
      throw err;
    }
  };

  // Prepends a viewer-saved result (marketplace export / set image) to the
  // owning collection. The viewer can be open on a multiproduct scene, whose
  // id lives in multiProductScenes (not studioProducts) — mirror the prepend
  // into the right collection so the open gallery reflects the save.
  const handleViewerContentSaved = (saved: GeneratedContent) => {
    const owningProductId = activeGallery?.productId;
    if (!owningProductId) return;
    const isScene = multiProductScenes.some((s) => s.id === owningProductId);
    if (isScene) {
      setMultiProductScenes((prev) =>
        prev.map((s) =>
          s.id === owningProductId
            ? {
                ...s,
                generatedContent: [saved, ...s.generatedContent],
              }
            : s
        )
      );
    } else {
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === owningProductId
            ? {
                ...p,
                generatedContent: [saved, ...p.generatedContent],
              }
            : p
        )
      );
    }
  };

  const handleAddAvatarFromViewer = async (
    result: GeneratedContent,
    productId?: string
  ) => {
    const id = productId ?? activeGallery?.productId;
    if (!id) return;
    const product = studioProducts.find((p) => p.id === id);
    const scene = multiProductScenes.find((s) => s.id === id);
    // Bail before creating a notification when the target no longer exists
    // (e.g. product deleted), so the Notification Hub never shows a spinner
    // that can't transition to success/error.
    if (!product && !scene) return;

    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(id),
      imageType: "Add Avatar",
      productId: id,
    });

    try {
      const uploaded = await uploadGeneratedImage(projectId, id, result).catch(
        () => result
      );

      if (product) {
        setStudioProducts((prev) =>
          prev.map((p) =>
            p.id === id
              ? { ...p, generatedContent: [uploaded, ...p.generatedContent] }
              : p
          )
        );
      } else {
        setMultiProductScenes((prev) =>
          prev.map((s) =>
            s.id === id
              ? { ...s, generatedContent: [uploaded, ...s.generatedContent] }
              : s
          )
        );
      }
      setActiveViewer((prev) =>
        prev
          ? { ...prev, content: uploaded, currentContentId: uploaded.id }
          : { content: uploaded, productId: id, currentContentId: uploaded.id }
      );
      if (!uploaded.dbPersisted) {
        const oldId = uploaded.id;
        persistGeneratedContent(projectId, id, uploaded)
          .then((dbId) => syncContentDbId(id, oldId, dbId))
          .catch((e) =>
            console.error("Failed to persist add avatar content", e)
          );
      }
      updateNotification(notificationId, {
        type: "success",
        contentId: uploaded.id,
        thumbnailUrl: uploaded.thumbnailUrl || uploaded.image || undefined,
        resultCount: 1,
      });
    } catch (err) {
      // Surface the failure via the notification only; re-throwing would risk
      // an unhandled rejection in the fire-and-forget caller.
      updateNotification(notificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
    }
  };

  const handleRemoveBackgroundFromViewer = async (
    _focusItem?: string,
    options?: {
      backgroundRemoval?: BackgroundRemovalConfig;
      imageGeneration?: ImageGenerationSettings;
    }
  ) => {
    const currentContent = activeGalleryContent ?? viewedContent;
    if (!activeGallery || !currentContent?.image) return;

    const productId = activeGallery.productId;
    const isViewingSourceImage = activeGallery.currentContentId === null;
    const cfg = resolveBackgroundRemovalConfig({
      backgroundRemoval: options?.backgroundRemoval,
    });
    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(productId),
      imageType: "Background Removal",
      productId,
    });

    // Set both viewer state and product isProcessing state
    setIsRemovingBackgroundInViewer(true);
    setBackgroundRemovalProgress("Starting background removal...");

    // Mark the product as processing so card shows indicator
    setStudioProducts((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, isProcessing: true } : p))
    );

    try {
      const baseImageFile: ImageFile = {
        storagePath: currentContent.storagePath,
        thumbnailUrl: currentContent.thumbnailUrl,
        fullUrl: currentContent.fullUrl,
        base64: "",
        mimeType: "image/png",
        name: "base.png",
      };

      let result: GeneratedContent;
      let noBgAppImage: AppImageFile | null = null;

      if (cfg.mode === "complex") {
        // Complex scene: mirror the studio JPEG-cutout path (Nano Banana Pro).
        // generateCutoutImage returns a GCS-backed GeneratedContent.
        // Named before the call — the backend writes the DB row from this
        // context (SIS-517).
        const filename = nameViewerQuickAction(
          productId,
          "Background_Removal",
          undefined,
          { extension: "jpg" }
        );
        setGenerationContext(
          projectId,
          productId,
          "Background Removal",
          serializeImageGenerationSettings(options?.imageGeneration, {
            imageType: "Background Removal",
            title: filename,
            backgroundRemoval: cfg,
          })
        );
        const res = await generateCutoutImage(
          baseImageFile,
          "1:1",
          undefined,
          cfg.shadows,
          undefined,
          (currentContent.title || "image").split(".")[0],
          "original",
          undefined,
          cfg.resolution ?? "1K"
        );

        result = {
          ...res,
          id: res.id ?? crypto.randomUUID(),
          title: filename,
          tags: ["Remove Background", "Complex Scene"],
          imageType: "Background Removal",
          generationSettings: mergeGeneratedImageSettings(
            res.generationSettings,
            {
              imageGeneration: options?.imageGeneration,
              imageType: "Background Removal",
              title: filename,
              backgroundRemoval: cfg,
            }
          ),
          timestamp: Date.now(),
          filename,
        };

        // Complex results are AI-reinterpreted shadow-on-white JPEGs, so we
        // never overwrite the product's canonical source photo (matching the
        // studio JPEG-cutout path). noBgAppImage stays null -> gallery only.
      } else {
        // Simple scene: Bria background removal (transparent or white fill).
        const fillBackgroundWhite = cfg.fill === "white";
        // Named before the call — the settings handed to the backend are what
        // it writes the DB row from (SIS-517).
        const filename = nameViewerQuickAction(productId, "Background_Removal");
        const noBg = await executeBackgroundRemoval(
          baseImageFile,
          (message) => setBackgroundRemovalProgress(message),
          projectId,
          productId,
          "Background Removal",
          serializeImageGenerationSettings(options?.imageGeneration, {
            imageType: "Background Removal",
            title: filename,
            backgroundRemoval: cfg,
          }),
          { fillBackgroundWhite }
        );

        noBgAppImage = isViewingSourceImage
          ? toAppImageFile(noBg, productId)
          : null;

        result = {
          id: noBg.dbId ?? crypto.randomUUID(),
          image: noBg.downloadUrl,
          storagePath: noBg.storagePath,
          thumbnailUrl: noBg.downloadUrl,
          text: fillBackgroundWhite
            ? "Background Removed (White Fill)"
            : "Background Removed",
          title: filename,
          video: null,
          tags: fillBackgroundWhite
            ? ["No Background", "White Filled"]
            : ["No Background", "Transparent"],
          imageType: "Background Removal",
          generationSettings: {
            imageGeneration: options?.imageGeneration,
            imageType: "Background Removal",
            title: filename,
            backgroundRemoval: cfg,
          },
          timestamp: Date.now(),
          filename,
          mimeType: noBg.mimeType,
          dimensions: { width: noBg.width, height: noBg.height },
          dbPersisted: !!noBg.dbId,
        };
      }

      // Add to generatedContent
      const isScene = multiProductScenes.some((s) => s.id === productId);
      if (isScene) {
        setMultiProductScenes((prev) =>
          prev.map((s) =>
            s.id === productId
              ? {
                  ...s,
                  generatedContent: [result, ...s.generatedContent],
                }
              : s
          )
        );
      } else {
        setStudioProducts((prev) =>
          prev.map((p) => {
            if (p.id === productId) {
              return {
                ...p,
                ...(isViewingSourceImage && noBgAppImage
                  ? {
                      image: noBgAppImage,
                    }
                  : {}),
                generatedContent: [result, ...p.generatedContent],
                isProcessing: false,
              };
            }
            return p;
          })
        );
      }
      updateViewerContent(result);
      updateNotification(notificationId, {
        type: "success",
        contentId: result.id,
        thumbnailUrl: result.thumbnailUrl || result.image || undefined,
        resultCount: 1,
      });

      if (!isScene && isViewingSourceImage && noBgAppImage) {
        updateStudioProductCurrentImage(
          projectId,
          productId,
          noBgAppImage
        ).catch((e) =>
          console.error("Failed to persist updated product image", e)
        );
      }

      if (!result.dbPersisted) {
        const genOldId = result.id;
        persistGeneratedContent(projectId, productId, result)
          .then((dbId) => syncContentDbId(productId, genOldId, dbId))
          .catch((e) =>
            console.error("Failed to persist generated content", e)
          );
      }
    } catch (err) {
      console.error(err);
      updateNotification(notificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
      // Clear processing state on error
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === productId ? { ...p, isProcessing: false } : p
        )
      );
    } finally {
      setIsRemovingBackgroundInViewer(false);
      setBackgroundRemovalProgress(null);
    }
  };

  const handleUpscaleFromViewer = async (
    upscaleFactor: UpscaleFactor,
    upscaler: Upscaler = "seedvr"
  ) => {
    // Use activeGalleryContent (what the modal actually displays) instead of
    // viewedContent which can become stale when studioProducts state changes.
    const currentContent = activeGalleryContent ?? viewedContent;
    if (!activeGallery || !currentContent?.image) return;

    const productId = activeGallery.productId;
    const isScene = multiProductScenes.some((s) => s.id === productId);
    const notificationId = addNotification({
      type: "progress",
      projectId,
      title: resolveNotificationTitle(productId),
      imageType: "Upscale",
      productId,
    });

    setIsUpscalingInViewer(true);
    if (!isScene) {
      setStudioProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, isProcessing: true } : p))
      );
    }

    try {
      const baseImageFile: ImageFile = {
        storagePath: currentContent.storagePath,
        thumbnailUrl: currentContent.thumbnailUrl,
        fullUrl: currentContent.fullUrl,
        base64: "",
        mimeType: "image/png",
        name: "base.png",
        width: currentContent.dimensions?.width,
        height: currentContent.dimensions?.height,
      };
      // Named before the call — the settings handed to the backend are what it
      // writes the DB row from (SIS-517).
      const filename = nameViewerQuickAction(
        productId,
        "Upscale",
        upscaleFactor
      );
      const upscaled = await executeUpscale(
        baseImageFile,
        upscaleFactor,
        upscaler,
        undefined,
        projectId,
        productId,
        "Upscale",
        serializeGenerationSettings({
          imageType: "Upscale",
          title: filename,
          upscaleFactor,
          upscaler,
        })
      );

      const result: GeneratedContent = {
        id: upscaled.dbId ?? crypto.randomUUID(),
        image: upscaled.downloadUrl,
        storagePath: upscaled.storagePath,
        thumbnailUrl: upscaled.downloadUrl,
        text: `Upscaled ${upscaleFactor}`,
        title: filename,
        video: null,
        tags: ["Upscaled", upscaleFactor],
        imageType: "Upscale",
        generationSettings: {
          imageType: "Upscale",
          title: filename,
        },
        timestamp: Date.now(),
        filename,
        mimeType: upscaled.mimeType,
        dimensions: { width: upscaled.width, height: upscaled.height },
        dbPersisted: !!upscaled.dbId,
      };

      if (isScene) {
        setMultiProductScenes((prev) =>
          prev.map((s) =>
            s.id === productId
              ? {
                  ...s,
                  generatedContent: [result, ...s.generatedContent],
                }
              : s
          )
        );
      } else {
        setStudioProducts((prev) =>
          prev.map((p) => {
            if (p.id === productId) {
              return {
                ...p,
                generatedContent: [result, ...p.generatedContent],
                isProcessing: false,
              };
            }
            return p;
          })
        );
      }
      updateViewerContent(result);
      updateNotification(notificationId, {
        type: "success",
        contentId: result.id,
        thumbnailUrl: result.thumbnailUrl || result.image || undefined,
        resultCount: 1,
      });

      if (!result.dbPersisted) {
        const upscaleOldId = result.id;
        persistGeneratedContent(projectId, productId, result)
          .then((dbId) => syncContentDbId(productId, upscaleOldId, dbId))
          .catch((e) => console.error("Failed to persist upscaled content", e));
      }
    } catch (err: unknown) {
      console.error(err);
      updateNotification(notificationId, {
        type: "error",
        message: resolveErrorMessage(err, t),
      });
      if (!isScene) {
        setStudioProducts((prev) =>
          prev.map((p) =>
            p.id === productId ? { ...p, isProcessing: false } : p
          )
        );
      }
    } finally {
      setIsUpscalingInViewer(false);
    }
  };

  const handleAddToStudio = async (content: GeneratedContent) => {
    if (!content.image) return;

    let imageDataUrl: string;
    try {
      imageDataUrl = await ensureContentDataUrl(content);
    } catch (err) {
      console.error("Failed to load image for studio insert", err);
      setError("Failed to load image. Please try again.");
      return;
    }

    // Upload the generated image to GCS *before* adding the product to state so
    // it always has a storagePath. A base64-only product cannot be generated
    // from — the backend resolves every image from GCS by storagePath (SIS-395).
    let newProduct: StudioProduct;
    try {
      const created = await createProductFromDataUrl(
        imageDataUrl,
        projectId,
        auth.currentUser!.uid,
        `${content.title || "generated_image"}.png`
      );
      newProduct = { ...created, isSelected: true };
    } catch (err) {
      reportError(err, {
        context: "product_upload",
        stage: "create_from_generated",
        projectId,
      });
      toast.error(resolveErrorMessage(err, t));
      return;
    }

    // Pessimistic persist (SIS-451): DB row first, then local state.
    // Reuses the storagePath already uploaded above.
    try {
      await persistStudioProduct(projectId, newProduct);
    } catch (err) {
      reportError(err, {
        context: "product_upload",
        stage: "persist_from_generated",
        projectId,
        productId: newProduct.id,
      });
      toast.error(resolveErrorMessage(err, t));
      return;
    }

    setStudioProducts((prev) => [newProduct, ...prev]);
    void runProductAnalysis(newProduct.id, newProduct.originalImage);
  };

  const handleRemoveBackgroundFromEditor = async (
    productId: string
  ): Promise<void> => {
    const product = studioProducts.find((p) => p.id === productId);
    if (!product || product.isProcessing) return;

    // Set processing state
    setStudioProducts((prev) =>
      prev.map((p) =>
        p.id === productId ? { ...p, isProcessing: true, error: null } : p
      )
    );

    // Named before the call — the settings handed to the backend are what it
    // writes the DB row from (SIS-517).
    const filename = generateUniqueFilename(
      namingTemplate,
      {
        originalName: product.originalImage.name,
        type: "Background_Removal",
      },
      product.generatedContent.map((c) => c.title)
    );

    try {
      const noBgResult = await executeBackgroundRemoval(
        product.originalImage,
        (message) => {
          // Update product with progress message
          setStudioProducts((prev) =>
            prev.map((p) =>
              p.id === productId
                ? { ...p, error: message } // Temporarily use error field for progress
                : p
            )
          );
        },
        projectId,
        productId,
        "Background Removal",
        serializeGenerationSettings({
          imageType: "Background Removal",
          title: filename,
        })
      );

      const noBgAppImage = toAppImageFile(noBgResult, productId);

      // Create new GeneratedContent with the background-removed image
      const result: GeneratedContent = {
        id: noBgResult.dbId ?? crypto.randomUUID(),
        image: noBgResult.downloadUrl,
        storagePath: noBgResult.storagePath,
        thumbnailUrl: noBgResult.downloadUrl,
        text: "Background Removed",
        title: filename,
        video: null,
        tags: ["No Background", "Transparent"],
        imageType: "Background Removal",
        generationSettings: {
          imageType: "Background Removal",
          title: filename,
        },
        timestamp: Date.now(),
        filename,
        mimeType: noBgResult.mimeType,
        dimensions: { width: noBgResult.width, height: noBgResult.height },
        dbPersisted: !!noBgResult.dbId,
      };

      // Add to generatedContent (keep product.image unchanged)
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? {
                ...p,
                image: noBgAppImage,
                generatedContent: [result, ...p.generatedContent],
                isProcessing: false,
                error: null,
              }
            : p
        )
      );

      updateStudioProductCurrentImage(projectId, productId, noBgAppImage).catch(
        (e) => console.error("Failed to persist updated product image", e)
      );

      if (!result.dbPersisted) {
        const bgOldId = result.id;
        persistGeneratedContent(projectId, productId, result)
          .then((dbId) => syncContentDbId(productId, bgOldId, dbId))
          .catch((e) =>
            console.error("Failed to persist generated content", e)
          );
      }
    } catch (err) {
      console.error("Background removal failed:", err);
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? {
                ...p,
                isProcessing: false,
                error: "Background removal failed. Please try again.",
              }
            : p
        )
      );
    }
  };

  const handleAddProductFromEditor = async (file: File): Promise<void> => {
    try {
      const product = await uploadFileAsProduct(
        file,
        projectId,
        auth.currentUser!.uid
      );
      // Pessimistic persist (SIS-451): DB row first, then local state.
      try {
        await persistStudioProduct(projectId, product);
      } catch (err) {
        reportError(err, {
          context: "product_upload",
          stage: "persist_from_editor",
          projectId,
          productId: product.id,
          fileName: sanitizeFileNameForReport(file.name),
          fileSize: file.size,
          fileType: file.type,
        });
        toast.error(resolveErrorMessage(err, t));
        return;
      }
      setStudioProducts((prev) => [product, ...prev]);
      void runProductAnalysis(product.id, product.originalImage);
    } catch (err) {
      reportError(err, {
        context: "product_upload",
        stage: "upload_from_editor",
        projectId,
        fileName: sanitizeFileNameForReport(file.name),
        fileSize: file.size,
        fileType: file.type,
      });
      toast.error(resolveErrorMessage(err, t));
    }
  };

  // Logic to determine active tab based on path
  const currentPath = location.pathname;
  const isSettings = currentPath.includes("/settings");
  const isWorkflows = currentPath.includes("/workflows");
  const isLibrary = currentPath.includes("/library");
  const isVideo = currentPath.includes("/video");
  const isHighlights = currentPath.includes("/highlights");
  const isStudio =
    !isSettings && !isWorkflows && !isLibrary && !isVideo && !isHighlights;

  const handleRemoveFromQueue = (taskId: string) => {
    setGenerationQueue((prev) => prev.filter((t) => t.id !== taskId));
  };

  const isSimpleCutoutSettings = (
    settings: Pick<
      GenerationSettings,
      "imageType" | "backgroundRemoval" | "cutoutShadows"
    >
  ) =>
    settings.imageType === "Cutout" &&
    resolveBackgroundRemovalConfig(settings).mode === "simple";

  const normalizeQueueTaskSettings = (
    settings: GenerationSettings
  ): GenerationSettings =>
    settings.imageType === "Decoration"
      ? { ...settings, aspectRatio: "1:1" }
      : settings;

  const handleUpdateQueueTaskSettings = (
    taskId: string,
    updates: Partial<GenerationSettings>
  ) => {
    setGenerationQueue((prev) =>
      prev.map((t) =>
        t.id === taskId
          ? {
              ...t,
              settings: normalizeQueueTaskSettings({
                ...t.settings,
                ...updates,
              }),
            }
          : t
      )
    );
  };

  const handleRunQueue = async () => {
    if (isQueueProcessing) return;

    const pendingTasks = generationQueue.filter((t) => t.status === "pending");
    if (pendingTasks.length === 0) return;

    setIsQueueProcessing(true);
    try {
      for (const task of pendingTasks) {
        setGenerationQueue((prev) =>
          prev.map((t) =>
            t.id === task.id ? { ...t, status: "processing" } : t
          )
        );

        let taskOk = true;
        let shouldStop = false;

        for (const productId of task.productIds) {
          const result = await handleGenerateForSinglePlant(
            productId,
            task.settings.resolution || "1K",
            task.settings
          );
          if (!result.ok) {
            taskOk = false;
            if (result.reason === "credits") {
              shouldStop = true;
              break;
            }
          }
        }

        setGenerationQueue((prev) =>
          prev.map((t) =>
            t.id === task.id
              ? { ...t, status: taskOk ? "completed" : "failed" }
              : t
          )
        );

        if (shouldStop) break;
      }
    } finally {
      setIsQueueProcessing(false);
    }
  };

  const [showResolutionModal, setShowResolutionModal] = React.useState(false);
  const [pendingQueueTask, setPendingQueueTask] = React.useState<{
    id: string;
    productIds: string[];
    status: "pending";
    createdAt: number;
    settings: Omit<GenerationSettings, "resolution">;
  } | null>(null);
  const [selectedResolution, setSelectedResolution] = React.useState<
    "1K" | "2K" | "4K"
  >("1K");

  const handleAddToQueue = () => {
    const selected = studioProducts.filter((p) => p.isSelected);
    if (selected.length === 0) {
      toast.error(t("studio.errorSelectOneProductForQueue"));
      return;
    }

    // Don't let incomplete Dimensional Drawing products into the queue; the
    // generation chokepoint would reject them at run time anyway, so surface it
    // up front when the user tries to queue.
    if (
      studioImageType === "Scaling" &&
      selected.some(
        (p) =>
          !scalingDimensionsComplete(
            p,
            activeDimensionPreset?.markerPreference ?? dimensionMarker
          )
      )
    ) {
      toast.error(t("studio.scalingDimensionRequiredError"));
      return;
    }

    const taskWithoutResolution: {
      id: string;
      productIds: string[];
      status: "pending";
      createdAt: number;
      settings: Omit<GenerationSettings, "resolution">;
    } = {
      id: crypto.randomUUID(),
      productIds: selected.map((p) => p.id),
      status: "pending",
      createdAt: Date.now(),
      settings: {
        imageGeneration: {
          ...studioImageGeneration,
          resolution: studioImageGeneration.resolution,
        },
        vibe: studioVibe,
        scene: studioScene,
        imageType: studioImageType,
        aspectRatio:
          studioImageType === "Decoration" ? "1:1" : studioAspectRatio,
        vibeContext: hidesCustomContext(studioImageType, {
          detailsMode: studioDetailsMode,
          technicalDrawingMode: studioTechnicalDrawingMode,
        })
          ? ""
          : studioVibeContext,
        useTrueToneStudio: useTrueToneStudio,
        productLightsOn,
        productLightsWarmth,
        decorationSurfaceOverride: studioDecorationSurfaceOverride,
        decorationFramingPreference: studioDecorationFramingPreference,
        decorationRoomPlacementPreference:
          studioDecorationRoomPlacementPreference,
        decorationViewAngle: studioDecorationViewAngle,
        profileId: studioSelectedProfileId,
        colorSwatches: [...colorSwatches],
        recolorAdditionalContext: studioRecolorOptions.additionalContext,
        rotationAngle: studioRotationAngle,
        rotationMode: studioRotationMode,
        useAvatar: useAvatar,
        avatarImage: studioAvatar,
        highlightsConfigId: studioHighlightsConfigId,
        highlightsInputs: { ...studioHighlightsInputs },
        technicalDrawingMode: studioTechnicalDrawingMode,
        technicalDrawingMaterialImage: studioTechnicalDrawingMaterialImage,
        technicalDrawingMaterial: studioTechnicalDrawingMaterial,
        technicalDrawingShadowStyle: studioTechnicalDrawingShadowStyle,
        detailsFocus: studioDetailsFocus,
        detailsMode: studioDetailsMode,
        detailsCameraPerspective: studioDetailsCameraPerspective,
        detailsMaterial: studioDetailsMaterial,
        detailsSceneDescription: studioDetailsSceneDescription,
        cutoutShadows: studioCutoutShadows,
        backgroundRemoval: studioBackgroundRemoval,
        dimensionMarker,
        dimensionCapStyle,
        dimensionShowLabel:
          dimensionShowLabel ?? activeDimensionPreset?.style.showLabel ?? true,
        dimensionTargetFormat,
        dimensionPreset: activeDimensionPreset
          ? structuredClone(activeDimensionPreset)
          : null,
        styleConfig: {
          softLighting,
          neverHideParts,
          alwaysCenter,
          desiredRoomStyle,
        },
      },
    };

    if (isSimpleCutoutSettings(taskWithoutResolution.settings)) {
      const task: GenerationTask = {
        ...taskWithoutResolution,
        settings: {
          ...taskWithoutResolution.settings,
          resolution: "1K",
        },
      };

      setGenerationQueue((prev) => [...prev, task]);
      setIsQueueOpen(true);
      return;
    }

    setPendingQueueTask(taskWithoutResolution);
    setSelectedResolution("1K");
    setShowResolutionModal(true);
  };

  const handleConfirmResolution = () => {
    if (!pendingQueueTask) return;

    const task: GenerationTask = {
      ...pendingQueueTask,
      settings: {
        ...pendingQueueTask.settings,
        imageGeneration: pendingQueueTask.settings.imageGeneration
          ? {
              ...pendingQueueTask.settings.imageGeneration,
              resolution: selectedResolution,
              customSize: undefined,
            }
          : undefined,
        resolution: isSimpleCutoutSettings(pendingQueueTask.settings)
          ? "1K"
          : selectedResolution,
      },
    };

    setGenerationQueue((prev) => [...prev, task]);
    setShowResolutionModal(false);
    setPendingQueueTask(null);
    setIsQueueOpen(true);
  };

  const handleSetBrand = async (updatedBrand: BrandIdentity) => {
    try {
      await updateBrandTemplate(updatedBrand);
      await refetchBrands();
    } catch (e) {
      console.error("Failed to update brand", e);
      setError("Failed to update brand");
    }
  };

  const editingStudioProduct =
    editingContent && editingContent.initialMode === "inpaint"
      ? studioProducts.find((p) => p.id === editingContent.productId)
      : undefined;
  const editingBaseImage =
    editingContent?.initialMode === "inpaint" &&
    editingContent.isSourceImage &&
    editingStudioProduct?.image
      ? getImageSrc(editingStudioProduct.image)
      : editingContent?.content.image;

  const handleNotificationNavigate = (notification: AppNotification) => {
    // Cross-project: jump into the originating project's Studio tab and
    // deep-link the image, which the layout auto-opens once that project's
    // data loads. Lands on the Studio tab (not the Library tab) so closing the
    // viewer leaves the user in the Studio — matching the global library's
    // "Open in project" behaviour.
    if (notification.projectId && notification.projectId !== projectId) {
      navigate({
        to: "/projects/$projectId",
        params: { projectId: notification.projectId },
        search: { image: notification.contentId },
      });
      return;
    }

    if (!notification.productId) return;

    // Search both products and multi-product scenes
    const product = studioProducts.find((p) => p.id === notification.productId);
    const scene = multiProductScenes.find(
      (s) => s.id === notification.productId
    );
    const generatedContent =
      product?.generatedContent ?? scene?.generatedContent ?? [];
    if (generatedContent.length === 0) return;

    // Resolve the generation this notification points at: prefer its content
    // id, then its thumbnail, and fall back to the most recent generation.
    let targetContent: GeneratedContent | undefined;
    if (notification.contentId) {
      targetContent = generatedContent.find(
        (c) => c.id === notification.contentId
      );
    }
    if (!targetContent && notification.thumbnailUrl) {
      targetContent = generatedContent.find(
        (c) =>
          c.thumbnailUrl === notification.thumbnailUrl ||
          c.image === notification.thumbnailUrl
      );
    }
    if (!targetContent) {
      targetContent = generatedContent[0];
    }

    if (targetContent) {
      // Land on the Studio tab so closing the viewer leaves the user there,
      // not on whichever tab they happened to be on when the notification
      // fired. The viewer itself is layout-level, so it opens over the Studio
      // tab regardless. Highlight Remix is the exception: its result view
      // lives on /highlights, so a finished remix opens in place.
      if (!isHighlights) {
        navigate({ to: "/projects/$projectId", params: { projectId } });
      }
      setActiveViewer({
        content: targetContent,
        productId: notification.productId,
        currentContentId: targetContent.id ?? null,
      });
    }
  };

  if (!user) {
    return null; // Redirect to login handled by __root.tsx
  }

  return (
    <ProjectContext.Provider
      value={{
        projectId,
        globalBrands,
        currentBrand,
        onBrandChange: handleSetBrand,
        studioProducts,
        setStudioProducts: setStudioProductsForProject,
        multiProductScenes,
        setMultiProductScenes,
        vibes,
        setVibes: handleSetVibes,
        scenes,
        setScenes: handleSetScenes,
        workflows,
        retailerProfiles,
        setRetailerProfiles,
        highlightsConfigs,
        setHighlightsConfigs,
        defaultBackgroundColor,
        setDefaultBackgroundColor,
        logoPosition,
        setLogoPosition,
        logoScale,
        setLogoScale,
        dimensionMarker,
        setDimensionMarker,
        dimensionPresetId,
        setDimensionPresetId,
        activeDimensionPreset,
        dimensionShowLabel,
        setDimensionShowLabel,
        dimensionCapStyle,
        setDimensionCapStyle,
        dimensionTargetFormat,
        setDimensionTargetFormat,
        namingTemplate,
        softLighting,
        neverHideParts,
        alwaysCenter,
        desiredRoomStyle,
        companyLogo,
        setCompanyLogo,
        useAvatar,
        setUseAvatar,
        savedColorSwatches,
        setSavedColorSwatches,
        modelType,
        setModelType,
        error,
        setError,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        isLoadingProducts:
          isLoadingProducts || projectContentLoadState.projectId !== projectId,
        isProjectDataLoaded: !!currentProject,
        productsLoadFailed: projectContentLoadState.status === "error",
        retryProductsLoad: () => setProductsLoadAttempt((n) => n + 1),
        setIsLoadingProducts,
        studioVibe,
        setStudioVibe,
        studioScene,
        setStudioScene,
        studioImageType,
        setStudioImageType,
        studioImageGeneration,
        setStudioImageGeneration,
        studioAspectRatio,
        setStudioAspectRatio,
        studioVibeContext,
        setStudioVibeContext,
        useTrueToneStudio,
        setUseTrueToneStudio,
        productLightsOn,
        setProductLightsOn,
        productLightsWarmth,
        setProductLightsWarmth,
        studioDecorationSurfaceOverride,
        setStudioDecorationSurfaceOverride,
        studioDecorationFramingPreference,
        setStudioDecorationFramingPreference,
        studioDecorationRoomPlacementPreference,
        setStudioDecorationRoomPlacementPreference,
        studioDecorationViewAngle,
        setStudioDecorationViewAngle,
        studioSelectedProfileId,
        setStudioSelectedProfileId,
        colorSwatches,
        setColorSwatches,
        studioRecolorOptions,
        setStudioRecolorOptions,
        studioRotationAngle,
        setStudioRotationAngle,
        studioRotationMode,
        setStudioRotationMode,
        studioRotationModel,
        setStudioRotationModel,
        studioRotationQuality,
        setStudioRotationQuality,
        studioAvatar,
        setStudioAvatar,
        studioHighlightsConfigId,
        setStudioHighlightsConfigId,
        studioHighlightsInputs,
        setStudioHighlightsInputs,
        studioTechnicalDrawingMode,
        setStudioTechnicalDrawingMode,
        studioTechnicalDrawingMaterialImage,
        setStudioTechnicalDrawingMaterialImage,
        studioTechnicalDrawingMaterial,
        setStudioTechnicalDrawingMaterial,
        studioTechnicalDrawingShadowStyle,
        setStudioTechnicalDrawingShadowStyle,
        studioDetailsFocus,
        setStudioDetailsFocus,
        studioDetailsMode,
        setStudioDetailsMode,
        studioDetailsCameraPerspective,
        setStudioDetailsCameraPerspective,
        studioDetailsMaterial,
        setStudioDetailsMaterial,
        studioDetailsMaterialReference,
        setStudioDetailsMaterialReference,
        studioDetailsSceneDescription,
        setStudioDetailsSceneDescription,
        studioCutoutShadows,
        setStudioCutoutShadows,
        studioBackgroundRemoval,
        setStudioBackgroundRemoval,
        studioProductDetails,
        setStudioProductDetails,
        lifestylePresets,
        createLifestylePreset: handleCreateLifestylePreset,
        deleteLifestylePreset: handleDeleteLifestylePreset,
        handleDownloadImage,
        handleGenerateForSinglePlant,
        activeViewer,
        setActiveViewer,
      }}
    >
      <div className="flex flex-col h-full w-full font-sans text-gray-900 antialiased bg-gray-50 relative">
        {GENERATION_QUEUE_ENABLED && isStudio && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 pointer-events-none">
            <div className="flex items-center shadow-md rounded-full bg-white border border-gray-200 p-1 transition-transform hover:scale-[1.02] pointer-events-auto">
              <button
                onClick={() => setIsQueueOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-l-full hover:bg-gray-50 text-sm font-semibold text-gray-700 transition-colors border-r border-gray-100"
              >
                <QueueListIcon className="w-5 h-5 text-black" />
                <span>{t("studio.queueButton")}</span>
                <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-lg text-xs border border-gray-200">
                  {generationQueue.filter((t) => t.status === "pending").length}
                </span>
              </button>
              <button
                onClick={handleAddToQueue}
                className="flex items-center justify-center px-3 py-2 rounded-r-full hover:bg-gray-50 text-gray-700 transition-colors"
                title={t("studio.queueAddSelected")}
              >
                <PlusCircleIcon className="w-5 h-5 text-black" />
              </button>
            </div>
          </div>
        )}

        {GENERATION_QUEUE_ENABLED && isQueueOpen && (
          <QueueOverlay
            queue={generationQueue}
            onClose={() => setIsQueueOpen(false)}
            onRemoveFromQueue={handleRemoveFromQueue}
            onUpdateTaskSettings={handleUpdateQueueTaskSettings}
            onRunQueue={handleRunQueue}
            isProcessing={isQueueProcessing}
            products={studioProducts}
            vibes={vibes}
            scenes={scenes}
            workflows={workflows}
          />
        )}

        <AppHeader
          left={
            <>
              <button
                onClick={handleBackToProjects}
                className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
              >
                <svg
                  width="24"
                  height="30"
                  viewBox="0 0 53 66"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.54 9.62L0.56 9.66L9.37 15.61H9.41C9.61 15.74 9.83 15.83 10.08 15.83C10.33 15.83 10.55 15.74 10.75 15.61H10.79L31.16 1.37C31.35 1.22 31.49 1.01 31.49 0.75C31.49 0.33 31.15 0 30.74 0H1.28C0.57 0.01 0 0.580001 0 1.29V8.6C0 9.02 0.22 9.38 0.54 9.62Z"
                    fill="#000005"
                  />
                  <path
                    d="M52.4499 57.1705V57.1405L42.3999 50.3605H42.3799C42.2599 50.2805 42.1299 50.2305 41.9799 50.2305C41.8299 50.2305 41.6999 50.2905 41.5799 50.3605H41.5599L21.6799 64.2505V64.2705C21.4899 64.4105 21.3599 64.6205 21.3599 64.8805C21.3599 65.3005 21.6999 65.6305 22.1099 65.6305H52.0299C52.4499 65.6305 52.7899 65.3005 52.7899 64.8805V57.7705C52.7899 57.5205 52.6599 57.3005 52.4699 57.1705H52.4499Z"
                    fill="#000005"
                  />
                  <path
                    d="M52.4498 32.4498L38.3298 22.9098V22.8898C38.1298 22.7498 37.9898 22.5398 37.9898 22.2798C37.9898 22.0198 38.1198 21.8098 38.3198 21.6698V21.6498L52.4598 11.7598V11.7298C52.6598 11.5898 52.7798 11.3798 52.7798 11.1398V0.759766C52.7798 0.339766 52.4398 0.00976562 52.0298 0.00976562H51.3398C51.1898 0.00976562 51.0598 0.0597667 50.9398 0.139767H50.9098L20.1298 21.6598V21.6798C19.9398 21.8198 19.8198 22.0298 19.8198 22.2798C19.8198 22.5298 19.9498 22.7398 20.1298 22.8798V22.8998L51.5798 44.1898L51.6298 44.1598C51.7498 44.2398 51.8798 44.2898 52.0298 44.2898C52.4498 44.2898 52.7798 43.9498 52.7798 43.5398V33.0898C52.7798 32.8398 52.6498 32.6298 52.4598 32.4898V32.4598L52.4498 32.4498Z"
                    fill="#000005"
                  />
                  <path
                    d="M31.92 43.2891V43.2691L1.19 22.5091H1.18C1.06 22.4291 0.92 22.3691 0.76 22.3691C0.34 22.3691 0 22.7091 0 23.1291V33.7291C0 33.9891 0.14 34.2091 0.34 34.3491V34.3791L13.63 43.3491V43.3691C13.79 43.5091 13.9 43.7091 13.9 43.9391C13.9 44.1891 13.77 44.3991 13.59 44.5291V44.5491L0.33 53.7991V53.8291C0.13 53.9691 0 54.1791 0 54.4291V64.8891C0 65.3091 0.34 65.6491 0.76 65.6491H1.5C1.65 65.6491 1.78 65.5991 1.89 65.5191H1.94L31.92 44.5391V44.5191C32.12 44.3791 32.26 44.1691 32.26 43.9091C32.26 43.6491 32.12 43.4291 31.92 43.2991V43.2891Z"
                    fill="#000005"
                  />
                </svg>
              </button>
              <div className="h-6 w-px bg-gray-200"></div>
              <div className="hidden md:block min-w-0">
                <p className="max-w-[140px] lg:max-w-[220px] w-full text-[10px] text-gray-500 uppercase tracking-wider font-semibold truncate">
                  {currentProject?.name || "AI Studio"}
                </p>
              </div>
            </>
          }
          center={
            <nav
              aria-label={t("studio.projectNavigation")}
              className="responsive-navigation flex whitespace-nowrap bg-gray-100/80 backdrop-blur-sm p-1 rounded-full border border-white/20"
            >
              <button
                aria-current={isStudio ? "page" : undefined}
                onClick={() =>
                  navigate({
                    to: "/projects/$projectId",
                    params: { projectId },
                  })
                }
                className={`responsive-navigation__item flex items-center px-3 xl:px-6 py-1.5 text-sm font-semibold rounded-full transition-all ${
                  isStudio
                    ? "bg-black text-white shadow-md"
                    : "text-gray-700 hover:text-gray-900"
                }`}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 shrink-0"
                >
                  <path
                    d="M2 8.37722C2 8.0269 2 7.85174 2.01462 7.70421C2.1556 6.28127 3.28127 5.1556 4.70421 5.01462C4.85174 5 5.03636 5 5.40558 5C5.54785 5 5.61899 5 5.67939 4.99634C6.45061 4.94963 7.12595 4.46288 7.41414 3.746C7.43671 3.68986 7.45781 3.62657 7.5 3.5C7.54219 3.37343 7.56329 3.31014 7.58586 3.254C7.87405 2.53712 8.54939 2.05037 9.32061 2.00366C9.38101 2 9.44772 2 9.58114 2H14.4189C14.5523 2 14.619 2 14.6794 2.00366C15.4506 2.05037 16.126 2.53712 16.4141 3.254C16.4367 3.31014 16.4578 3.37343 16.5 3.5C16.5422 3.62657 16.5633 3.68986 16.5859 3.746C16.874 4.46288 17.5494 4.94963 18.3206 4.99634C18.381 5 18.4521 5 18.5944 5C18.9636 5 19.1483 5 19.2958 5.01462C20.7187 5.1556 21.8444 6.28127 21.9854 7.70421C22 7.85174 22 8.0269 22 8.37722V16.2C22 17.8802 22 18.7202 21.673 19.362C21.3854 19.9265 20.9265 20.3854 20.362 20.673C19.7202 21 18.8802 21 17.2 21H6.8C5.11984 21 4.27976 21 3.63803 20.673C3.07354 20.3854 2.6146 19.9265 2.32698 19.362C2 18.7202 2 17.8802 2 16.2V8.37722Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 16.5C14.2091 16.5 16 14.7091 16 12.5C16 10.2909 14.2091 8.5 12 8.5C9.79086 8.5 8 10.2909 8 12.5C8 14.7091 9.79086 16.5 12 16.5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="responsive-navigation__label">Image</span>
              </button>
              <button
                aria-current={isHighlights ? "page" : undefined}
                onClick={() =>
                  navigate({
                    to: "/projects/$projectId/highlights",
                    params: { projectId },
                  })
                }
                className={`responsive-navigation__item flex items-center px-3 xl:px-6 py-1.5 text-sm font-semibold rounded-full transition-all ${
                  isHighlights
                    ? "bg-black text-white shadow-md"
                    : "text-gray-700 hover:text-gray-900"
                }`}
              >
                <SparklesIcon className="h-4 w-4 shrink-0" />
                <span className="responsive-navigation__label">
                  {t("highlights.navLabel")}
                </span>
              </button>
              <button
                aria-current={isVideo ? "page" : undefined}
                onClick={() =>
                  navigate({
                    to: "/projects/$projectId/video",
                    params: { projectId },
                  })
                }
                className={`responsive-navigation__item flex items-center px-3 xl:px-6 py-1.5 text-sm font-semibold rounded-full transition-all ${
                  isVideo
                    ? "bg-black text-white shadow-md"
                    : "text-gray-700 hover:text-gray-900"
                }`}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 shrink-0"
                >
                  <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.5 8.96533C9.5 8.48805 9.5 8.24941 9.59974 8.11618C9.68666 8.00007 9.81971 7.92744 9.96438 7.9171C10.1304 7.90525 10.3311 8.03429 10.7326 8.29239L15.4532 11.3271C15.8016 11.551 15.9758 11.663 16.0359 11.8054C16.0885 11.9298 16.0885 12.0702 16.0359 12.1946C15.9758 12.337 15.8016 12.449 15.4532 12.6729L10.7326 15.7076C10.3311 15.9657 10.1304 16.0948 9.96438 16.0829C9.81971 16.0726 9.68666 15.9999 9.59974 15.8838C9.5 15.7506 9.5 15.512 9.5 15.0347V8.96533Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="responsive-navigation__label">Video</span>
              </button>
              <button
                aria-current={isWorkflows ? "page" : undefined}
                onClick={() =>
                  navigate({
                    to: "/projects/$projectId/workflows",
                    params: { projectId },
                  })
                }
                className={`responsive-navigation__item flex items-center px-3 xl:px-6 py-1.5 text-sm font-semibold rounded-full transition-all ${
                  isWorkflows
                    ? "bg-black text-white shadow-md"
                    : "text-gray-700 hover:text-gray-900"
                }`}
              >
                <WorkflowIcon className="w-4 h-4 shrink-0" />
                <span className="responsive-navigation__label">
                  {t("studio.workflows")}
                </span>
              </button>
              <button
                aria-current={isLibrary ? "page" : undefined}
                onClick={() =>
                  navigate({
                    to: "/projects/$projectId/library",
                    params: { projectId },
                  })
                }
                className={`responsive-navigation__item flex items-center px-3 xl:px-6 py-1.5 text-sm font-semibold rounded-full transition-all ${
                  isLibrary
                    ? "bg-black text-white shadow-md"
                    : "text-gray-700 hover:text-gray-900"
                }`}
              >
                <FolderIcon className="w-4 h-4 shrink-0" />
                <span className="responsive-navigation__label">
                  {t("studio.library")}
                </span>
              </button>
            </nav>
          }
          rightExtras={
            <>
              <NotificationHub
                onNavigate={handleNotificationNavigate}
                currentProjectId={projectId}
                resolveProjectName={(id) =>
                  // The studio route only loads the current project (SIS-453);
                  // notifications from other projects render without a name chip.
                  id === projectId ? currentProject?.name : undefined
                }
              />
              <CreditDisplay />
            </>
          }
        />

        <Outlet />

        {viewedContent && (
          <PanelErrorBoundary
            panel="image-viewer"
            resetKeys={[viewedContent.id]}
            renderFallback={(reset) => (
              // The viewer is a full-screen modal, so its fallback has to be
              // one too — and it needs a way out, because a broken viewer
              // leaves no other control to reach. The crashed viewer took its
              // keyboard handling with it, so the dialog brings its own.
              <PanelErrorDialog reset={reset} onDismiss={handleCloseViewer} />
            )}
          >
            <ImageViewerModal
              content={activeGalleryContent ?? viewedContent}
              initialTab={activeViewer?.initialTab}
              onClose={handleCloseViewer}
              onDownload={handleDownloadImage}
              sourceAsset={activeGalleryProduct?.originalImage}
              generationInputImage={activeGalleryProduct?.image}
              currentIndex={activeGalleryCurrentIndex}
              totalImages={activeGalleryVisibleImages.length}
              onNext={() => handleGalleryNavigation("next")}
              onPrevious={() => handleGalleryNavigation("prev")}
              onDelete={handleGalleryDelete}
              onRename={handleRenameGalleryImage}
              renameSiblings={activeGalleryAllImages}
              onStartEdit={handleStartEdit}
              onApplyInlineEdit={handleApplyInlineEdit}
              onGenerateLifestyleScene={handleGenerateLifestyleScene}
              isGeneratingLifestyleScene={isLifestyleGenerating}
              lifestylePresets={lifestylePresets}
              onCreateLifestylePreset={handleCreateLifestylePreset}
              onDeleteLifestylePreset={handleDeleteLifestylePreset}
              isApplyingInlineEdit={isApplyingEdit}
              editorStudioProducts={studioProducts}
              onSaveProductMeasurements={handleSaveProductMeasurements}
              onUpdateProductDimensions={handleUpdateProductDimensions}
              onEditorAddProduct={handleAddProductFromEditor}
              onEditorRemoveBackground={handleRemoveBackgroundFromEditor}
              onRecolor={handleRecolorFromViewer}
              savedColorSwatches={savedColorSwatches}
              isRecoloring={isRecoloringInViewer}
              directionSketches={activeDirectionSketches}
              onDirectionSketchGenerated={handleDirectionSketchGenerated}
              onNameDirectionSketch={() =>
                nameViewerQuickAction(activeGallery?.productId ?? "", "Sketch")
              }
              onRotate={handleRotateFromViewer}
              isRotating={isRotatingInViewer}
              onRemoveBackground={handleRemoveBackgroundFromViewer}
              isRemovingBackground={isRemovingBackgroundInViewer}
              backgroundRemovalProgress={backgroundRemovalProgress}
              onUpscale={handleUpscaleFromViewer}
              isUpscaling={isUpscalingInViewer}
              onAspectRatioResult={handleAspectRatioFromViewer}
              onAspectRatioStart={(settings) => {
                setIsAspectRatioGeneratingInViewer(true);
                const productId = activeGallery?.productId;
                if (!productId) return undefined;
                const title = nameViewerQuickAction(
                  productId,
                  "AspectRatio",
                  settings.aspectRatio.replace(":", "x")
                );
                // Anchor the backend write to this product and name the row —
                // without this the call snapshots whatever context the previous
                // generation left behind (SIS-517). Mirrors the studio's Aspect
                // Ratio branch.
                setGenerationContext(
                  projectId,
                  productId,
                  "Aspect Ratio",
                  serializeImageGenerationSettings(settings.imageGeneration, {
                    imageType: "Aspect Ratio",
                    title,
                    aspectRatio: settings.aspectRatio,
                    resolution: settings.resolution,
                  })
                );
                return title;
              }}
              onAspectRatioEnd={() => {
                setIsAspectRatioGeneratingInViewer(false);
                setGenerationContext(projectId);
              }}
              onDetailsResult={handleDetailsFromViewer}
              onDetailsStart={(settings) => {
                const productId = activeGallery?.productId;
                if (!productId) return undefined;
                const title = nameViewerQuickAction(
                  productId,
                  "Details",
                  settings.detailsFocus
                );
                setGenerationContext(
                  projectId,
                  productId,
                  "Details",
                  // Keys must match what the details panel reads (and what the
                  // viewer stores in memory) — the backend persists this
                  // verbatim for dbPersisted results.
                  serializeImageGenerationSettings(settings.imageGeneration, {
                    imageType: "Details",
                    title,
                    detailsFocus: settings.detailsFocus,
                    detailsMode: settings.detailsMode,
                    detailsCameraPerspective: settings.detailsCameraPerspective,
                    detailsMaterial: settings.detailsMaterial,
                    aspectRatio: settings.aspectRatio,
                    resolution: settings.resolution,
                    model: "Nano Banana Pro",
                  })
                );
                return title;
              }}
              onDetailsEnd={() => setGenerationContext(projectId)}
              onMilieuCreativeResult={handleMilieuCreativeFromViewer}
              onMilieuCreativeError={handleMilieuCreativeErrorFromViewer}
              onMilieuCreativeStart={(settings) => {
                const productId = activeGallery?.productId;
                if (!productId) return;
                setGenerationContext(
                  projectId,
                  productId,
                  MILIEU_CREATIVE_LABEL,
                  // Keys must match what the Studio's Standard path persists
                  // (and what the viewer stores in memory) — the backend
                  // persists this verbatim for dbPersisted results, and the
                  // SIS-502 regeneration path reads the ids back.
                  serializeImageGenerationSettings(settings.imageGeneration, {
                    // The StudioImageType value, not the display label — the
                    // regeneration avatar gates test the literal "Standard";
                    // the label stays on the top-level content imageType.
                    imageType: "Standard",
                    title: settings.title,
                    vibe: settings.vibeLabel,
                    scene: settings.sceneLabel,
                    ...environmentIdSettings({
                      vibe: settings.vibeId,
                      scene: settings.sceneId,
                    }),
                    aspectRatio: settings.aspectRatio,
                    resolution: settings.resolution,
                    model: "Nano Banana Pro",
                    // Explicit "" / false (not undefined) so a regeneration
                    // keeps them blank/off instead of falling back to the live
                    // Studio state (SIS-500).
                    context: settings.context,
                    useTrueToneStudio: settings.useTrueToneStudio,
                    productLightsOn: settings.productLightsOn,
                    productLightsWarmth: settings.productLightsOn
                      ? settings.productLightsWarmth
                      : undefined,
                    // The useAvatar flag mirrors the Studio's persisted shape
                    // (the image itself is never persisted).
                    ...(settings.useAvatar ? { useAvatar: true } : {}),
                  })
                );
              }}
              onMilieuCreativeEnd={() => setGenerationContext(projectId)}
              onAddAvatarStart={(imageGeneration) => {
                const productId = activeGallery?.productId;
                if (!productId) return undefined;
                const title = nameViewerQuickAction(productId, "Avatar");
                setGenerationContext(
                  projectId,
                  productId,
                  "Add Avatar",
                  serializeImageGenerationSettings(imageGeneration, {
                    imageType: "Add Avatar",
                    title,
                    model: "Nano Banana Pro",
                  })
                );
                return title;
              }}
              onAddAvatarResult={handleAddAvatarFromViewer}
              onAddAvatarEnd={() => setGenerationContext(projectId)}
              onCropResult={handleCropFromViewer}
              onDimensionResult={handleDimensionFromViewer}
              dimensionDefaults={{
                productWidth: activeGallery
                  ? studioProducts.find((p) => p.id === activeGallery.productId)
                      ?.width
                  : undefined,
                productHeight: activeGallery
                  ? studioProducts.find((p) => p.id === activeGallery.productId)
                      ?.height
                  : undefined,
                companyLogo,
                logoPosition,
              }}
              productId={activeGallery?.productId}
              productCategory={
                activeGallery
                  ? studioProducts.find((p) => p.id === activeGallery.productId)
                      ?.productCategory
                  : undefined
              }
              productDescription={
                activeGallery
                  ? studioProducts.find((p) => p.id === activeGallery.productId)
                      ?.productDescription
                  : undefined
              }
              analysisStatus={
                activeGallery
                  ? studioProducts.find((p) => p.id === activeGallery.productId)
                      ?.analysisStatus
                  : undefined
              }
              analysisError={
                activeGallery
                  ? studioProducts.find((p) => p.id === activeGallery.productId)
                      ?.analysisError
                  : undefined
              }
              onUpdateProductAnalysis={handleUpdateProductAnalysis}
              onRetryProductAnalysis={handleRetryProductAnalysis}
              onAddToGallery={handleAddToStudio}
              onRegenerate={handleRegenerate}
              isRegenerating={isRegeneratingInViewer}
              projectType="staging"
              allImages={activeGalleryVisibleImages}
              onSelectImage={(selection) => {
                if (!activeGallery) return;
                const product = studioProducts.find(
                  (p) => p.id === activeGallery.productId
                );
                const multiScene = multiProductScenes.find(
                  (s) => s.id === activeGallery.productId
                );
                if (product) {
                  if (selection === -1) {
                    const content = getSourceGalleryContent(product);
                    setActiveViewer({
                      content,
                      productId: activeGallery.productId,
                      currentContentId: null,
                    });
                  } else {
                    const selectedContent = getVisibleGalleryImages(
                      activeGallery.productId
                    ).find((content) => content.id === selection);
                    if (!selectedContent) return;
                    setActiveViewer({
                      content: selectedContent,
                      productId: activeGallery.productId,
                      currentContentId: selectedContent.id,
                    });
                  }
                } else if (multiScene) {
                  // Multi-product scenes don't have a source image
                  if (selection === -1) return;
                  const selectedContent = getVisibleGalleryImages(
                    activeGallery.productId
                  ).find((content) => content.id === selection);
                  if (!selectedContent) return;
                  setActiveViewer({
                    content: selectedContent,
                    productId: activeGallery.productId,
                    currentContentId: selectedContent.id,
                  });
                }
              }}
              vibes={vibes}
              scenes={scenes}
              projectId={projectId}
              onExportSaved={handleViewerContentSaved}
              onSetImageSaved={handleViewerContentSaved}
            />
          </PanelErrorBoundary>
        )}

        {editingContent && (
          <div
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-8"
            style={{ backgroundImage: "none", background: "" }}
          >
            <div className="w-full h-full max-w-6xl">
              <ImageEditor
                baseImage={editingBaseImage!}
                onCancel={handleCancelEdit}
                onApply={handleApplyEdit}
                isApplyingEdit={isApplyingEdit}
                studioProducts={studioProducts}
                onAddProduct={handleAddProductFromEditor}
                onRemoveBackground={handleRemoveBackgroundFromEditor}
                initialEditMode={editingContent.initialMode}
                scenes={scenes}
                productCategory={editingStudioProduct?.productCategory}
                productDescription={editingStudioProduct?.productDescription}
                onGenerateLifestyleScene={handleGenerateLifestyleScene}
                isLifestyleGenerating={isLifestyleGenerating}
                lifestylePresets={lifestylePresets}
                onCreateLifestylePreset={handleCreateLifestylePreset}
                onDeleteLifestylePreset={handleDeleteLifestylePreset}
                editingProductId={
                  editingContent.initialMode === "inpaint" &&
                  editingContent.isSourceImage
                    ? editingStudioProduct?.id
                    : undefined
                }
                onUpdateProductDimensions={handleUpdateProductDimensions}
              />
            </div>
          </div>
        )}

        {isApplyingEdit && !editingContent && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[90] animate-in slide-in-from-bottom-10">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-800 text-white text-sm font-medium shadow-md border border-gray-900">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>{t("studio.editorApplyingEditInBackground")}</span>
            </div>
          </div>
        )}

        {isAspectRatioGeneratingInViewer && !viewedContent && (
          <div className="fixed bottom-6 right-[calc(1.5rem+250px+0.5rem)] z-[90] animate-in slide-in-from-bottom-10 flex flex-col items-end">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-800 text-white text-sm font-medium shadow-md border border-gray-900">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>{t("studio.aspectRatioConvertingInBackground")}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-10">
            <div className="bg-red-600 text-white px-6 py-4 rounded-xl shadow-xl flex items-center gap-4">
              <ExclamationTriangleIcon className="w-6 h-6" />
              <div>
                <p className="font-semibold">Error Occurred</p>
                <p className="text-sm opacity-90">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="p-1 hover:bg-white/20 rounded-full"
              >
                <XCircleIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {productsLoadFailed && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-10">
            <div className="bg-red-600 text-white px-6 py-4 rounded-xl shadow-xl flex items-center gap-4">
              <ExclamationTriangleIcon className="w-6 h-6" />
              <p className="text-sm font-semibold">
                {t("studio.errorLoadProducts")}
              </p>
              <button
                onClick={() => setProductsLoadAttempt((n) => n + 1)}
                className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-semibold whitespace-nowrap"
              >
                {t("common.retry")}
              </button>
              <button
                onClick={() => {
                  // The banner has reported the load failure. Dismissing it
                  // explicitly abandons any link that was waiting for Retry,
                  // rather than leaving unreachable work hidden in state.
                  setProductsLoadFailed(false);
                  setDeepLinkCapture(discardPendingImageDeepLink);
                }}
                className="p-1 hover:bg-white/20 rounded-full"
              >
                <XCircleIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Resolution Selection Modal */}
        {GENERATION_QUEUE_ENABLED && showResolutionModal && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setShowResolutionModal(false);
                setPendingQueueTask(null);
              }}
            />
            <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Select Resolution
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                Choose the resolution for this generation task. Higher
                resolution means better quality but longer generation time.
              </p>
              <div className="grid grid-cols-3 gap-3 mb-6">
                {(["1K", "2K", "4K"] as const).map((res) => (
                  <button
                    key={res}
                    onClick={() => setSelectedResolution(res)}
                    className={`py-3 px-4 text-sm font-semibold rounded-lg border-2 transition-all ${
                      selectedResolution === res
                        ? "bg-black text-white border-black shadow-md"
                        : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="font-semibold text-base mb-1">{res}</div>
                    <div className="text-xs opacity-75">
                      {res === "1K"
                        ? "Standard"
                        : res === "4K"
                          ? "Ultra HD"
                          : "High Res"}
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowResolutionModal(false);
                    setPendingQueueTask(null);
                  }}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmResolution}
                  className="px-4 py-2 text-sm font-semibold text-white bg-black rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Add to Queue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ProjectContext.Provider>
  );
};

/**
 * Full-page spinner with stall diagnostics. After STALL_DETAILS_AFTER_MS it
 * names the suspected cause (offline pause, auth pending, retrying, hung
 * request); after STALL_ACTIONS_AFTER_MS it offers retry/reload and logs one
 * diagnostic warning to Sentry so stalls become visible in production.
 */
const ProjectLoadingScreen: React.FC<{
  hasUser: boolean;
  fetchStatus: "fetching" | "paused" | "idle";
  failureCount: number;
  onRetry: () => void;
}> = ({ hasUser, fetchStatus, failureCount, onRetry }) => {
  const { t } = useTranslation();
  const [online, setOnline] = React.useState(() => navigator.onLine);
  const [showDetails, setShowDetails] = React.useState(false);
  const [showActions, setShowActions] = React.useState(false);
  const hasReportedRef = React.useRef(false);

  React.useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // The component only mounts while the route is in the "loading" state, so
  // the timers are naturally scoped to one loading episode.
  React.useEffect(() => {
    const detailsTimer = window.setTimeout(
      () => setShowDetails(true),
      STALL_DETAILS_AFTER_MS
    );
    const actionsTimer = window.setTimeout(
      () => setShowActions(true),
      STALL_ACTIONS_AFTER_MS
    );
    return () => {
      window.clearTimeout(detailsTimer);
      window.clearTimeout(actionsTimer);
    };
  }, []);

  const cause = resolveLoadingStallCause({
    hasUser,
    online,
    fetchStatus,
    failureCount,
  });

  React.useEffect(() => {
    if (!showActions || hasReportedRef.current) return;
    hasReportedRef.current = true;
    logWarning("Project loading stalled past action threshold", {
      cause,
      fetchStatus,
      failureCount,
      online,
      hasUser,
      stalledForMs: STALL_ACTIONS_AFTER_MS,
    });
  }, [showActions, cause, fetchStatus, failureCount, online, hasUser]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-4">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
      {showDetails && (
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700">
            {t("projectLoading.stillWorking")}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {t(STALL_CAUSE_I18N_KEY[cause], { attempt: failureCount + 1 })}
          </p>
        </div>
      )}
      {showActions && (
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onRetry}
            className="rounded-full bg-gray-900 px-5 py-2 text-sm font-medium text-white hover:bg-gray-700"
          >
            {t("projectLoading.retry")}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t("projectLoading.reload")}
          </button>
        </div>
      )}
    </div>
  );
};

const ProjectStudioRoute: React.FC = () => {
  const { t } = useTranslation();
  const rqClient = useQueryClient();
  const { projectId } = Route.useParams();
  const user = auth.currentUser;
  const validId = isProjectUuid(projectId);
  const projectQuery = useGetProject(
    { id: projectId },
    { enabled: !!user && validId }
  );
  const state = resolveProjectRouteState({
    validId,
    hasData: projectQuery.data !== undefined,
    queryError: projectQuery.isError,
    hasProject: (projectQuery.data?.projects?.length ?? 0) > 0,
  });

  if (state === "loading") {
    return (
      <ProjectLoadingScreen
        hasUser={!!user}
        fetchStatus={projectQuery.fetchStatus}
        failureCount={projectQuery.failureCount}
        onRetry={() => {
          // A hung initial fetch has no cached data, and TanStack only
          // cancel-restarts refetches when data exists — a bare refetch
          // would dedupe onto the stuck request. Cancel it explicitly so
          // the retry issues a genuinely new request.
          void rqClient
            .cancelQueries({ queryKey: ["GetProject"] })
            .then(() => projectQuery.refetch());
        }}
      />
    );
  }
  if (state === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("projectLoadError.title")}
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            {t("projectLoadError.description")}
          </p>
          <button
            type="button"
            onClick={() => void projectQuery.refetch()}
            className="mt-6 inline-block rounded-full bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-700"
          >
            {t("projectLoadError.retry")}
          </button>
        </div>
      </div>
    );
  }
  if (state === "not_found") return <ProjectNotFound />;
  return <ProjectStudio />;
};

interface ProjectStudioSearch {
  // Deep link to auto-open the ImageViewerModal on this generated image.
  // Set by the global library's "Open in project" and by cross-project
  // notifications; handled once in the layout so it works from any tab. Cleared
  // after handling.
  image?: string;
  /**
   * Studio image type to select on arrival (`?mode=Cutout`), set by the
   * onboarding goal step. Consumed once by the studio index and removed.
   */
  mode?: string;
}

/**
 * Error boundary for the whole project route.
 *
 * The panel boundaries inside `ProjectStudio` only catch errors that originate
 * in their own subtree. A render loop in the layout's own effects (React
 * error #185) is thrown at `ProjectStudio` itself, above both of them, and
 * would otherwise reach the global `Sentry.ErrorBoundary` in `index.tsx` —
 * which replaces the entire app with a bare paragraph. This keeps the shell
 * and offers a retry that remounts the route with fresh state; navigating
 * away resets it too (TanStack keys the boundary on the route match).
 */
const ProjectStudioErrorComponent: React.FC<ErrorComponentProps> = ({
  reset,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div
        role="alert"
        className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm"
      >
        <h1 className="text-xl font-semibold text-gray-900">
          {t("errors.projectCrashedTitle")}
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          {t("errors.projectCrashedHint")}
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={reset}
            className="inline-block rounded-full bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-700"
          >
            {t("errors.projectCrashedRetry")}
          </button>
          <Link
            to="/"
            search={{ returnTab: "projects" }}
            className="inline-block rounded-full border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            {t("projectNotFound.backToDashboard")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/projects/$projectId")({
  component: ProjectStudioRoute,
  errorComponent: ProjectStudioErrorComponent,
  // TanStack's CatchBoundary calls `onCatch(error, errorInfo)` (see
  // `componentDidCatch` in `@tanstack/react-router`'s CatchBoundary) but the
  // option is typed with `error` only, hence the rest parameter.
  onCatch: (error, ...rest: unknown[]) => {
    const info = rest[0] as React.ErrorInfo | undefined;
    reportPanelCrash("project-route", error, info?.componentStack);
  },
  validateSearch: (search: Record<string, unknown>): ProjectStudioSearch => ({
    image: typeof search.image === "string" ? search.image : undefined,
    mode: typeof search.mode === "string" ? search.mode : undefined,
  }),
});
